// src/components/SponsorManagement.js
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { firestore, storage, auth } from '../firebase';
import { getActiveEventId, resolveAdminDefaultEventId } from '../utils/activeEvent';
import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  setDoc,
  deleteDoc,
  doc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  deleteField,
  writeBatch,
  Timestamp,
} from 'firebase/firestore';
import {
  buildOrganizationFieldsFromForm,
  denormalizedParticipationFromOrg,
  mergeParticipationWithOrg,
} from '../utils/sponsorOrgUtils';
import {
  fetchSponsorOrganizationsByIds,
  findSponsorOrganizationByNormalizedName,
  fetchSponsorParticipationsForEvent,
  fetchPrizesForOrganization,
  fetchAllSponsorOrganizations,
} from '../utils/sponsorQueries';
import {
  fetchOrgNoteLog,
  addOrgNoteLog,
  updateOrgNoteLog,
  fetchSponsorRowNoteLog,
  addSponsorRowNoteLog,
  updateSponsorRowNoteLog,
  noteWasEdited,
  SPONSOR_ROW_NOTE_SUBCOLLECTION,
  snapshotAuthor,
} from '../utils/sponsorNoteLog';
import {
  fetchSponsorOutreachLog,
  addSponsorOutreachLog,
  addSponsorPipelineChangeLog,
  OUTREACH_CHANNELS,
  OUTREACH_OUTCOMES,
  OUTREACH_SUBCOLLECTION,
  outreachOutcomeLabel,
  outreachChannelLabel,
} from '../utils/sponsorOutreachLog';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getDisplayName } from '../utils/flyerDisplay';
import { normalizePrizeStatus } from '../utils/prizeStatus';
import { parseRaffleTicketBarcodeScan, resolveFlyerIdFromNoisyScan } from '../utils/raffleTicketBarcode';
import { buildSponsorFollowUpGlobalMergeFields } from '../utils/sponsorFollowUpEventStats';
import './SponsorManagement.css';
import './admin/FmModal.css';
import EmailComposerModal from './EmailComposerModal';
import { useEmailComposer } from '../hooks/useEmailComposer';
import SponsorMapTab from './SponsorMapTab';
import { copyText, buildTsvTable, formatEmailList } from '../utils/clipboardUtils';
import { openPrintWindow, getOperationalTablePrintCss } from '../utils/printUtils';
import AdminCopyMenu from './admin/AdminCopyMenu';
import AdminColumnExportModal from './admin/AdminColumnExportModal';
import { collectInvalidNamedControlNames } from '../utils/collectInvalidNamedControlNames';
import ModalFieldRequiredError from './ModalFieldRequiredError';
import { useModalEscapeKey } from '../utils/useModalEscapeKey';
import { List } from 'react-window';

function formatFirestoreDateTime(ts) {
  if (!ts || typeof ts.toDate !== 'function') return '—';
  try {
    return ts.toDate().toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return '—';
  }
}

function formatFirestoreDateOnly(ts) {
  if (!ts || typeof ts.toDate !== 'function') return '—';
  try {
    return ts.toDate().toLocaleDateString(undefined, { dateStyle: 'medium' });
  } catch {
    return '—';
  }
}

const SPONSOR_TYPE_SORT_ORDER = { interactive: 0, exhibit: 1, prize: 2, none: 3 };

const AwardCategoryRow = React.memo(function AwardCategoryRow({
  style,
  category,
  onDebouncedChange,
  onBlurCommit,
  onDelete,
  warning,
}) {
  const [draftName, setDraftName] = useState(categoryName(category));
  const [draftSubtitle, setDraftSubtitle] = useState((category.subtitle || '').trim());

  useEffect(() => {
    setDraftName(categoryName(category));
    setDraftSubtitle((category.subtitle || '').trim());
  }, [category]);

  useEffect(() => {
    const timer = setTimeout(() => {
      onDebouncedChange(category.id, draftName, draftSubtitle);
    }, 300);
    return () => clearTimeout(timer);
  }, [category.id, draftName, draftSubtitle, onDebouncedChange]);

  return (
    <div style={style} className="sponsor-management-award-category-virtual-item">
      <div className="sponsor-management-award-category-row">
        <div className="sponsor-management-award-category-inputs">
          <input
            type="text"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onBlur={() => onBlurCommit(category.id, draftName, draftSubtitle, category)}
            className="sponsor-management-award-category-input"
          />
          <input
            type="text"
            value={draftSubtitle}
            onChange={(e) => setDraftSubtitle(e.target.value)}
            onBlur={() => onBlurCommit(category.id, draftName, draftSubtitle, category)}
            className="sponsor-management-award-category-subtitle-input"
            placeholder="e.g. 1st place · Freshling division"
          />
          {warning ? <div className="sponsor-management-award-category-warning">{warning}</div> : null}
        </div>
        <button
          type="button"
          className="sponsor-management-btn sponsor-management-btn-sm sponsor-management-btn-danger"
          onClick={() => onDelete(category.id)}
          title="Remove category"
        >
          ×
        </button>
      </div>
    </div>
  );
});

function extractYearLabel(text) {
  const match = String(text || '').match(/\b(20\d{2})\b/);
  return match ? match[1] : '';
}

function timelineEntryMillis(ts) {
  if (ts && typeof ts.toMillis === 'function') return ts.toMillis();
  return 0;
}

/** Chronological (oldest → newest). If over `limit`, keeps the latest `limit` entries. */
function mergeOutreachAndNotesAscending(outreach, notes, limit) {
  const o = outreach.map((e) => ({ kind: 'outreach', entry: e, key: `o-${e.id}` }));
  const n = notes.map((e) => ({ kind: 'note', entry: e, key: `n-${e.id}` }));
  const merged = [...o, ...n].sort(
    (a, b) => timelineEntryMillis(a.entry.createdAt) - timelineEntryMillis(b.entry.createdAt)
  );
  if (!Number.isFinite(limit) || merged.length <= limit) return merged;
  return merged.slice(-limit);
}

function formatSponsorLastResponseCell(s) {
  const oc = s.lastOutreachOutcome ? outreachOutcomeLabel(s.lastOutreachOutcome) : '';
  const sum = (s.lastOutreachSummary || '').trim();
  if (!oc && !sum) return '—';
  const short = sum.length > 72 ? `${sum.slice(0, 72)}…` : sum;
  if (oc && short) return `${oc} — ${short}`;
  return oc || short || '—';
}

/** Short label for sponsor table: first name, or email local-part (not full email). */
function compactAuthorForSponsorTable(raw) {
  const s = (raw || '').trim();
  if (!s) return '';
  if (s.includes('@')) {
    const local = s.split('@')[0] || '';
    const token = local.split(/[._+-]/)[0] || local;
    if (!token) return s;
    return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
  }
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return parts[0];
  return s;
}

function formatSponsorLastTouchCell(s) {
  const whoRaw = (s.lastTouchByDisplayName || '').trim();
  const who = compactAuthorForSponsorTable(whoRaw) || whoRaw;
  const when = formatFirestoreDateTime(s.lastTouchAt);
  if (!who && (when === '—' || !when)) return '—';
  if (who && when !== '—') return `${who} · ${when}`;
  return who || when;
}

function formatSponsorLastTouchCellTitle(s) {
  const whoRaw = (s.lastTouchByDisplayName || '').trim();
  const when = formatFirestoreDateTime(s.lastTouchAt);
  if (!whoRaw && (when === '—' || !when)) return undefined;
  if (whoRaw && when !== '—') return `${whoRaw} · ${when}`;
  return whoRaw || (when !== '—' ? when : undefined);
}

/** Merged outreach + row-note items (chronological), same shape as activity popover cache. */
function renderSponsorMergedTimelineList(items) {
  if (!items || items.length === 0) return null;
  return (
    <ul className="sponsor-management-activity-timeline-list sponsor-management-conversation-thread">
      {items.map((item) => {
        if (item.kind === 'outreach') {
          const row = item.entry;
          const isPipelineChange = row.type === 'pipeline_change';
          return (
            <li
              key={item.key}
              className={`sponsor-management-activity-timeline-item ${isPipelineChange ? 'sponsor-management-activity-timeline-pipeline-change sponsor-management-timeline-pipeline-change' : 'sponsor-management-activity-timeline-outreach sponsor-management-timeline-outreach'} sponsor-management-conversation-entry`}
            >
              <div className="sponsor-management-conversation-entry-header">
                <span className={`sponsor-management-timeline-badge ${isPipelineChange ? 'sponsor-management-timeline-badge-pipeline-change' : 'sponsor-management-timeline-badge-outreach'}`}>
                  {isPipelineChange ? 'Pipeline' : 'Outreach'}
                </span>
                <span className="sponsor-management-conversation-who-when">
                  {row.authorDisplayName || 'Unknown'} · {formatFirestoreDateTime(row.createdAt)}
                </span>
              </div>
              {isPipelineChange ? (
                <>
                  <div className="sponsor-management-activity-timeline-body">
                    Pipeline changed: <strong>{sponsorPipelineStageDisplayLabel(row.from)}</strong> → <strong>{sponsorPipelineStageDisplayLabel(row.to)}</strong>
                  </div>
                  {row.body ? (
                    <div className="sponsor-management-conversation-entry-footer">{row.body}</div>
                  ) : null}
                </>
              ) : (
                <>
                  <div className="sponsor-management-activity-timeline-body">{row.body}</div>
                  <div className="sponsor-management-conversation-entry-footer">
                    {outreachChannelLabel(row.channel)}
                    {row.outcome ? ` · ${outreachOutcomeLabel(row.outcome)}` : ''}
                    {row.nextFollowUpAt ? ` · Follow up ${formatFirestoreDateOnly(row.nextFollowUpAt)}` : ''}
                  </div>
                </>
              )}
            </li>
          );
        }
        const n = item.entry;
        return (
          <li
            key={item.key}
            className="sponsor-management-activity-timeline-item sponsor-management-activity-timeline-note sponsor-management-conversation-entry"
          >
            <div className="sponsor-management-conversation-entry-header">
              <span className="sponsor-management-timeline-badge sponsor-management-timeline-badge-note">Note</span>
              <span className="sponsor-management-conversation-who-when">
                {n.authorDisplayName || 'Unknown'} · {formatFirestoreDateTime(n.createdAt)}
                {noteWasEdited(n) ? ` · Edited ${formatFirestoreDateTime(n.updatedAt)}` : ''}
              </span>
            </div>
            <div className="sponsor-management-activity-timeline-body">{n.body}</div>
          </li>
        );
      })}
    </ul>
  );
}

const SPONSOR_TYPES = [
  { value: 'none', label: 'None' },
  { value: 'prize', label: 'Prize' },
  { value: 'exhibit', label: 'Exhibit' },
  { value: 'interactive', label: 'Interactive' },
];

/** Max entries shown in Activity popover + inline thread (merged chronological tail). */
const SPONSOR_MERGED_TIMELINE_LIMIT = 200;

const SPONSOR_PIPELINE_STAGES = [
  { value: '', label: 'NOT CONTACTED' },
  { value: 'save_the_date', label: 'SAVE THE DATE' },
  { value: 'offer_sent', label: 'OFFER SENT' },
  { value: 'waiting', label: 'WAITING' },
  { value: 'responded_yes', label: 'RESPONDED YES' },
  { value: 'responded_no', label: 'RESPONDED NO' },
  { value: 'responded_maybe', label: 'RESPONDED MAYBE' },
  { value: 'declined', label: 'DECLINED' },
  { value: 'confirmed', label: 'CONFIRMED ✓' },
];

const SPONSOR_PIPELINE_STAGE_LABEL_MAP = SPONSOR_PIPELINE_STAGES.reduce((acc, { value, label }) => {
  acc[value] = label;
  return acc;
}, {});

function sponsorPipelineStageDisplayLabel(raw) {
  if (raw === undefined || raw === null) return '—';
  const v = String(raw);
  if (Object.prototype.hasOwnProperty.call(SPONSOR_PIPELINE_STAGE_LABEL_MAP, v)) {
    return SPONSOR_PIPELINE_STAGE_LABEL_MAP[v];
  }
  const trimmed = v.trim();
  if (Object.prototype.hasOwnProperty.call(SPONSOR_PIPELINE_STAGE_LABEL_MAP, trimmed)) {
    return SPONSOR_PIPELINE_STAGE_LABEL_MAP[trimmed];
  }
  if (!trimmed) return '—';
  return trimmed.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const SPONSOR_BILLING_STAGES = [
  { value: '', label: '(not set)' },
  { value: 'needs_invoice', label: 'NEEDS INVOICE' },
  { value: 'invoiced', label: 'INVOICE SENT' },
  { value: 'paid', label: 'INVOICE PAID' },
];

function derivePipelineFromLegacy(s) {
  const v = (s.sponsorPipelineStage || '').trim();
  if (v) return v;
  if (!s.saveTheDate) return '';
  if (!s.sentEmail) return 'save_the_date';
  if (!s.answered) return 'waiting';
  return 'responded_yes';
}

function deriveBillingFromLegacy(s) {
  const v = (s.sponsorBillingStage || '').trim();
  if (v) return v;
  if (s.paid) return 'paid';
  if (s.invoiceSent) return 'invoiced';
  return '';
}

function legacyBoolPayloadFromStages(pipeline, billing) {
  const pIdx = SPONSOR_PIPELINE_STAGES.findIndex((x) => x.value === pipeline);
  const bIdx = SPONSOR_BILLING_STAGES.findIndex((x) => x.value === billing);
  const pi = pIdx < 0 ? 0 : pIdx;
  const bi = bIdx < 0 ? 0 : bIdx;
  return {
    saveTheDate: pi >= 1,
    sentEmail: pi >= 2,
    answered: pi >= 4,
    invoiceSent: bi >= 2,
    paid: bi >= 3,
  };
}

/** Compact preview for the Last note cell. */
function formatActivityCellPreview(s) {
  const text = (s.lastNote?.text || '').trim();
  if (!text) return { snippet: '—', empty: true };
  return {
    snippet: `${text.slice(0, 52)}${text.length > 52 ? '…' : ''}`,
    empty: false,
  };
}

const GIVEAWAY_TYPES = [
  { value: 'general_raffle', label: 'General Raffle' },
  { value: 'bucket_raffle', label: 'Bucket Raffle' },
  { value: 'plinko', label: 'Plinko' },
  { value: 'goody_bag', label: 'Goody Bag' },
  { value: 'prize', label: 'Prize' },
  { value: 'all_freshlings', label: 'All Freshlings' },
  { value: 'unknown', label: 'Unknown' },
];

const PRIZE_STATUSES = [
  { value: 'assumed', label: 'ASSUMED', bg: '#C8C8C8', fg: '#1A1A1A' },
  { value: 'confirmed', label: 'CONFIRMED', bg: '#F5F4F0', fg: '#D96510', border: '1.5px solid #F47B20' },
  { value: 'received', label: 'RECEIVED', bg: '#F47B20', fg: '#FFFFFF' },
  { value: 'printed', label: 'PRINTED', bg: '#3D3D3D', fg: '#F5F4F0' },
  { value: 'awarded', label: 'AWARDED', bg: '#1A1A1A', fg: '#F47B20' },
];

const PRIZE_STATUS_INDEX = PRIZE_STATUSES.reduce((acc, row, idx) => {
  acc[row.value] = idx;
  return acc;
}, {});

const PIPELINE_BADGE_STYLES = {
  '': { bg: '#C8C8C8', fg: '#1A1A1A', border: '1px solid #C8C8C8' },
  save_the_date: { bg: '#F5F4F0', fg: '#3D3D3D', border: '1px solid #C8C8C8' },
  offer_sent: { bg: '#F5F4F0', fg: '#D96510', border: '1px solid #F47B20' },
  waiting: { bg: '#F5F4F0', fg: '#3D3D3D', border: '1px solid #C8C8C8' },
  responded_yes: { bg: '#F5F4F0', fg: '#D96510', border: '1px solid #F47B20' },
  responded_no: { bg: '#1A1A1A', fg: '#C8C8C8', border: '1px solid #1A1A1A' },
  responded_maybe: { bg: '#F5F4F0', fg: '#3D3D3D', border: '1px solid #C8C8C8' },
  confirmed: { bg: '#F47B20', fg: '#FFFFFF', border: '1px solid #F47B20' },
  declined: { bg: '#1A1A1A', fg: '#C8C8C8', border: '1px solid #1A1A1A' },
};

const BILLING_BADGE_STYLES = {
  '': { bg: '#C8C8C8', fg: '#1A1A1A', border: '1px solid #C8C8C8' },
  /** Billing stays visually quieter than pipeline (no solid FM orange). */
  needs_invoice: { bg: '#F8FAFC', fg: '#334155', border: '1px solid #64748b' },
  invoiced: { bg: '#F5F4F0', fg: '#334155', border: '1px solid #94a3b8' },
  paid: { bg: '#1e293b', fg: '#f1f5f9', border: '1px solid #1e293b' },
};

// Award categories for "Prize" giveaway type — who gets what (prize from list or custom e.g. medal, mug)
const AWARD_CATEGORIES = [
  { id: 'award-0', name: 'Best Team Name FRESHLING' },
  { id: 'award-1', name: 'Best Team Name FRESHLING' },
  { id: 'award-2', name: 'Best Team Name MENTOR' },
  { id: 'award-3', name: 'Sub 75 3rd Place' },
  { id: 'award-4', name: 'Sub 75 2nd Place' },
  { id: 'award-5', name: 'Sub 75 1st Place' },
  { id: 'award-6', name: 'Skychamps FRESHLING' },
  { id: 'award-7', name: 'Skychamps FRESHLING' },
  { id: 'award-8', name: 'Skychamps MENTOR' },
  { id: 'award-9', name: 'Freshest Freshling' },
  { id: 'award-10', name: 'Freshest Freshling' },
  { id: 'award-11', name: 'Freshest Freshling' },
  { id: 'award-12', name: 'Forever Fresh' },
  { id: 'award-13', name: 'Forever Fresh' },
  { id: 'award-14', name: '3rd Beginner FRESHLING' },
  { id: 'award-15', name: '3rd Beginner FRESHLING' },
  { id: 'award-16', name: '3rd Beginner MENTOR' },
  { id: 'award-17', name: '2nd Beginner FRESHLING' },
  { id: 'award-18', name: '2nd Beginner FRESHLING' },
  { id: 'award-19', name: '2nd Beginner MENTOR' },
  { id: 'award-20', name: '1st Beginner FRESHLING' },
  { id: 'award-21', name: '1st Beginner FRESHLING' },
  { id: 'award-22', name: '1st Beginner MENTOR' },
  { id: 'award-23', name: '3rd Intermediate FRESHLING' },
  { id: 'award-24', name: '3rd Intermediate FRESHLING' },
  { id: 'award-25', name: '3rd Intermediate MENTOR' },
  { id: 'award-26', name: '2nd Intermediate FRESHLING' },
  { id: 'award-27', name: '2nd Intermediate FRESHLING' },
  { id: 'award-28', name: '2nd Intermediate MENTOR' },
  { id: 'award-29', name: '1st Intermediate FRESHLING' },
  { id: 'award-30', name: '1st Intermediate FRESHLING' },
  { id: 'award-31', name: '1st Intermediate MENTOR' },
];

function normalizeCategoryTitle(s) {
  return String(s || '').replace(/\b[a-z]/gi, (c) => c.toUpperCase());
}

function normalizeCategoryNameKeepingConventions(name) {
  return normalizeCategoryTitle(name)
    .replace(/\bFreshling\b/g, 'FRESHLING')
    .replace(/\bMentor\b/g, 'MENTOR');
}

function categoryName(cat) {
  return (cat?.name || cat?.label || '').trim();
}

function categoryDisplayLabel(cat) {
  const name = categoryName(cat);
  const subtitle = (cat?.subtitle || '').trim();
  return subtitle ? `${name} — ${subtitle}` : (name || '—');
}

function migrateAwardCategories(rawCategories) {
  const source = Array.isArray(rawCategories) && rawCategories.length ? rawCategories : AWARD_CATEGORIES;
  const migrated = source.map((cat, idx) => ({
    id: cat?.id || `award-${idx}`,
    name: normalizeCategoryNameKeepingConventions(cat?.name || cat?.label || `Category ${idx + 1}`),
    subtitle: (cat?.subtitle || '').trim(),
  }));
  const byName = new Map();
  migrated.forEach((cat) => {
    const key = categoryName(cat).toLowerCase();
    if (!key) return;
    if (!byName.has(key)) byName.set(key, []);
    byName.get(key).push(cat);
  });
  byName.forEach((dupes) => {
    if (dupes.length < 2) return;
    dupes.forEach((cat, idx) => {
      if (!cat.subtitle) cat.subtitle = `Variant ${idx + 1}`;
    });
  });
  return migrated;
}

/** HTML-escape cell text for print export tables. */
function escapeHtmlCell(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const SPONSORS_EXPORT_COLS_STATIC = [
  { id: 'type', label: 'Type', default: true },
  { id: 'company', label: 'Company', default: true },
  { id: 'website', label: 'Website', default: true },
  { id: 'contact', label: 'Contact', default: true },
  { id: 'logo', label: 'Logo URL', default: false },
  { id: 'dash', label: 'Dashboard', default: false },
  { id: 'sched', label: 'Schedule', default: false },
  { id: 'rules', label: 'Rules page', default: false },
  { id: 'bonus', label: 'Bonus puzzles', default: false },
  { id: 'pipeline', label: 'Pipeline', default: false },
  { id: 'billing', label: 'Billing', default: false },
  { id: 'lastTouchBy', label: 'Last touched by', default: false },
  { id: 'nextFollowUp', label: 'Next follow-up', default: false },
  { id: 'lastResponse', label: 'Last response', default: false },
];

const SPONSORS_EXPORT_COLS_COMPARE = [
  { id: 'priorStatus', label: 'Prior status', default: true },
  { id: 'priorAnswered', label: 'Prior answered', default: true },
];

const DIRECTORY_EXPORT_COLS = [
  { id: 'company', label: 'Company', default: true },
  { id: 'activeContacts', label: 'Active contacts', default: true },
  { id: 'inThisEvent', label: 'In this event', default: true },
];

const PRIZES_EXPORT_COLS = [
  { id: 'giveaway', label: 'Giveaway', default: true },
  { id: 'quantity', label: '#', default: true },
  { id: 'sponsor', label: 'Sponsor', default: true },
  { id: 'nameDesc', label: 'Name / description', default: true },
  { id: 'status', label: 'Status', default: true },
  { id: 'expires', label: 'Expires', default: true },
  { id: 'unlisted', label: 'Unlisted', default: false },
];

const AWARDS_EXPORT_COLS = [
  { id: 'category', label: 'Award category', default: true },
  { id: 'items', label: 'Items won', default: true },
  { id: 'wonBy', label: 'Won by', default: true },
];

const RAFFLES_EXPORT_COLS = [
  { id: 'num', label: '#', default: true },
  { id: 'type', label: 'Type', default: true },
  { id: 'sponsor', label: 'Sponsor', default: true },
  { id: 'prize', label: 'Prize', default: true },
  { id: 'wonBy', label: 'Won by', default: true },
  { id: 'sentToSponsor', label: 'Sent to sponsor', default: false },
];

const BUCKET_EXPORT_COLS = [
  { id: 'prize', label: 'Prize', default: true },
  { id: 'sponsor', label: 'Sponsor', default: true },
  { id: 'entry', label: 'Entry', default: true },
];

const BUCKET_SCAN_SKIP_LEADING_LS = 'fm.sponsorMgmt.bucketScan.skipLeading';

function readBucketScanSkipLeadingFromStorage() {
  try {
    const raw = localStorage.getItem(BUCKET_SCAN_SKIP_LEADING_LS);
    const n = parseInt(raw || '0', 10);
    if (!Number.isFinite(n) || n < 0) return 0;
    return Math.min(500, n);
  } catch {
    return 0;
  }
}

function persistBucketScanSkipLeading(n) {
  try {
    localStorage.setItem(BUCKET_SCAN_SKIP_LEADING_LS, String(n));
  } catch {
    /* ignore quota / private mode */
  }
}

const PRIZE_HISTORY_EXPORT_COLS = [
  { id: 'event', label: 'Event', default: true },
  { id: 'name', label: 'Name', default: true },
  { id: 'description', label: 'Description', default: true },
  { id: 'giveaway', label: 'Giveaway', default: true },
];

const SPONSOR_EXPORT_SUBTITLE = {
  sponsors: 'Sponsors',
  directory: 'Sponsor directory',
  prizes: 'Prizes',
  awardPrizes: 'Award prizes',
  raffles: 'Raffle draw order',
  bucketEntries: 'Bucket raffle entries',
  prizeHistory: 'Prize history',
};

/** Normalize a bucket registration row (legacy plain string or object). */
function normalizeBucketEntry(raw) {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    const t = raw.trim();
    return t ? { winnerFlyerId: null, winnerName: t } : null;
  }
  if (typeof raw === 'object') {
    const id = raw.winnerFlyerId || raw.flyerId || null;
    const name = String(raw.winnerName ?? raw.name ?? '').trim();
    if (id) return { winnerFlyerId: id, winnerName: name };
    if (name) return { winnerFlyerId: null, winnerName: name };
  }
  return null;
}

function escapeHtmlForEmail(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatHumanNameList(names) {
  const list = Array.isArray(names) ? names.map((n) => String(n || '').trim()).filter(Boolean) : [];
  if (list.length === 0) return '';
  if (list.length === 1) return list[0];
  if (list.length === 2) return `${list[0]} and ${list[1]}`;
  return `${list.slice(0, -1).join(', ')}, and ${list[list.length - 1]}`;
}

function firstNameOnly(nameOrEmail) {
  const raw = String(nameOrEmail || '').trim();
  if (!raw) return '';
  // Prefer first token of a name; fall back to email local-part if needed.
  const token = raw.split(/[,\s]+/).filter(Boolean)[0] || '';
  if (!token) return '';
  const cleaned = token.replace(/[^\p{L}\p{N}'-]+/gu, '').trim();
  if (cleaned) return cleaned;
  const at = raw.indexOf('@');
  if (at > 0) return raw.slice(0, at);
  return raw;
}

function possessive(name) {
  const n = String(name || '').trim();
  if (!n) return '';
  return /[sS]$/.test(n) ? `${n}'` : `${n}'s`;
}

/** Primary + contact emails, de-duplicated by lowercased address (preserves first-seen casing). */
function collectSponsorContactEmails(sponsor) {
  if (!sponsor) return [];
  const primary = (sponsor.contactEmail || '').toString().trim();
  const extra = Array.isArray(sponsor.contacts)
    ? sponsor.contacts
      .filter((c) => c && c.active !== false)
      .map((c) => (c.email || '').toString().trim())
      .filter(Boolean)
    : [];
  const seen = new Set();
  const out = [];
  [primary, ...extra].filter(Boolean).forEach((em) => {
    const low = em.toLowerCase();
    if (seen.has(low)) return;
    seen.add(low);
    out.push(em);
  });
  return out;
}

/**
 * Prize / award / raffle / bucket follow-up block for one sponsor (HTML for email merge).
 */
function buildSponsorFollowUpDetailsHtml({
  sponsorId,
  awardData,
  raffleOrdered,
  getRafflePrize,
  getRaffleWinner,
  getCategoryWinner,
  getWinnerDisplayLabel,
  prizes,
  prizeTypePrizes,
  bucketRegistrations,
  freshlings,
  mentors,
}) {
  const flyerById = (id) => {
    if (!id) return null;
    return freshlings.find((x) => x.id === id) || mentors.find((x) => x.id === id) || null;
  };
  const winnerEmail = (w) => {
    const f = flyerById(w?.winnerFlyerId);
    return ((f && f.email) || '').trim() || '—';
  };

  const awardWon = [];
  awardData.categories.forEach((cat) => {
    const a = awardData.assignments[cat.id];
    if (!a || !Array.isArray(a.items)) return;
    const winner = getCategoryWinner(cat.id);
    const winnerLabel = getWinnerDisplayLabel(winner);
    if (winnerLabel === '—') return;
    a.items.forEach((it) => {
      if (it.type !== 'prize' || !it.prizeId) return;
      const p = prizeTypePrizes.find((x) => x.id === it.prizeId);
      if (!p || p.sponsorId !== sponsorId) return;
      awardWon.push({
        categoryLabel: categoryDisplayLabel(cat),
        prizeTitle: p.name || p.description || p.id,
        prizeDescription: (p.description || '').trim(),
        prize: p,
        winnerLabel,
        winner,
      });
    });
  });

  const raffleWon = [];
  raffleOrdered.forEach((entry, idx) => {
    const prize = getRafflePrize(entry);
    if (!prize || prize.sponsorId !== sponsorId) return;
    const rw = getRaffleWinner(idx);
    const winnerLabel = getWinnerDisplayLabel(rw);
    if (winnerLabel === '—') return;
    raffleWon.push({
      prizeTitle: prize.name || prize.description || entry.prizeId,
      raffleTypeLabel: entry.type === 'general' ? 'General raffle' : 'Bucket raffle',
      winnerLabel,
      winner: rw,
      prize,
    });
  });

  const awardPending = [];
  awardData.categories.forEach((cat) => {
    const a = awardData.assignments[cat.id];
    if (!a || !Array.isArray(a.items)) return;
    const winnerLabel = getWinnerDisplayLabel(getCategoryWinner(cat.id));
    if (winnerLabel !== '—') return;
    a.items.forEach((it) => {
      if (it.type !== 'prize' || !it.prizeId) return;
      const p = prizeTypePrizes.find((x) => x.id === it.prizeId);
      if (!p || p.sponsorId !== sponsorId) return;
      awardPending.push(`${p.name || p.description || p.id} (${categoryDisplayLabel(cat)})`);
    });
  });

  const rafflePending = [];
  raffleOrdered.forEach((entry, idx) => {
    const prize = getRafflePrize(entry);
    if (!prize || prize.sponsorId !== sponsorId) return;
    if (getWinnerDisplayLabel(getRaffleWinner(idx)) !== '—') return;
    rafflePending.push(
      `${prize.name || prize.description || entry.prizeId} (${entry.type === 'general' ? 'General raffle' : 'Bucket raffle'})`
    );
  });

  const pendingDedup = Array.from(new Set([...awardPending, ...rafflePending]));

  let anyWinnerPhotos = false;
  const attachmentUrls = [];
  [...awardWon, ...raffleWon].forEach((row) => {
    const urls = row.prize && Array.isArray(row.prize.winnerPhotoUrls) ? row.prize.winnerPhotoUrls : [];
    if (urls.length) {
      anyWinnerPhotos = true;
      urls.forEach((u) => {
        const url = (u || '').toString().trim();
        if (url) attachmentUrls.push(url);
      });
    }
  });
  // Dedupe and cap to keep emails safe to send.
  const uniqueAttachmentUrls = Array.from(new Set(attachmentUrls)).slice(0, 6);

  const pText = 'margin:0 0 12px 0;font-size:14px;line-height:1.5;';
  const hr =
    '<p style="margin:20px 0 0 0;padding-top:16px;border-top:1px solid #e2e8f0;line-height:0;font-size:0;">&nbsp;</p>';

  const blocks = [];
  let firstBlock = true;
  const pushSection = (innerHtml) => {
    const sep = firstBlock ? '' : hr;
    firstBlock = false;
    blocks.push(`${sep}${innerHtml}`);
  };

  awardWon.forEach((row) => {
    pushSection(
      `<p style="${pText}"><strong>Prize</strong><br/>${escapeHtmlForEmail(row.prizeTitle)}</p>` +
        `<p style="${pText}"><strong>Winner:</strong> ${escapeHtmlForEmail(row.winnerLabel)} (${escapeHtmlForEmail(winnerEmail(row.winner))})</p>` +
        `<p style="${pText}"><strong>Award</strong><br/>${escapeHtmlForEmail(row.categoryLabel)}</p>` +
        (row.prizeDescription
          ? `<p style="${pText}">${escapeHtmlForEmail(row.prizeDescription)}</p>`
          : '')
    );
  });

  raffleWon.forEach((row) => {
    pushSection(
      `<p style="${pText}"><strong>Prize</strong><br/>${escapeHtmlForEmail(row.prizeTitle)}</p>` +
        `<p style="${pText}"><strong>Draw:</strong> ${escapeHtmlForEmail(row.raffleTypeLabel)}</p>` +
        `<p style="${pText}"><strong>Winner:</strong> ${escapeHtmlForEmail(row.winnerLabel)} (${escapeHtmlForEmail(winnerEmail(row.winner))})</p>`
    );
  });

  if (anyWinnerPhotos) {
    pushSection(`<p style="${pText}">Photos of the winner(s) are attached.</p>`);
  }

  const bucketParts = [];
  const bucketPrizeList = (bucketRegistrations && typeof bucketRegistrations === 'object' ? bucketRegistrations : {});
  const allPrizes = Array.isArray(prizes) ? prizes : [];
  Object.keys(bucketPrizeList).forEach((prizeId) => {
    const prize = allPrizes.find((x) => x.id === prizeId);
    if (!prize || prize.sponsorId !== sponsorId || (prize.giveawayType || '') !== 'bucket_raffle') return;
    const entries = bucketPrizeList[prizeId] || [];
    if (!entries.length) return;
    const title = prize.name || prize.description || prizeId;
    const lines = entries
      .map((e) => {
        const label = getWinnerDisplayLabel(e);
        const em = winnerEmail(e);
        return `<li style="margin:0 0 6px 0;font-size:14px;line-height:1.45;">${escapeHtmlForEmail(label)} (${escapeHtmlForEmail(em)})</li>`;
      })
      .join('');
    bucketParts.push(
      `<p style="${pText}"><strong>Bucket entries — ${escapeHtmlForEmail(title)}</strong></p>` +
        `<ul style="margin:0 0 12px 0;padding-left:1.2rem;">${lines}</ul>`
    );
  });

  if (bucketParts.length) {
    pushSection(
      `<p style="${pText}"><strong>Bucket entries</strong></p>` +
        `<p style="${pText}">The following Freshlings entered for your prize:</p>` +
        bucketParts.join('') +
        `<p style="${pText}">These participants specifically expressed interest in your product. A well-timed follow-up email or small offer could be an effective way to connect with this group.</p>`
    );
  }

  if (pendingDedup.length) {
    pushSection(
      `<p style="${pText}"><strong>Additional prizes</strong></p>` +
        `<p style="${pText}">Thank you for these prizes as well. They are not recorded as awarded yet; we will follow up when winners are finalized:</p>` +
        `<ul style="margin:0 0 0 1.1rem;padding:0;font-size:14px;line-height:1.5;">${pendingDedup
          .map((line) => `<li>${escapeHtmlForEmail(line)}</li>`)
          .join('')}</ul>`
    );
  }

  return { html: blocks.join('\n'), attachmentUrls: uniqueAttachmentUrls };
}

const SponsorManagement = () => {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [sponsors, setSponsors] = useState([]);
  const [prizes, setPrizes] = useState([]);
  const [freshlings, setFreshlings] = useState([]);
  const [mentors, setMentors] = useState([]);
  const [allFlyers, setAllFlyers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [dzMappings, setDzMappings] = useState({});
  const [memoryStatsOverrides, setMemoryStatsOverrides] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [sponsorModalOpen, setSponsorModalOpen] = useState(false);
  const [sponsorModalInvalidNames, setSponsorModalInvalidNames] = useState(() => new Set());
  const [editingSponsorId, setEditingSponsorId] = useState(null);
  const [sponsorForm, setSponsorForm] = useState({
    directoryId: '',
    type: 'exhibit',
    name: '',
    website: '',
    contactEmail: '',
    logo: '',
    displayOrder: 0,
    contacts: [],
    invoiceContactIndex: 0,
    mainContactIndex: 0,
    organizationNotes: '',
  });
  const [sponsorLogoMode, setSponsorLogoMode] = useState('directory'); // directory | event_upload
  const [sponsorLogoUploadedThisEvent, setSponsorLogoUploadedThisEvent] = useState(false);
  const [directoryEntries, setDirectoryEntries] = useState([]);
  const [directoryNameMatches, setDirectoryNameMatches] = useState([]);
  const [directoryNameDebounced, setDirectoryNameDebounced] = useState('');
  const [directoryEntriesLoading, setDirectoryEntriesLoading] = useState(false);

  const [prizeModalOpen, setPrizeModalOpen] = useState(false);
  const [editingPrizeId, setEditingPrizeId] = useState(null);
  const [prizeForm, setPrizeForm] = useState({
    sponsorId: '',
    name: '',
    description: '',
    giveawayType: 'general_raffle',
    status: 'assumed',
    quantity: 1,
    imageUrl: '',
    winnerPhotoUrls: [],
    expirationDate: '',
    unlisted: false,
  });
  const prizeFormFirstInputRef = useRef(null);
  const saveAndAddAnotherRef = useRef(false);
  const prizeFormRef = useRef(null);
  const prizeImageInputRef = useRef(null);
  const prizeWinnerPhotoInputRef = useRef(null);
  const [prizeImageUploading, setPrizeImageUploading] = useState(false);
  const [prizeWinnerPhotoUploading, setPrizeWinnerPhotoUploading] = useState(false);

  const [directorySeedModalOpen, setDirectorySeedModalOpen] = useState(false);
  const [directorySeedEventId, setDirectorySeedEventId] = useState('');
  const [directorySeedBusy, setDirectorySeedBusy] = useState(false);
  const [directorySeedError, setDirectorySeedError] = useState('');
  const [directorySeedSummary, setDirectorySeedSummary] = useState('');
  const [directoryRefreshToken, setDirectoryRefreshToken] = useState(0);
  const [detailsSponsor, setDetailsSponsor] = useState(null);
  const [contactPopoverId, setContactPopoverId] = useState(null);
  const contactPopoverRef = useRef(null);
  const [activityPopoverSponsorId, setActivityPopoverSponsorId] = useState(null);
  const activityPopoverRef = useRef(null);
  const activityTimelineCacheRef = useRef({});
  const [activityTimelineUi, setActivityTimelineUi] = useState(() => ({}));
  const [showLogoInclusionColumns, setShowLogoInclusionColumns] = useState(false);
  const [showNoneType, setShowNoneType] = useState(false);
  const [sortBy, setSortBy] = useState('type');
  const [prizeSortKey, setPrizeSortKey] = useState('giveawayType'); // 'giveawayType' | 'sponsor'
  const [prizeSortDir, setPrizeSortDir] = useState('asc'); // 'asc' | 'desc'
  const [prizeStatusFilter, setPrizeStatusFilter] = useState('all');
  const [openPrizeStatusMenuId, setOpenPrizeStatusMenuId] = useState(null);
  const [prizeStatusMenuLayout, setPrizeStatusMenuLayout] = useState(null);
  const prizeStatusAnchorRef = useRef(null);
  const prizeStatusDropdownRef = useRef(null);

  const closePrizeStatusMenu = useCallback(() => {
    setOpenPrizeStatusMenuId(null);
    setPrizeStatusMenuLayout(null);
    prizeStatusAnchorRef.current = null;
  }, []);

  const syncPrizeStatusMenuPosition = useCallback(() => {
    const el = prizeStatusAnchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const minW = Math.max(r.width, 150);
    const pad = 8;
    let left = r.left;
    left = Math.min(left, window.innerWidth - minW - pad);
    left = Math.max(pad, left);
    setPrizeStatusMenuLayout({
      top: r.bottom + 4,
      left,
      minWidth: minW,
    });
  }, []);
  const [expandedNoteId, setExpandedNoteId] = useState(null);
  const [tableQuickNoteDraft, setTableQuickNoteDraft] = useState(() => ({}));
  const [tableQuickNoteSubmittingId, setTableQuickNoteSubmittingId] = useState(null);
  const [activeTab, setActiveTab] = useState('sponsors');
  const closeSponsorModal = useCallback(() => {
    setSponsorModalOpen(false);
    setEditingSponsorId(null);
  }, []);
  const [compareEventId, setCompareEventId] = useState('');
  const [compareMap, setCompareMap] = useState(() => new Map());
  /** Raw directory orgs from API; merged with `sponsors` in `directoryList` useMemo (no refetch on every sponsor row edit). */
  const [directoryOrgsRaw, setDirectoryOrgsRaw] = useState([]);
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const [directoryDetailsOrg, setDirectoryDetailsOrg] = useState(null);
  const [directoryDetailsLoading, setDirectoryDetailsLoading] = useState(false);
  const [directoryOrgNotes, setDirectoryOrgNotes] = useState([]);
  const [directoryOrgNotesLoading, setDirectoryOrgNotesLoading] = useState(false);
  const [newDirectoryOrgNoteText, setNewDirectoryOrgNoteText] = useState('');
  const [directoryOrgNoteSubmitting, setDirectoryOrgNoteSubmitting] = useState(false);
  const [editingDirectoryOrgNoteId, setEditingDirectoryOrgNoteId] = useState(null);
  const [editingDirectoryOrgNoteText, setEditingDirectoryOrgNoteText] = useState('');
  const [sponsorRowNoteLog, setSponsorRowNoteLog] = useState([]);
  const [sponsorOutreachLog, setSponsorOutreachLog] = useState([]);
  const [sponsorEventTimelineLoading, setSponsorEventTimelineLoading] = useState(false);
  const [newSponsorRowNoteText, setNewSponsorRowNoteText] = useState('');
  const [sponsorRowNoteSubmitting, setSponsorRowNoteSubmitting] = useState(false);
  const [editingSponsorRowNoteId, setEditingSponsorRowNoteId] = useState(null);
  const [editingSponsorRowNoteText, setEditingSponsorRowNoteText] = useState('');
  const [outreachChannel, setOutreachChannel] = useState('email');
  const [outreachOutcome, setOutreachOutcome] = useState('');
  const [outreachBody, setOutreachBody] = useState('');
  const [outreachNextFollowUpDate, setOutreachNextFollowUpDate] = useState('');
  const [outreachSubmitting, setOutreachSubmitting] = useState(false);
  const [pipelineNudgeBySponsor, setPipelineNudgeBySponsor] = useState({});
  const [pipelineNudgeNoteBySponsor, setPipelineNudgeNoteBySponsor] = useState({});
  const [sponsorToast, setSponsorToast] = useState('');
  const [sponsorModalMetaBusy, setSponsorModalMetaBusy] = useState(false);
  const [prizeHistoryModal, setPrizeHistoryModal] = useState(null);
  const [prizeHistoryRows, setPrizeHistoryRows] = useState([]);
  const [prizeHistoryLoading, setPrizeHistoryLoading] = useState(false);
  const { emailComposerProps, openEmailComposer, closeEmailComposer } = useEmailComposer();
  const [emailSponsorType, setEmailSponsorType] = useState('all'); // all | interactive | exhibit | prize | interactive_exhibit
  const [awardData, setAwardData] = useState({
    categories: [],
    otherItems: [],
    assignments: {},
  });
  const [newOtherItemLabel, setNewOtherItemLabel] = useState('');
  const [awardAddSelection, setAwardAddSelection] = useState({}); // { [catId]: string[] } e.g. ['prize:id', 'other:Medal']
  const [expandedAwardCategories, setExpandedAwardCategories] = useState(() => new Set()); // expanded row IDs; empty = all collapsed by default
  const [openAddDropdownCatId, setOpenAddDropdownCatId] = useState(null);
  const awardAddDropdownRef = useRef(null);
  const [awardCategoriesBlockCollapsed, setAwardCategoriesBlockCollapsed] = useState(true);
  const [awardCategoryWarnings, setAwardCategoryWarnings] = useState({});
  const [openWonByKey, setOpenWonByKey] = useState(null); // 'catId-idx' or 'raffle-{type}-{prizeId}' when dropdown open
  const [wonBySearch, setWonBySearch] = useState('');
  const wonByDropdownRef = useRef(null);
  const [raffleData, setRaffleData] = useState({
    order: [], // [{ prizeId, type: 'general'|'bucket' }, ...]; quantity expands to multiple slots
    winners: {}, // { "0": { winnerFlyerId, winnerName }, ... }
    sentToSponsor: {}, // { "0": true, ... } — sent to sponsor for this slot
  });
  const [raffleHighlightIndex, setRaffleHighlightIndex] = useState(null); // highlight this row (e.g. next to fill)
  const [bucketRegistrations, setBucketRegistrations] = useState({}); // { [prizeId]: { winnerFlyerId, winnerName }[] }
  const bucketRegistrationsPendingRef = useRef({});
  const bucketFlushTimerRef = useRef(null);
  const selectedEventRef = useRef('');
  selectedEventRef.current = selectedEvent || '';
  const [collapsedBucketPrizeIds, setCollapsedBucketPrizeIds] = useState(() => new Set());
  const [openBucketAddPrizeId, setOpenBucketAddPrizeId] = useState(null);
  const [bucketEntrySearch, setBucketEntrySearch] = useState('');
  const [bucketEntryHighlightIdx, setBucketEntryHighlightIdx] = useState(0);
  const bucketAddDropdownRef = useRef(null);
  const [bucketScanModeByPrize, setBucketScanModeByPrize] = useState({}); // { [prizeId]: true }
  const [bucketScanDraftByPrize, setBucketScanDraftByPrize] = useState({});
  const [bucketScanErrorByPrize, setBucketScanErrorByPrize] = useState({});
  const bucketScanInputRefs = useRef({});
  const [bucketScanSkipLeading, setBucketScanSkipLeading] = useState(readBucketScanSkipLeadingFromStorage);
  const [mapSlotAssignments, setMapSlotAssignments] = useState({}); // { Slot_0: [sponsorId], ... }
  const [mapSaving, setMapSaving] = useState(false);
  const [sponsorTableExport, setSponsorTableExport] = useState(null); // { ctx, mode } | null
  const [sponsorTableExportCols, setSponsorTableExportCols] = useState(() => new Set());

  const sponsorEventTimeline = useMemo(
    () => mergeOutreachAndNotesAscending(sponsorOutreachLog, sponsorRowNoteLog, SPONSOR_MERGED_TIMELINE_LIMIT),
    [sponsorOutreachLog, sponsorRowNoteLog]
  );

  const editingSponsorRow = useMemo(
    () => (editingSponsorId ? sponsors.find((s) => s.id === editingSponsorId) || null : null),
    [sponsors, editingSponsorId]
  );

  const fetchLastNoteForSponsor = useCallback(async (sponsorId) => {
    if (!sponsorId) return null;
    const [noteSnap, outreachSnap] = await Promise.all([
      getDocs(
        query(
          collection(firestore, 'sponsors', sponsorId, SPONSOR_ROW_NOTE_SUBCOLLECTION),
          orderBy('createdAt', 'desc'),
          limit(1)
        )
      ),
      getDocs(
        query(
          collection(firestore, 'sponsors', sponsorId, OUTREACH_SUBCOLLECTION),
          orderBy('createdAt', 'desc'),
          limit(1)
        )
      ),
    ]);
    const note = noteSnap.docs[0]?.data() || null;
    const outreach = outreachSnap.docs[0]?.data() || null;
    const noteMs = note?.createdAt?.toMillis?.() || 0;
    const outreachMs = outreach?.createdAt?.toMillis?.() || 0;
    const latest = noteMs >= outreachMs ? note : outreach;
    if (!latest) return null;
    return {
      text: (latest.body || '').trim(),
      by: latest.authorDisplayName || 'Unknown',
      at: latest.createdAt || null,
    };
  }, []);

  const hydrateSponsorsWithLastNote = useCallback(async (rows) => {
    const withLastNote = await Promise.all(
      rows.map(async (row) => ({
        ...row,
        lastNote: await fetchLastNoteForSponsor(row.id),
      }))
    );
    return withLastNote;
  }, [fetchLastNoteForSponsor]);

  const patchSponsorFromServer = useCallback(async (sponsorId) => {
    delete activityTimelineCacheRef.current[sponsorId];
    setActivityTimelineUi((prev) => {
      const next = { ...prev };
      delete next[sponsorId];
      return next;
    });
    const snap = await getDoc(doc(firestore, 'sponsors', sponsorId));
    if (!snap.exists()) return;
    const raw = { id: snap.id, ...snap.data() };
    const orgIds = raw.organizationId ? [raw.organizationId] : [];
    const orgById = await fetchSponsorOrganizationsByIds(orgIds);
    const merged = mergeParticipationWithOrg(raw, orgById);
    const lastNote = await fetchLastNoteForSponsor(sponsorId);
    setSponsors((prev) => prev.map((s) => (s.id === sponsorId ? { ...merged, lastNote } : s)));
  }, [fetchLastNoteForSponsor]);

  const refreshMergedTimelineForSponsor = useCallback(async (sponsorId) => {
    if (!sponsorId) return;
    const [outreach, notes] = await Promise.all([fetchSponsorOutreachLog(sponsorId), fetchSponsorRowNoteLog(sponsorId)]);
    const merged = mergeOutreachAndNotesAscending(outreach, notes, SPONSOR_MERGED_TIMELINE_LIMIT);
    activityTimelineCacheRef.current[sponsorId] = { items: merged };
    setActivityTimelineUi((p) => ({
      ...p,
      [sponsorId]: { loading: false, items: merged, error: null },
    }));
  }, []);

  const submitTableQuickNote = useCallback(
    async (sponsorId, rawText) => {
      const text = (rawText || '').trim();
      if (!text) return;
      if (!auth.currentUser) {
        setError('Sign in to add notes.');
        return;
      }
      setTableQuickNoteSubmittingId(sponsorId);
      try {
        await addSponsorRowNoteLog(sponsorId, text, auth.currentUser);
        setTableQuickNoteDraft((d) => {
          const next = { ...d };
          delete next[sponsorId];
          return next;
        });
        await patchSponsorFromServer(sponsorId);
        await refreshMergedTimelineForSponsor(sponsorId);
        if (editingSponsorId === sponsorId) {
          const list = await fetchSponsorRowNoteLog(sponsorId);
          setSponsorRowNoteLog(list);
        }
      } catch (err) {
        setError(err.message || 'Could not add note');
      } finally {
        setTableQuickNoteSubmittingId(null);
      }
    },
    [patchSponsorFromServer, refreshMergedTimelineForSponsor, editingSponsorId]
  );

  useEffect(() => {
    if (!contactPopoverId) return;
    const onDocClick = (e) => {
      if (contactPopoverRef.current && !contactPopoverRef.current.contains(e.target)) {
        setContactPopoverId(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [contactPopoverId]);

  useEffect(() => {
    const rawIds = [activityPopoverSponsorId, expandedNoteId].filter(Boolean);
    const uniqueIds = [...new Set(rawIds)];
    if (uniqueIds.length === 0) return undefined;

    const cancelledById = Object.fromEntries(uniqueIds.map((id) => [id, false]));

    uniqueIds.forEach((id) => {
      const cached = activityTimelineCacheRef.current[id];
      if (cached) {
        setActivityTimelineUi((p) => ({
          ...p,
          [id]: { loading: false, items: cached.items, error: null },
        }));
        return;
      }
      setActivityTimelineUi((p) => ({ ...p, [id]: { loading: true, items: [], error: null } }));
      Promise.all([fetchSponsorOutreachLog(id), fetchSponsorRowNoteLog(id)])
        .then(([outreach, notes]) => {
          if (cancelledById[id]) return;
          const merged = mergeOutreachAndNotesAscending(outreach, notes, SPONSOR_MERGED_TIMELINE_LIMIT);
          activityTimelineCacheRef.current[id] = { items: merged };
          setActivityTimelineUi((p) => ({
            ...p,
            [id]: { loading: false, items: merged, error: null },
          }));
        })
        .catch((err) => {
          if (cancelledById[id]) return;
          setActivityTimelineUi((p) => ({
            ...p,
            [id]: { loading: false, items: [], error: err.message || String(err) },
          }));
        });
    });

    return () => {
      uniqueIds.forEach((id) => {
        cancelledById[id] = true;
      });
    };
  }, [activityPopoverSponsorId, expandedNoteId]);

  useEffect(() => {
    if (!activityPopoverSponsorId) return;
    const onDocClick = (e) => {
      if (activityPopoverRef.current && !activityPopoverRef.current.contains(e.target)) {
        setActivityPopoverSponsorId(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [activityPopoverSponsorId]);

  useEffect(() => {
    if (openAddDropdownCatId == null) return;
    const onDocClick = (e) => {
      if (awardAddDropdownRef.current && !awardAddDropdownRef.current.contains(e.target)) {
        setOpenAddDropdownCatId(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [openAddDropdownCatId]);

  useLayoutEffect(() => {
    if (openPrizeStatusMenuId == null || !prizeStatusAnchorRef.current) return;
    syncPrizeStatusMenuPosition();
  }, [openPrizeStatusMenuId, syncPrizeStatusMenuPosition]);

  useEffect(() => {
    if (openPrizeStatusMenuId == null) return;
    const onDocClick = (e) => {
      if (
        prizeStatusAnchorRef.current?.contains(e.target) ||
        prizeStatusDropdownRef.current?.contains(e.target)
      ) {
        return;
      }
      closePrizeStatusMenu();
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [openPrizeStatusMenuId, closePrizeStatusMenu]);

  useEffect(() => {
    if (openPrizeStatusMenuId == null) return;
    const onScrollOrResize = () => syncPrizeStatusMenuPosition();
    window.addEventListener('resize', onScrollOrResize);
    document.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      document.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [openPrizeStatusMenuId, syncPrizeStatusMenuPosition]);

  useEffect(() => {
    if (!sponsorToast) return undefined;
    const t = setTimeout(() => setSponsorToast(''), 3000);
    return () => clearTimeout(t);
  }, [sponsorToast]);

  useEffect(() => {
    if (openWonByKey == null) return;
    const onDocClick = (e) => {
      if (wonByDropdownRef.current && !wonByDropdownRef.current.contains(e.target)) {
        setOpenWonByKey(null);
        setWonBySearch('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [openWonByKey]);

  useEffect(() => {
    if (openBucketAddPrizeId == null) return;
    const onDocClick = (e) => {
      if (bucketAddDropdownRef.current && !bucketAddDropdownRef.current.contains(e.target)) {
        setOpenBucketAddPrizeId(null);
        setBucketEntrySearch('');
        setBucketEntryHighlightIdx(0);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [openBucketAddPrizeId]);

  useEffect(() => {
    let cancelled = false;
    async function fetchEvents() {
      try {
        const [snap, activeId] = await Promise.all([
          getDocs(collection(firestore, 'events')),
          getActiveEventId(),
        ]);
        if (cancelled) return;
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setEvents(list);
        if (list.length > 0) {
          setSelectedEvent((prev) =>
            prev && list.some((e) => e.id === prev)
              ? prev
              : resolveAdminDefaultEventId({ eventIdParam: '', activeEventId: activeId, events: list })
          );
        }
      } catch (err) {
        if (!cancelled) setError('Failed to fetch events: ' + err.message);
      }
    }
    fetchEvents();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedEvent) {
      if (bucketFlushTimerRef.current) {
        clearTimeout(bucketFlushTimerRef.current);
        bucketFlushTimerRef.current = null;
      }
      setSponsors([]);
      setPrizes([]);
      setFreshlings([]);
      setMentors([]);
      setAllFlyers([]);
      setTeams([]);
      setDzMappings({});
      setMemoryStatsOverrides(null);
      setAwardData({ categories: [], otherItems: [], assignments: {} });
      setAwardCategoryWarnings({});
      setRaffleData({ order: [], winners: {} });
      setBucketRegistrations({});
      bucketRegistrationsPendingRef.current = {};
      setCollapsedBucketPrizeIds(new Set());
      setOpenBucketAddPrizeId(null);
      setBucketEntrySearch('');
      setLoading(false);
      return;
    }
    let cancelled = false;
    const eventIdForThisRun = selectedEvent;
    if (bucketFlushTimerRef.current) {
      clearTimeout(bucketFlushTimerRef.current);
      bucketFlushTimerRef.current = null;
    }
    setLoading(true);
    async function fetch() {
      try {
        const [
          sponsorsSnap,
          prizesSnap,
          flyersSnap,
          assignmentsSnap,
          mapSlotsSnap,
          teamsSnap,
          dzSnap,
          memSnap,
        ] = await Promise.all([
          getDocs(query(collection(firestore, 'sponsors'), where('eventId', '==', selectedEvent))),
          getDocs(query(collection(firestore, 'prizes'), where('eventId', '==', selectedEvent))),
          getDocs(query(collection(firestore, 'flyers'), where('eventId', '==', selectedEvent))),
          getDoc(doc(firestore, 'prizeAssignments', selectedEvent)).catch(() => null),
          getDoc(doc(firestore, 'sponsorMapSlots', selectedEvent)).catch(() => null),
          getDocs(query(collection(firestore, 'teams'), where('eventId', '==', selectedEvent))),
          getDoc(doc(firestore, '_config', 'dzMappings')).catch(() => null),
          getDoc(doc(firestore, 'eventMemoryMeta', selectedEvent)).catch(() => null),
        ]);
        if (cancelled) return;

        const prizeAssignDoc = (assignmentsSnap && assignmentsSnap.exists()) ? assignmentsSnap.data() : {};

        const assignments = prizeAssignDoc.assignments || {};
        const migrated = {};
        Object.keys(assignments).forEach((k) => {
          const v = assignments[k];
          if (v && Array.isArray(v.items)) {
            const block = { ...v };
            if (block.winnerFlyerId == null && block.winnerName == null && block.items.length > 0) {
              const firstWithWinner = block.items.find((it) => it.winnerFlyerId || it.winnerName);
              if (firstWithWinner) {
                block.winnerFlyerId = firstWithWinner.winnerFlyerId || null;
                block.winnerName = firstWithWinner.winnerName || '';
              }
            }
            migrated[k] = block;
          } else if (v && (v.type === 'prize' || v.type === 'custom')) {
            migrated[k] = { items: [v] };
          } else {
            migrated[k] = { items: [] };
          }
        });

        let loadedRaffleData;
        const hasNewRaffle = Array.isArray(prizeAssignDoc.raffleOrder) && prizeAssignDoc.raffleOrder.length > 0;
        if (hasNewRaffle) {
          loadedRaffleData = {
            order: prizeAssignDoc.raffleOrder,
            winners: prizeAssignDoc.raffleWinners && typeof prizeAssignDoc.raffleWinners === 'object' ? prizeAssignDoc.raffleWinners : {},
            sentToSponsor: prizeAssignDoc.raffleSentToSponsor && typeof prizeAssignDoc.raffleSentToSponsor === 'object' ? prizeAssignDoc.raffleSentToSponsor : {},
          };
        } else {
          const gOrder = Array.isArray(prizeAssignDoc.raffleGeneralOrder) ? prizeAssignDoc.raffleGeneralOrder : [];
          const bOrder = Array.isArray(prizeAssignDoc.raffleBucketOrder) ? prizeAssignDoc.raffleBucketOrder : [];
          const gWinners = prizeAssignDoc.raffleGeneralWinners && typeof prizeAssignDoc.raffleGeneralWinners === 'object' ? prizeAssignDoc.raffleGeneralWinners : {};
          const bWinners = prizeAssignDoc.raffleBucketWinners && typeof prizeAssignDoc.raffleBucketWinners === 'object' ? prizeAssignDoc.raffleBucketWinners : {};
          const migratedOrder = [
            ...gOrder.map((prizeId) => ({ prizeId, type: 'general' })),
            ...bOrder.map((prizeId) => ({ prizeId, type: 'bucket' })),
          ];
          const migratedWinners = {};
          gOrder.forEach((prizeId, i) => { if (gWinners[prizeId]) migratedWinners[String(i)] = gWinners[prizeId]; });
          bOrder.forEach((prizeId, i) => { if (bWinners[prizeId]) migratedWinners[String(gOrder.length + i)] = bWinners[prizeId]; });
          loadedRaffleData = { order: migratedOrder, winners: migratedWinners, sentToSponsor: {} };
        }

        const mapData = (mapSlotsSnap && mapSlotsSnap.exists()) ? mapSlotsSnap.data() : {};

        const rawSponsors = sponsorsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const orgIds = [...new Set(rawSponsors.map((s) => s.organizationId).filter(Boolean))];
        const orgById = await fetchSponsorOrganizationsByIds(orgIds);
        const mergedSponsors = rawSponsors.map((s) => mergeParticipationWithOrg(s, orgById));
        setSponsors(await hydrateSponsorsWithLastNote(mergedSponsors));
        const loadedPrizes = prizesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const migratedPrizeStatus = loadedPrizes
          .map((p) => ({ id: p.id, normalized: normalizePrizeStatus(p.status), raw: p.status }))
          .filter((row) => row.normalized !== row.raw);
        if (migratedPrizeStatus.length > 0) {
          const batch = writeBatch(firestore);
          migratedPrizeStatus.forEach((row) => {
            batch.update(doc(firestore, 'prizes', row.id), {
              status: row.normalized,
              updatedAt: serverTimestamp(),
            });
          });
          await batch.commit();
        }
        setPrizes(loadedPrizes.map((p) => ({ ...p, status: normalizePrizeStatus(p.status) })));
        const flyers = flyersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setAllFlyers(flyers);
        setFreshlings(flyers.filter((f) => (f.type || '') === 'freshling' && !f.cancelled));
        setMentors(flyers.filter((f) => (f.type || '') === 'mentor' && !f.cancelled));
        setTeams(teamsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setDzMappings(dzSnap && dzSnap.exists() ? dzSnap.data().mappings || {} : {});
        setMemoryStatsOverrides(memSnap && memSnap.exists() ? memSnap.data().statsOverrides || null : null);
        setAwardData({
          categories: migrateAwardCategories(prizeAssignDoc.categories),
          otherItems: Array.isArray(prizeAssignDoc.otherItems) ? prizeAssignDoc.otherItems : [],
          assignments: migrated,
        });
        setAwardCategoryWarnings({});
        setRaffleData(loadedRaffleData);
        const rawBr = prizeAssignDoc.bucketRegistrations;
        let loadedBucketReg = {};
        if (rawBr && typeof rawBr === 'object' && !Array.isArray(rawBr)) {
          Object.keys(rawBr).forEach((k) => {
            const v = rawBr[k];
            if (Array.isArray(v)) {
              const entries = v.map((x) => normalizeBucketEntry(x)).filter(Boolean);
              if (entries.length) loadedBucketReg[k] = entries;
            }
          });
        }
        setBucketRegistrations(loadedBucketReg);
        bucketRegistrationsPendingRef.current = loadedBucketReg;
        setMapSlotAssignments(mapData.slotAssignments && typeof mapData.slotAssignments === 'object' ? mapData.slotAssignments : {});
      } catch (err) {
        if (!cancelled) setError('Failed to fetch: ' + err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetch();
    return () => {
      cancelled = true;
      const hadPendingDebouncedSave = !!bucketFlushTimerRef.current;
      if (bucketFlushTimerRef.current) {
        clearTimeout(bucketFlushTimerRef.current);
        bucketFlushTimerRef.current = null;
      }
      if (eventIdForThisRun && hadPendingDebouncedSave) {
        void setDoc(
          doc(firestore, 'prizeAssignments', eventIdForThisRun),
          {
            eventId: eventIdForThisRun,
            bucketRegistrations: bucketRegistrationsPendingRef.current,
          },
          { merge: true }
        ).catch(() => {});
      }
    };
  }, [selectedEvent, hydrateSponsorsWithLastNote]);

  useEffect(() => {
    if (!compareEventId) {
      setCompareMap(new Map());
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const prev = await fetchSponsorParticipationsForEvent(compareEventId);
        const orgIds = [...new Set(prev.map((s) => s.organizationId).filter(Boolean))];
        const orgById = await fetchSponsorOrganizationsByIds(orgIds);
        const merged = prev.map((s) => mergeParticipationWithOrg(s, orgById));
        const m = new Map();
        merged.forEach((s) => {
          if (s.organizationId) {
            m.set(s.organizationId, { status: s.status || '', answered: !!s.answered });
          }
        });
        if (!cancelled) setCompareMap(m);
      } catch (_) {
        if (!cancelled) setCompareMap(new Map());
      }
    })();
    return () => { cancelled = true; };
  }, [compareEventId]);

  useEffect(() => {
    if (activeTab !== 'directory' || !selectedEvent) {
      setDirectoryOrgsRaw([]);
      setDirectoryLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setDirectoryLoading(true);
      try {
        const orgs = await fetchAllSponsorOrganizations();
        if (cancelled) return;
        setDirectoryOrgsRaw(Array.isArray(orgs) ? orgs : []);
      } catch (err) {
        if (!cancelled) setError('Directory load failed: ' + (err.message || err));
      } finally {
        if (!cancelled) setDirectoryLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, selectedEvent, directoryRefreshToken]);

  useEffect(() => {
    if (!sponsorModalOpen || !editingSponsorId) {
      setSponsorRowNoteLog([]);
      setSponsorOutreachLog([]);
      setSponsorEventTimelineLoading(false);
      setNewSponsorRowNoteText('');
      setSponsorRowNoteSubmitting(false);
      setEditingSponsorRowNoteId(null);
      setEditingSponsorRowNoteText('');
      setOutreachChannel('email');
      setOutreachOutcome('');
      setOutreachBody('');
      setOutreachNextFollowUpDate('');
      setDirectoryNameMatches([]);
      setDirectoryNameDebounced('');
      return;
    }
    let cancelled = false;
    setSponsorEventTimelineLoading(true);
    Promise.all([fetchSponsorRowNoteLog(editingSponsorId), fetchSponsorOutreachLog(editingSponsorId)])
      .then(([notes, outreach]) => {
        if (!cancelled) {
          setSponsorRowNoteLog(notes);
          setSponsorOutreachLog(outreach);
          setSponsorEventTimelineLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setSponsorEventTimelineLoading(false);
          setError('Could not load sponsor timeline: ' + (err.message || err));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [sponsorModalOpen, editingSponsorId]);

  useEffect(() => {
    if (!sponsorModalOpen) return undefined;
    const timer = setTimeout(() => {
      setDirectoryNameDebounced((sponsorForm.name || '').trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [sponsorModalOpen, sponsorForm.name]);

  useEffect(() => {
    if (!sponsorModalOpen) return;
    const needle = directoryNameDebounced.toLowerCase();
    if (!needle) {
      setDirectoryNameMatches([]);
      return;
    }
    const matches = directoryEntries.filter((d) => (d.name || '').toLowerCase().includes(needle));
    setDirectoryNameMatches(matches.slice(0, 8));
  }, [sponsorModalOpen, directoryEntries, directoryNameDebounced]);

  useEffect(() => {
    if (!sponsorModalOpen) setSponsorModalInvalidNames(new Set());
  }, [sponsorModalOpen]);

  const openDirectorySeedModal = () => {
    setDirectorySeedError('');
    setDirectorySeedSummary('');
    const fallback = events.find((e) => e.id !== selectedEvent)?.id || '';
    setDirectorySeedEventId(fallback);
    setDirectorySeedModalOpen(true);
  };

  const closeDirectoryDetailsModal = useCallback(() => {
    setDirectoryDetailsOrg(null);
    setDirectoryDetailsLoading(false);
    setDirectoryOrgNotes([]);
    setDirectoryOrgNotesLoading(false);
    setNewDirectoryOrgNoteText('');
    setDirectoryOrgNoteSubmitting(false);
    setEditingDirectoryOrgNoteId(null);
    setEditingDirectoryOrgNoteText('');
  }, []);

  useModalEscapeKey(sponsorModalOpen, closeSponsorModal);
  useModalEscapeKey(prizeModalOpen, () => setPrizeModalOpen(false));
  useModalEscapeKey(!!detailsSponsor, () => setDetailsSponsor(null));
  useModalEscapeKey(directorySeedModalOpen, () => setDirectorySeedModalOpen(false), { enabled: !directorySeedBusy });
  useModalEscapeKey(!!prizeHistoryModal, () => setPrizeHistoryModal(null), { enabled: !prizeHistoryLoading });
  useModalEscapeKey(Boolean(directoryDetailsLoading || directoryDetailsOrg), closeDirectoryDetailsModal);

  const openDirectoryOrgDetails = async (orgId) => {
    if (!orgId) return;
    setDirectoryDetailsOrg(null);
    setDirectoryDetailsLoading(true);
    setDirectoryOrgNotes([]);
    setDirectoryOrgNotesLoading(true);
    setEditingDirectoryOrgNoteId(null);
    setEditingDirectoryOrgNoteText('');
    setNewDirectoryOrgNoteText('');
    try {
      const snap = await getDoc(doc(firestore, 'sponsorOrganizations', orgId));
      if (!snap.exists()) {
        setError('Organization record was not found.');
        return;
      }
      const notes = await fetchOrgNoteLog(orgId);
      setDirectoryDetailsOrg({ ...snap.data(), id: snap.id });
      setDirectoryOrgNotes(notes);
    } catch (err) {
      setError('Could not load organization: ' + (err.message || err));
    } finally {
      setDirectoryDetailsLoading(false);
      setDirectoryOrgNotesLoading(false);
    }
  };

  const handleAddDirectoryOrgNote = async () => {
    if (!directoryDetailsOrg?.id) return;
    if (!auth.currentUser) {
      setError('Sign in to add notes.');
      return;
    }
    const text = newDirectoryOrgNoteText.trim();
    if (!text) return;
    setDirectoryOrgNoteSubmitting(true);
    try {
      await addOrgNoteLog(directoryDetailsOrg.id, text, auth.currentUser);
      setNewDirectoryOrgNoteText('');
      const list = await fetchOrgNoteLog(directoryDetailsOrg.id);
      setDirectoryOrgNotes(list);
    } catch (err) {
      setError(err.message || 'Could not add note');
    } finally {
      setDirectoryOrgNoteSubmitting(false);
    }
  };

  const handleSaveDirectoryOrgNoteEdit = async () => {
    if (!directoryDetailsOrg?.id || !editingDirectoryOrgNoteId) return;
    if (!auth.currentUser) {
      setError('Sign in to edit notes.');
      return;
    }
    const text = editingDirectoryOrgNoteText.trim();
    if (!text) return;
    setDirectoryOrgNoteSubmitting(true);
    try {
      await updateOrgNoteLog(directoryDetailsOrg.id, editingDirectoryOrgNoteId, text, auth.currentUser);
      setEditingDirectoryOrgNoteId(null);
      setEditingDirectoryOrgNoteText('');
      const list = await fetchOrgNoteLog(directoryDetailsOrg.id);
      setDirectoryOrgNotes(list);
    } catch (err) {
      setError(err.message || 'Could not update note');
    } finally {
      setDirectoryOrgNoteSubmitting(false);
    }
  };

  const handleAddSponsorRowNote = async () => {
    if (!editingSponsorId) return;
    if (!auth.currentUser) {
      setError('Sign in to add notes.');
      return;
    }
    const text = newSponsorRowNoteText.trim();
    if (!text) return;
    setSponsorRowNoteSubmitting(true);
    try {
      await addSponsorRowNoteLog(editingSponsorId, text, auth.currentUser);
      setNewSponsorRowNoteText('');
      const list = await fetchSponsorRowNoteLog(editingSponsorId);
      setSponsorRowNoteLog(list);
      await patchSponsorFromServer(editingSponsorId);
    } catch (err) {
      setError(err.message || 'Could not add note');
    } finally {
      setSponsorRowNoteSubmitting(false);
    }
  };

  const handleSaveSponsorRowNoteEdit = async () => {
    if (!editingSponsorId || !editingSponsorRowNoteId) return;
    if (!auth.currentUser) {
      setError('Sign in to edit notes.');
      return;
    }
    const text = editingSponsorRowNoteText.trim();
    if (!text) return;
    setSponsorRowNoteSubmitting(true);
    try {
      await updateSponsorRowNoteLog(editingSponsorId, editingSponsorRowNoteId, text, auth.currentUser);
      setEditingSponsorRowNoteId(null);
      setEditingSponsorRowNoteText('');
      const list = await fetchSponsorRowNoteLog(editingSponsorId);
      setSponsorRowNoteLog(list);
      await patchSponsorFromServer(editingSponsorId);
    } catch (err) {
      setError(err.message || 'Could not update note');
    } finally {
      setSponsorRowNoteSubmitting(false);
    }
  };

  const handleAddSponsorOutreach = async () => {
    if (!editingSponsorId) return;
    if (!auth.currentUser) {
      setError('Sign in to log outreach.');
      return;
    }
    setOutreachSubmitting(true);
    try {
      const selectedOutcome = outreachOutcome;
      await addSponsorOutreachLog(
        editingSponsorId,
        {
          body: outreachBody,
          channel: outreachChannel,
          outcome: selectedOutcome,
          nextFollowUpDate: outreachNextFollowUpDate || null,
        },
        auth.currentUser
      );
      if (selectedOutcome === 'yes_committed') {
        await updateSponsorPipelineStage(editingSponsorId, 'confirmed', {
          skipNudgeCheck: true,
          successToast: 'Pipeline updated to Confirmed',
          note: 'Auto-updated from outreach outcome: yes_committed',
        });
      } else if (selectedOutcome === 'no') {
        const markDeclined = window.confirm('Mark as Declined?');
        if (markDeclined) {
          await updateSponsorPipelineStage(editingSponsorId, 'declined', {
            skipNudgeCheck: true,
            note: 'Auto-updated from outreach outcome: no',
          });
        }
      }
      setOutreachBody('');
      setOutreachNextFollowUpDate('');
      const list = await fetchSponsorOutreachLog(editingSponsorId);
      setSponsorOutreachLog(list);
      await patchSponsorFromServer(editingSponsorId);
    } catch (err) {
      setError(err.message || 'Could not log outreach');
    } finally {
      setOutreachSubmitting(false);
    }
  };

  const handleClearSponsorNextFollowUp = async () => {
    if (!editingSponsorId) return;
    setSponsorModalMetaBusy(true);
    try {
      await updateDoc(doc(firestore, 'sponsors', editingSponsorId), {
        nextFollowUpAt: deleteField(),
      });
      await patchSponsorFromServer(editingSponsorId);
    } catch (err) {
      setError(err.message || 'Could not clear follow-up date');
    } finally {
      setSponsorModalMetaBusy(false);
    }
  };

  const runDirectorySeedFromPastEvent = async () => {
    const sourceEventId = (directorySeedEventId || '').trim();
    if (!sourceEventId) {
      setDirectorySeedError('Select an event to import from.');
      return;
    }
    setDirectorySeedBusy(true);
    setDirectorySeedError('');
    try {
      const sponsorQ = query(collection(firestore, 'sponsors'), where('eventId', '==', sourceEventId));
      const sponsorSnap = await getDocs(sponsorQ);
      let newOrgs = 0;
      let reusedOrgs = 0;
      let processed = 0;
      const sponsorIdToOrgId = new Map();

      for (const d of sponsorSnap.docs) {
        const p = d.data();
        const nameTrimmed = (p.name || '').trim();
        if (!nameTrimmed) continue;
        processed += 1;

        const contacts = Array.isArray(p.contacts) && p.contacts.length > 0
          ? p.contacts
          : (p.contactEmail || p.primaryContactName
            ? [{ name: p.primaryContactName || '', email: p.contactEmail || '', phone: '', note: '', active: true }]
            : []);

        const orgFields = buildOrganizationFieldsFromForm({
          name: nameTrimmed,
          website: p.website,
          logo: p.logo,
          contacts,
          mainContactIndex: p.mainContactIndex != null ? Number(p.mainContactIndex) : 0,
          invoiceContactIndex: p.invoiceContactIndex != null ? Number(p.invoiceContactIndex) : 0,
          notes: null,
        });

        let organizationId = (p.organizationId || '').trim() || null;
        if (organizationId) {
          const orgSnap = await getDoc(doc(firestore, 'sponsorOrganizations', organizationId));
          if (!orgSnap.exists()) {
            organizationId = null;
          }
        }

        if (organizationId) {
          await updateDoc(doc(firestore, 'sponsorOrganizations', organizationId), {
            ...orgFields,
            updatedAt: serverTimestamp(),
          });
          reusedOrgs += 1;
        } else {
          const found = await findSponsorOrganizationByNormalizedName(orgFields.normalizedName);
          if (found) {
            organizationId = found.id;
            await updateDoc(doc(firestore, 'sponsorOrganizations', organizationId), {
              ...orgFields,
              updatedAt: serverTimestamp(),
            });
            reusedOrgs += 1;
          } else {
            const orgRef = await addDoc(collection(firestore, 'sponsorOrganizations'), {
              ...orgFields,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            });
            organizationId = orgRef.id;
            newOrgs += 1;
          }
        }

        const denorm = denormalizedParticipationFromOrg(orgFields);
        await updateDoc(doc(firestore, 'sponsors', d.id), {
          organizationId,
          name: denorm.name,
          website: denorm.website,
          logo: denorm.logo,
          contactEmail: denorm.contactEmail,
          primaryContactName: denorm.primaryContactName,
          contacts: denorm.contacts,
          mainContactIndex: denorm.mainContactIndex,
          invoiceContactIndex: denorm.invoiceContactIndex,
        });
        sponsorIdToOrgId.set(d.id, organizationId);
      }

      const prizeQ = query(collection(firestore, 'prizes'), where('eventId', '==', sourceEventId));
      const prizeSnap = await getDocs(prizeQ);
      let prizesTagged = 0;
      for (const pd of prizeSnap.docs) {
        const pr = pd.data();
        const sid = (pr.sponsorId || '').trim();
        if (!sid) continue;
        const oid = sponsorIdToOrgId.get(sid);
        if (!oid || pr.organizationId === oid) continue;
        await updateDoc(pd.ref, { organizationId: oid });
        prizesTagged += 1;
      }

      setDirectorySeedModalOpen(false);
      setDirectorySeedEventId('');
      setDirectoryRefreshToken((t) => t + 1);
      setDirectorySeedSummary(
        `Directory import complete: ${newOrgs} new organization record(s), ${reusedOrgs} sponsor row(s) tied to existing or updated orgs, ${processed} participation row(s) on the source event updated, ${prizesTagged} prize(s) tagged with organizationId.`
      );
      setTimeout(() => setDirectorySeedSummary(''), 12000);
    } catch (err) {
      setDirectorySeedError(err.message || String(err));
    } finally {
      setDirectorySeedBusy(false);
    }
  };

  const ensureDirectoryEntriesLoaded = useCallback(async () => {
    if (directoryEntries.length > 0) return;
    setDirectoryEntriesLoading(true);
    try {
      const orgs = await fetchAllSponsorOrganizations();
      setDirectoryEntries(orgs);
    } catch (err) {
      setError('Directory load failed: ' + (err.message || err));
    } finally {
      setDirectoryEntriesLoading(false);
    }
  }, [directoryEntries.length]);

  const applyDirectoryEntryToSponsorForm = useCallback((entry) => {
    if (!entry) return;
    const contacts = Array.isArray(entry.contacts)
      ? entry.contacts.map((c) => ({
        name: c.name || '',
        email: c.email || '',
        phone: c.phone || '',
        note: c.note || '',
        active: c.active !== false,
      }))
      : [];
    setSponsorForm((f) => ({
      ...f,
      directoryId: entry.id,
      name: entry.name || f.name,
      website: entry.website || '',
      logo: entry.logo || '',
      contacts,
      mainContactIndex: entry.mainContactIndex != null ? Number(entry.mainContactIndex) : 0,
      invoiceContactIndex: entry.invoiceContactIndex != null ? Number(entry.invoiceContactIndex) : 0,
      organizationNotes: (entry.notes != null ? String(entry.notes) : '') || '',
    }));
    setSponsorLogoMode('directory');
    setSponsorLogoUploadedThisEvent(false);
  }, []);

  const openAddSponsor = () => {
    setEditingSponsorId(null);
    setSponsorForm({
      directoryId: '',
      type: 'exhibit',
      name: '',
      website: '',
      contactEmail: '',
      logo: '',
      displayOrder: sponsors.length,
      contacts: [],
      invoiceContactIndex: 0,
      mainContactIndex: 0,
      organizationNotes: '',
    });
    setSponsorLogoMode('directory');
    setSponsorLogoUploadedThisEvent(false);
    void ensureDirectoryEntriesLoaded();
    setSponsorModalOpen(true);
  };

  const openSponsorEmailComposer = () => {
    if (!selectedEvent) return;
    const base = sponsors.filter((s) => {
      if ((s.type || 'none') === 'none') return false;
      return collectSponsorContactEmails(s).length > 0;
    });
    let filtered = base;
    if (emailSponsorType === 'interactive') {
      filtered = base.filter((s) => (s.type || 'none') === 'interactive');
    } else if (emailSponsorType === 'exhibit') {
      filtered = base.filter((s) => (s.type || 'none') === 'exhibit');
    } else if (emailSponsorType === 'interactive_exhibit') {
      filtered = base.filter((s) => {
        const t = (s.type || 'none');
        return t === 'interactive' || t === 'exhibit';
      });
    } else if (emailSponsorType === 'prize') {
      filtered = base.filter((s) => (s.type || 'none') === 'prize');
    }
    if (filtered.length === 0) {
      window.alert('No sponsors with email for that type.');
      return;
    }
    const emails = Array.from(
      new Set(
        filtered
          .flatMap((s) => collectSponsorContactEmails(s))
          .filter(Boolean)
      )
    );
    if (emails.length === 0) {
      window.alert('No sponsor emails found.');
      return;
    }
    const activeEvent = events.find((ev) => ev.id === selectedEvent) || null;
    openEmailComposer({
      to: '',
      bcc: emails.join(', '),
      template: 'custom',
      initialSubject: '',
      initialBody: '',
      context: {
        event: activeEvent,
        sponsorIds: filtered.map((s) => s.id),
        sponsorTypeFilter: emailSponsorType,
        scope: 'sponsors',
      },
    });
  };

  const openEditSponsor = (s) => {
    setEditingSponsorId(s.id);
    const contacts = Array.isArray(s.contacts) && s.contacts.length > 0
      ? s.contacts.map((c) => ({
        name: c.name || '',
        email: c.email || '',
        phone: c.phone || '',
        note: c.note || '',
        active: c.active !== false,
      }))
      : (s.contactEmail || s.primaryContactName ? [{ name: s.primaryContactName || '', email: s.contactEmail || '', phone: '', note: '', active: true }] : []);
    setSponsorForm({
      directoryId: s.directoryId || s.organizationId || '',
      type: s.type || 'exhibit',
      name: s.name || '',
      website: s.website || '',
      contactEmail: s.contactEmail || '',
      logo: s.logo || '',
      displayOrder: s.displayOrder ?? 0,
      contacts,
      invoiceContactIndex: s.invoiceContactIndex != null ? Number(s.invoiceContactIndex) : 0,
      mainContactIndex: s.mainContactIndex != null ? Number(s.mainContactIndex) : 0,
      organizationNotes: (s._directoryNotes != null ? String(s._directoryNotes) : '') || '',
    });
    setSponsorLogoMode('directory');
    setSponsorLogoUploadedThisEvent(false);
    void ensureDirectoryEntriesLoaded();
    setSponsorModalOpen(true);
  };

  const updateFormContact = (index, field, value) => {
    setSponsorForm((f) => ({
      ...f,
      contacts: f.contacts.map((c, i) => (i === index ? { ...c, [field]: value } : c)),
    }));
  };

  const addFormContact = () => {
    setSponsorForm((f) => ({ ...f, contacts: [...f.contacts, { name: '', email: '', phone: '', note: '', active: true }] }));
  };

  const removeFormContact = (index) => {
    setSponsorForm((f) => {
      const next = f.contacts.filter((_, i) => i !== index);
      const nextInv = Math.min(f.invoiceContactIndex, Math.max(0, next.length - 1));
      const nextMain = Math.min(f.mainContactIndex, Math.max(0, next.length - 1));
      return { ...f, contacts: next, invoiceContactIndex: nextInv, mainContactIndex: nextMain };
    });
  };

  const handleLogoFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const directoryId = (sponsorForm.directoryId || '').trim();
    if (!directoryId) {
      setError('Link this sponsor to a Directory entry before uploading a logo.');
      e.target.value = '';
      return;
    }
    const ext = (file.name.split('.').pop() || 'png').toLowerCase();
    const storageRef = ref(storage, `sponsors/${directoryId}/logo.${ext}`);
    try {
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setSponsorForm((f) => ({ ...f, logo: url }));
      setSponsorLogoMode('event_upload');
      setSponsorLogoUploadedThisEvent(true);
      await updateDoc(doc(firestore, 'sponsorOrganizations', directoryId), {
        logo: url,
        updatedAt: serverTimestamp(),
      });
      setError('');
    } catch (err) {
      setError('Logo upload failed: ' + (err.message || err));
    }
  };

  const saveSponsor = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (!form.checkValidity()) {
      setSponsorModalInvalidNames(collectInvalidNamedControlNames(form));
      const inv = form.querySelector(':invalid');
      if (inv && typeof inv.focus === 'function') inv.focus();
      return;
    }
    setSponsorModalInvalidNames(new Set());
    if (!selectedEvent) return;
    const orgFields = buildOrganizationFieldsFromForm({
      name: sponsorForm.name,
      website: sponsorForm.website,
      logo: sponsorForm.logo,
      contacts: sponsorForm.contacts,
      mainContactIndex: sponsorForm.mainContactIndex,
      invoiceContactIndex: sponsorForm.invoiceContactIndex,
      notes: sponsorForm.organizationNotes,
    });
    if (!orgFields.name) {
      setSponsorModalInvalidNames(new Set(['sponsorName']));
      return;
    }
    const exactDirectoryMatch = directoryEntries.find(
      (d) => (d.name || '').trim().toLowerCase() === orgFields.name.toLowerCase()
    );
    if (!editingSponsorId && exactDirectoryMatch && !sponsorForm.directoryId) {
      setError('This name matches a Directory entry. Select it from "Did you mean…?" before saving.');
      return;
    }
    try {
      let organizationId;
      const existingRow = editingSponsorId ? sponsors.find((x) => x.id === editingSponsorId) : null;
      if (editingSponsorId) {
        organizationId = (sponsorForm.directoryId || existingRow?.directoryId || existingRow?.organizationId || '').trim();
        if (!organizationId) {
          const orgRef = await addDoc(collection(firestore, 'sponsorOrganizations'), {
            ...orgFields,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
          organizationId = orgRef.id;
        } else {
          await updateDoc(doc(firestore, 'sponsorOrganizations', organizationId), {
            ...orgFields,
            updatedAt: serverTimestamp(),
          });
        }
      } else if ((sponsorForm.directoryId || '').trim()) {
        organizationId = sponsorForm.directoryId.trim();
        await updateDoc(doc(firestore, 'sponsorOrganizations', organizationId), {
          ...orgFields,
          updatedAt: serverTimestamp(),
        });
      } else {
        const fuzzyHasMatches = directoryNameMatches.length > 0;
        if (!fuzzyHasMatches) {
          // No likely match: create directory record concurrently with sponsor save.
          const orgRef = await addDoc(collection(firestore, 'sponsorOrganizations'), {
            ...orgFields,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
          organizationId = orgRef.id;
        } else {
          const found = await findSponsorOrganizationByNormalizedName(orgFields.normalizedName);
          if (found) {
            organizationId = found.id;
            await updateDoc(doc(firestore, 'sponsorOrganizations', organizationId), {
              ...orgFields,
              updatedAt: serverTimestamp(),
            });
          }
        }
      }
      if (!organizationId) {
        const orgRef = await addDoc(collection(firestore, 'sponsorOrganizations'), {
          ...orgFields,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        organizationId = orgRef.id;
      }

      const denorm = denormalizedParticipationFromOrg({ ...orgFields, mainContactIndex: orgFields.mainContactIndex, invoiceContactIndex: orgFields.invoiceContactIndex });
      const participationPayload = {
        eventId: selectedEvent,
        directoryId: organizationId,
        organizationId,
        type: sponsorForm.type,
        name: denorm.name,
        website: denorm.website,
        logo: null,
        contactEmail: denorm.contactEmail,
        primaryContactName: denorm.primaryContactName,
        contacts: denorm.contacts,
        mainContactIndex: denorm.mainContactIndex,
        invoiceContactIndex: denorm.invoiceContactIndex,
      };
      if (editingSponsorId) {
        participationPayload.displayOrder = existingRow?.displayOrder ?? 0;
        await updateDoc(doc(firestore, 'sponsors', editingSponsorId), participationPayload);
      } else {
        participationPayload.displayOrder = Number(sponsorForm.displayOrder) || 0;
        await addDoc(collection(firestore, 'sponsors'), participationPayload);
      }
      closeSponsorModal();
      const q = query(collection(firestore, 'sponsors'), where('eventId', '==', selectedEvent));
      const snap = await getDocs(q);
      const raw = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const orgIds = [...new Set(raw.map((s) => s.organizationId).filter(Boolean))];
      const orgById = await fetchSponsorOrganizationsByIds(orgIds);
      setSponsors(await hydrateSponsorsWithLastNote(raw.map((s) => mergeParticipationWithOrg(s, orgById))));
    } catch (err) {
      setError('Save failed: ' + err.message);
    }
  };

  const deleteSponsor = async (id, opts = {}) => {
    const skipConfirm = !!opts.skipConfirm;
    if (!skipConfirm && !window.confirm('Delete this sponsor? Prizes linked to them will remain but show "Unknown sponsor".')) {
      return false;
    }
    try {
      await deleteDoc(doc(firestore, 'sponsors', id));
      delete activityTimelineCacheRef.current[id];
      setActivityTimelineUi((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setActivityPopoverSponsorId((open) => (open === id ? null : open));
      setExpandedNoteId((open) => (open === id ? null : open));
      setTableQuickNoteDraft((d) => {
        if (!(id in d)) return d;
        const next = { ...d };
        delete next[id];
        return next;
      });
      setSponsors((prev) => prev.filter((s) => s.id !== id));
      setPrizes((prev) => prev.map((p) => (p.sponsorId === id ? { ...p, sponsorId: null } : p)));
      return true;
    } catch (err) {
      setError('Delete failed: ' + err.message);
      return false;
    }
  };

  const updateSponsorType = async (sponsorId, newType) => {
    try {
      await updateDoc(doc(firestore, 'sponsors', sponsorId), { type: newType });
      setSponsors((prev) => prev.map((s) => (s.id === sponsorId ? { ...s, type: newType } : s)));
    } catch (err) {
      setError('Update failed: ' + err.message);
    }
  };

  const updateSponsorPipelineStage = async (sponsorId, value, options = {}) => {
    const pipeline = (value || '').trim();
    const sponsor = sponsors.find((s) => s.id === sponsorId);
    const prevPipeline = sponsor ? derivePipelineFromLegacy(sponsor) : '';
    const billing = sponsor ? deriveBillingFromLegacy(sponsor) : '';
    const legacy = legacyBoolPayloadFromStages(pipeline, billing);
    const iso = new Date().toISOString().slice(0, 10);
    const payload = {
      ...legacy,
      sponsorPipelineStage: pipeline || deleteField(),
      sponsorPipelineUpdatedAt: pipeline ? iso : deleteField(),
    };
    try {
      await updateDoc(doc(firestore, 'sponsors', sponsorId), payload);
      const touch = prevPipeline !== pipeline ? snapshotAuthor(auth.currentUser) : null;
      const touchAt = touch ? Timestamp.now() : null;
      if (prevPipeline !== pipeline) {
        await addSponsorPipelineChangeLog(
          sponsorId,
          {
            from: prevPipeline,
            to: pipeline,
            note: options.note || '',
          },
          auth.currentUser
        );
      }
      setSponsors((prev) =>
        prev.map((row) =>
          row.id === sponsorId
            ? {
                ...row,
                sponsorPipelineStage: pipeline || null,
                sponsorPipelineUpdatedAt: pipeline ? iso : null,
                ...legacy,
                ...(touch
                  ? {
                      lastTouchByDisplayName: touch.authorDisplayName,
                      lastTouchByUid: touch.authorUid,
                      lastTouchAt: touchAt,
                    }
                  : {}),
              }
            : row
        )
      );
      if (options.successToast) {
        setSponsorToast(options.successToast);
      }
      if (!options.skipNudgeCheck && sponsor && prevPipeline !== pipeline) {
        const lastOutreachMs = sponsor.lastOutreachAt?.toMillis?.() || 0;
        const cutoffMs = Date.now() - (60 * 60 * 1000);
        const shouldNudge = !lastOutreachMs || lastOutreachMs < cutoffMs;
        setPipelineNudgeBySponsor((prev) => ({ ...prev, [sponsorId]: shouldNudge }));
      }
    } catch (err) {
      setError('Update failed: ' + err.message);
    }
  };

  const handleSavePipelineNudgeNote = async (sponsorId) => {
    const note = (pipelineNudgeNoteBySponsor[sponsorId] || '').trim();
    if (!note) return;
    const sponsor = sponsors.find((s) => s.id === sponsorId);
    try {
      await addSponsorPipelineChangeLog(
        sponsorId,
        {
          from: derivePipelineFromLegacy(sponsor),
          to: derivePipelineFromLegacy(sponsor),
          note,
        },
        auth.currentUser
      );
      setPipelineNudgeBySponsor((prev) => ({ ...prev, [sponsorId]: false }));
      setPipelineNudgeNoteBySponsor((prev) => ({ ...prev, [sponsorId]: '' }));
      if (editingSponsorId === sponsorId) {
        const list = await fetchSponsorOutreachLog(sponsorId);
        setSponsorOutreachLog(list);
      }
      await patchSponsorFromServer(sponsorId);
    } catch (err) {
      setError(err.message || 'Could not save pipeline note');
    }
  };

  const updateSponsorBillingStage = async (sponsorId, value) => {
    const billing = (value || '').trim();
    const sponsor = sponsors.find((s) => s.id === sponsorId);
    const pipeline = sponsor ? derivePipelineFromLegacy(sponsor) : '';
    const legacy = legacyBoolPayloadFromStages(pipeline, billing);
    const iso = new Date().toISOString().slice(0, 10);
    const payload = {
      ...legacy,
      sponsorBillingStage: billing || deleteField(),
      sponsorBillingUpdatedAt: billing ? iso : deleteField(),
    };
    try {
      await updateDoc(doc(firestore, 'sponsors', sponsorId), payload);
      setSponsors((prev) =>
        prev.map((row) =>
          row.id === sponsorId
            ? {
                ...row,
                sponsorBillingStage: billing || null,
                sponsorBillingUpdatedAt: billing ? iso : null,
                ...legacy,
              }
            : row
        )
      );
    } catch (err) {
      setError('Update failed: ' + err.message);
    }
  };

  const openAddPrize = () => {
    const eligibleSponsors = sponsors.filter((s) => (s.type || 'none') !== 'none');
    setEditingPrizeId(null);
    setPrizeForm({
      sponsorId: eligibleSponsors.length ? eligibleSponsors[0].id : '',
      name: '',
      description: '',
      giveawayType: 'general_raffle',
      status: 'assumed',
      quantity: 1,
      imageUrl: '',
      expirationDate: '',
      unlisted: false,
    });
    setPrizeModalOpen(true);
    setTimeout(() => prizeFormFirstInputRef.current?.focus(), 100);
  };

  const openEditPrize = (p) => {
    setEditingPrizeId(p.id);
    const exp = p.expirationDate;
    const expStr = exp && (exp.toDate ? exp.toDate().toISOString().slice(0, 10) : (typeof exp === 'string' ? exp.slice(0, 10) : ''));
    setPrizeForm({
      sponsorId: p.sponsorId || '',
      name: p.name || '',
      description: p.description || '',
      giveawayType: p.giveawayType || 'general_raffle',
      status: normalizePrizeStatus(p.status),
      quantity: p.quantity != null ? Number(p.quantity) : 1,
      imageUrl: p.imageUrl || '',
      winnerPhotoUrls: Array.isArray(p.winnerPhotoUrls) ? [...p.winnerPhotoUrls] : (p.winnerPhotoUrl ? [p.winnerPhotoUrl] : []),
      expirationDate: expStr || '',
      unlisted: !!p.unlisted,
    });
    setPrizeModalOpen(true);
  };

  const resetPrizeFormForAddAnother = () => {
    const eligibleSponsors = sponsors.filter((s) => (s.type || 'none') !== 'none');
    const keepSponsorId = (prizeForm.sponsorId || '').trim();
    const sameSponsorStillEligible = eligibleSponsors.some((s) => s.id === keepSponsorId);
    setEditingPrizeId(null);
    setPrizeForm({
      sponsorId: sameSponsorStillEligible ? keepSponsorId : (eligibleSponsors.length ? eligibleSponsors[0].id : ''),
      name: '',
      description: '',
      giveawayType: 'general_raffle',
      status: 'assumed',
      quantity: 1,
      imageUrl: '',
      winnerPhotoUrls: [],
      expirationDate: '',
      unlisted: false,
    });
    setTimeout(() => prizeFormFirstInputRef.current?.focus(), 50);
  };

  const savePrize = async (e) => {
    e.preventDefault();
    const addAnother = saveAndAddAnotherRef.current;
    saveAndAddAnotherRef.current = false;
    if (!selectedEvent) return;
    const qty = Math.max(1, Math.floor(Number(prizeForm.quantity)) || 1);
    const sponsorRow = sponsors.find((x) => x.id === (prizeForm.sponsorId || '').trim());
    const organizationId = sponsorRow?.organizationId || null;
    const payload = {
      eventId: selectedEvent,
      sponsorId: (prizeForm.sponsorId || '').trim() || null,
      organizationId,
      name: (prizeForm.name || '').trim() || null,
      description: (prizeForm.description || '').trim() || null,
      giveawayType: prizeForm.giveawayType || 'general_raffle',
      status: normalizePrizeStatus(prizeForm.status),
      quantity: qty,
      imageUrl: (prizeForm.imageUrl || '').trim() || null,
      winnerPhotoUrls: (prizeForm.winnerPhotoUrls || []).filter((u) => (u || '').trim()),
      winnerPhotoUrl: null,
      expirationDate: (prizeForm.expirationDate || '').trim() || null,
      unlisted: !!prizeForm.unlisted,
    };
    try {
      if (editingPrizeId) {
        const updatePayload = { ...payload, updatedAt: serverTimestamp() };
        await updateDoc(doc(firestore, 'prizes', editingPrizeId), updatePayload);
        setPrizes((prev) => prev.map((p) => (p.id === editingPrizeId ? { ...p, ...payload, updatedAt: new Date() } : p)));
      } else {
        const createPayload = { ...payload, createdAt: serverTimestamp(), updatedAt: serverTimestamp() };
        const ref = await addDoc(collection(firestore, 'prizes'), createPayload);
        const now = new Date();
        setPrizes((prev) => [...prev, { id: ref.id, ...payload, createdAt: now, updatedAt: now }]);
      }
      if (addAnother) {
        resetPrizeFormForAddAnother();
      } else {
        setPrizeModalOpen(false);
      }
    } catch (err) {
      setError('Save failed: ' + err.message);
    }
  };

  const handlePrizeImageFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedEvent) return;
    const ext = (file.name.split('.').pop() || 'png').toLowerCase().replace(/[^a-z0-9]/gi, '') || 'png';
    const safeName = (file.name.slice(0, 30) || 'image').replace(/[^a-zA-Z0-9._-]/g, '-');
    const path = `prizes/${selectedEvent}/${Date.now()}-${safeName}.${ext}`;
    const storageRef = ref(storage, path);
    setPrizeImageUploading(true);
    setError('');
    try {
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setPrizeForm((f) => ({ ...f, imageUrl: url }));
      if (prizeImageInputRef.current) prizeImageInputRef.current.value = '';
    } catch (err) {
      setError('Image upload failed: ' + (err.message || err));
    } finally {
      setPrizeImageUploading(false);
    }
  };

  const handlePrizeWinnerPhotoFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedEvent) return;
    const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/gi, '') || 'jpg';
    const safeName = (file.name.slice(0, 30) || 'winner').replace(/[^a-zA-Z0-9._-]/g, '-');
    const path = `prizes/${selectedEvent}/winners/${Date.now()}-${safeName}.${ext}`;
    const storageRef = ref(storage, path);
    setPrizeWinnerPhotoUploading(true);
    setError('');
    try {
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setPrizeForm((f) => ({ ...f, winnerPhotoUrls: [...(f.winnerPhotoUrls || []), url] }));
      if (prizeWinnerPhotoInputRef.current) prizeWinnerPhotoInputRef.current.value = '';
    } catch (err) {
      setError('Winner photo upload failed: ' + (err.message || err));
    } finally {
      setPrizeWinnerPhotoUploading(false);
    }
  };

  const updatePrizeStatus = async (prizeId, status) => {
    const normalizedStatus = normalizePrizeStatus(status);
    try {
      await updateDoc(doc(firestore, 'prizes', prizeId), { status: normalizedStatus, updatedAt: serverTimestamp() });
      setPrizes((prev) => prev.map((p) => (p.id === prizeId ? { ...p, status: normalizedStatus, updatedAt: new Date() } : p)));
    } catch (err) {
      setError('Update failed: ' + err.message);
    }
  };

  const updatePrizeUnlisted = async (prizeId, unlisted) => {
    try {
      await updateDoc(doc(firestore, 'prizes', prizeId), { unlisted: !!unlisted, updatedAt: serverTimestamp() });
      setPrizes((prev) => prev.map((p) => (p.id === prizeId ? { ...p, unlisted: !!unlisted, updatedAt: new Date() } : p)));
    } catch (err) {
      setError('Update failed: ' + err.message);
    }
  };

  const deletePrize = async (id) => {
    if (!window.confirm('Delete this prize?')) return;
    try {
      await deleteDoc(doc(firestore, 'prizes', id));
      setPrizes((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      setError('Delete failed: ' + err.message);
    }
  };

  const getSponsorName = (sponsorId) => sponsors.find((s) => s.id === sponsorId)?.name || '—';

  const prizeTypePrizes = React.useMemo(
    () => prizes.filter((p) => (p.giveawayType || '') === 'prize'),
    [prizes]
  );

  const saveAwardData = useCallback(async (payload) => {
    if (!selectedEvent) return;
    setAwardData(payload);
    try {
      await setDoc(
        doc(firestore, 'prizeAssignments', selectedEvent),
        { eventId: selectedEvent, ...payload },
        { merge: true }
      );
    } catch (err) {
      setError('Save failed: ' + err.message);
    }
  }, [selectedEvent]);

  const saveRaffleData = useCallback(
    async (updates) => {
      if (!selectedEvent) return;
      try {
        await setDoc(
          doc(firestore, 'prizeAssignments', selectedEvent),
          updates,
          { merge: true }
        );
      } catch (err) {
        setError('Raffle save failed: ' + err.message);
      }
    },
    [selectedEvent]
  );

  const scheduleBucketPersist = useCallback(() => {
    if (bucketFlushTimerRef.current) clearTimeout(bucketFlushTimerRef.current);
    bucketFlushTimerRef.current = setTimeout(() => {
      bucketFlushTimerRef.current = null;
      const ev = selectedEventRef.current;
      if (!ev) return;
      void (async () => {
        try {
          await setDoc(
            doc(firestore, 'prizeAssignments', ev),
            { eventId: ev, bucketRegistrations: bucketRegistrationsPendingRef.current },
            { merge: true }
          );
        } catch (err) {
          setError('Bucket entries save failed: ' + err.message);
        }
      })();
    }, 400);
  }, []);

  const saveMapSlotAssignments = useCallback(
    async (nextAssignments) => {
      if (!selectedEvent) return;
      setMapSlotAssignments(nextAssignments);
      setMapSaving(true);
      try {
        await setDoc(
          doc(firestore, 'sponsorMapSlots', selectedEvent),
          { eventId: selectedEvent, slotAssignments: nextAssignments },
          { merge: true }
        );
      } catch (err) {
        setError('Map save failed: ' + err.message);
      } finally {
        setMapSaving(false);
      }
    },
    [selectedEvent]
  );

  const updateCategoryLabel = useCallback((catId, name, subtitle = '') => {
    const next = {
      ...awardData,
      categories: awardData.categories.map((c) => (c.id === catId ? { ...c, name, subtitle } : c)),
    };
    saveAwardData(next);
  }, [awardData, saveAwardData]);

  const setCategoryLabelDraft = useCallback((catId, name, subtitle = '') => {
    setAwardData((prev) => ({
      ...prev,
      categories: prev.categories.map((c) => (c.id === catId ? { ...c, name, subtitle } : c)),
    }));
  }, []);

  const validateCategorySubtitles = useCallback((categories) => {
    const warnings = {};
    const byName = new Map();
    categories.forEach((cat) => {
      const key = categoryName(cat).toLowerCase();
      if (!key) return;
      if (!byName.has(key)) byName.set(key, []);
      byName.get(key).push(cat);
    });
    byName.forEach((dupes) => {
      if (dupes.length < 2) return;
      const subtitleSeen = new Set();
      dupes.forEach((cat) => {
        const subtitleKey = (cat.subtitle || '').trim().toLowerCase();
        if (!subtitleKey || subtitleSeen.has(subtitleKey)) {
          warnings[cat.id] = 'Add a subtitle to distinguish from duplicate';
        } else {
          subtitleSeen.add(subtitleKey);
        }
      });
    });
    return warnings;
  }, []);

  const commitCategoryLabel = useCallback((catId, name, subtitle, fallbackCategory) => {
    const normalizedName = normalizeCategoryNameKeepingConventions((name || '').trim() || categoryName(fallbackCategory));
    const normalizedSubtitle = normalizeCategoryNameKeepingConventions((subtitle || '').trim());
    const nextCategories = awardData.categories.map((c) => (
      c.id === catId ? { ...c, name: normalizedName, subtitle: normalizedSubtitle } : c
    ));
    const warnings = validateCategorySubtitles(nextCategories);
    setAwardCategoryWarnings(warnings);
    if (warnings[catId]) return;
    updateCategoryLabel(catId, normalizedName, normalizedSubtitle);
  }, [awardData.categories, updateCategoryLabel, validateCategorySubtitles]);

  const addAwardCategory = () => {
    const id = `award-${Date.now()}`;
    const next = {
      ...awardData,
      categories: [...awardData.categories, { id, name: 'New Category', subtitle: '' }],
    };
    setAwardCategoryWarnings(validateCategorySubtitles(next.categories));
    saveAwardData(next);
  };

  const removeAwardCategory = (catId) => {
    const next = {
      ...awardData,
      categories: awardData.categories.filter((c) => c.id !== catId),
      assignments: { ...awardData.assignments },
    };
    delete next.assignments[catId];
    setAwardCategoryWarnings(validateCategorySubtitles(next.categories));
    saveAwardData(next);
  };

  const addAwardOtherItem = (label) => {
    const trimmed = (label || '').trim();
    if (!trimmed) return;
    if (awardData.otherItems.includes(trimmed)) return;
    saveAwardData({ ...awardData, otherItems: [...awardData.otherItems, trimmed] });
  };

  const removeAwardOtherItem = (index) => {
    const otherItems = awardData.otherItems.filter((_, i) => i !== index);
    saveAwardData({ ...awardData, otherItems });
  };

  const getAssignmentItems = (catId) => {
    const a = awardData.assignments[catId];
    return (a && a.items) ? a.items : [];
  };

  const getCategoryWinner = (catId) => {
    const a = awardData.assignments[catId];
    return a ? { winnerFlyerId: a.winnerFlyerId, winnerName: a.winnerName } : { winnerFlyerId: null, winnerName: '' };
  };

  const getWinnerDisplayLabel = useCallback((winner) => {
    if (!winner || !winner.winnerFlyerId) return winner?.winnerName || '—';
    const freshling = freshlings.find((x) => x.id === winner.winnerFlyerId);
    if (freshling) return getDisplayName(freshling) || freshling.email || '—';
    const mentor = mentors.find((x) => x.id === winner.winnerFlyerId);
    if (mentor) return getDisplayName(mentor) || mentor.email || '—';
    return winner.winnerName || '—';
  }, [freshlings, mentors]);

  const setCategoryWinner = (catId, { winnerFlyerId, winnerName }) => {
    const next = { ...awardData, assignments: { ...awardData.assignments } };
    const current = next.assignments[catId] || { items: [] };
    next.assignments[catId] = { ...current, winnerFlyerId: winnerFlyerId || null, winnerName: winnerName || '' };
    saveAwardData(next);
  };

  const generalRafflePrizes = React.useMemo(
    () => prizes.filter((p) => (p.giveawayType || '') === 'general_raffle'),
    [prizes]
  );
  const bucketRafflePrizes = React.useMemo(
    () => prizes.filter((p) => (p.giveawayType || '') === 'bucket_raffle'),
    [prizes]
  );

  const bucketPrizesSorted = React.useMemo(() => {
    const sponsorLabel = (sponsorId) => sponsors.find((s) => s.id === sponsorId)?.name || '—';
    return [...bucketRafflePrizes].sort((a, b) => {
      const sa = sponsorLabel(a.sponsorId).localeCompare(sponsorLabel(b.sponsorId), undefined, { sensitivity: 'base' });
      if (sa !== 0) return sa;
      const na = (a.name || a.description || '').toString();
      const nb = (b.name || b.description || '').toString();
      return na.localeCompare(nb, undefined, { sensitivity: 'base' });
    });
  }, [bucketRafflePrizes, sponsors]);

  useEffect(() => {
    if (activeTab !== 'bucketEntries' || !selectedEvent) return undefined;
    const onKeyDown = (e) => {
      const mod = e.ctrlKey || e.metaKey;
      if (!mod || !e.altKey) return;
      if (String(e.key || '').toLowerCase() !== 'v') return;
      if (document.querySelector('.fm-modal-overlay')) return;
      const t = e.target;
      if (t && typeof t.closest === 'function') {
        if (t.closest('.sponsor-management-bucket-add-dropdown')) return;
      }
      e.preventDefault();
      const firstId = bucketPrizesSorted[0]?.id;
      if (!firstId) return;
      setBucketScanModeByPrize((prev) => ({ ...prev, [firstId]: true }));
      setBucketScanErrorByPrize((prev) => ({ ...prev, [firstId]: '' }));
      setTimeout(() => {
        bucketScanInputRefs.current[firstId]?.focus();
        bucketScanInputRefs.current[firstId]?.select?.();
      }, 0);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [activeTab, selectedEvent, bucketPrizesSorted]);

  const addBucketFreshling = (prizeId, f) => {
    const entry = {
      winnerFlyerId: f.id,
      winnerName: getDisplayName(f) || String(f.email || '').trim() || '',
    };
    setBucketRegistrations((prev) => {
      const existing = prev[prizeId] || [];
      const next = { ...prev, [prizeId]: [...existing, entry] };
      bucketRegistrationsPendingRef.current = next;
      scheduleBucketPersist();
      return next;
    });
    setBucketEntrySearch('');
    setBucketEntryHighlightIdx(0);
  };

  const addBucketEntryFromScan = (prizeId, rawScan) => {
    const skip = Math.max(0, Math.min(500, Math.floor(Number(bucketScanSkipLeading)) || 0));
    let trimmed = String(rawScan || '').trim().replace(/\r|\n/g, '');
    trimmed = trimmed.slice(skip);
    const parsedId = parseRaffleTicketBarcodeScan(trimmed);
    if (!parsedId) {
      setBucketScanErrorByPrize((prev) => ({ ...prev, [prizeId]: 'Unrecognized scan' }));
      return;
    }
    const scanPool = [...freshlings, ...mentors];
    const idSet = new Set(scanPool.map((x) => x.id));
    const flyerId = resolveFlyerIdFromNoisyScan(parsedId, idSet, 6) || parsedId;
    const f = scanPool.find((x) => x.id === flyerId);
    if (!f) {
      setBucketScanErrorByPrize((prev) => ({
        ...prev,
        [prizeId]:
          'No freshling or mentor matches this code. Check event, or use “ignore first N chars” if the scanner adds a prefix.',
      }));
      return;
    }
    setBucketScanErrorByPrize((prev) => ({ ...prev, [prizeId]: '' }));
    addBucketFreshling(prizeId, f);
    requestAnimationFrame(() => {
      const el = bucketScanInputRefs.current[prizeId];
      if (el) {
        el.focus();
        el.select();
      }
    });
  };

  const removeBucketRegistrant = (prizeId, index) => {
    setBucketRegistrations((prev) => {
      const list = [...(prev[prizeId] || [])];
      list.splice(index, 1);
      const next = { ...prev };
      if (list.length === 0) delete next[prizeId];
      else next[prizeId] = list;
      bucketRegistrationsPendingRef.current = next;
      scheduleBucketPersist();
      return next;
    });
  };

  const clearBucketRegistrants = (prizeId) => {
    if (!window.confirm('Remove all names entered for this bucket prize?')) return;
    setBucketRegistrations((prev) => {
      const next = { ...prev };
      delete next[prizeId];
      bucketRegistrationsPendingRef.current = next;
      scheduleBucketPersist();
      return next;
    });
  };

  const toggleBucketPrizeCollapsed = (prizeId) => {
    setCollapsedBucketPrizeIds((prev) => {
      const next = new Set(prev);
      if (next.has(prizeId)) next.delete(prizeId);
      else next.add(prizeId);
      return next;
    });
    if (openBucketAddPrizeId === prizeId) {
      setOpenBucketAddPrizeId(null);
      setBucketEntrySearch('');
      setBucketEntryHighlightIdx(0);
    }
  };

  const pickBucketHighlightedFreshling = (prizeId, pickerOptions) => {
    if (!Array.isArray(pickerOptions) || pickerOptions.length === 0) return false;
    const start = Math.max(0, bucketEntryHighlightIdx);
    for (let i = start; i < pickerOptions.length; i += 1) {
      const option = pickerOptions[i];
      if (option) {
        addBucketFreshling(prizeId, option.freshling);
        return true;
      }
    }
    return false;
  };

  const raffleOrdered = React.useMemo(() => {
    const generalSlots = generalRafflePrizes.flatMap((p) =>
      Array(Math.max(1, p.quantity || 1)).fill(null).map(() => ({ prizeId: p.id, type: 'general' }))
    );
    const bucketSlots = bucketRafflePrizes.flatMap((p) =>
      Array(Math.max(1, p.quantity || 1)).fill(null).map(() => ({ prizeId: p.id, type: 'bucket' }))
    );
    if (!raffleData.order || raffleData.order.length === 0) {
      return [...generalSlots, ...bucketSlots];
    }
    const result = [...raffleData.order];
    const countInOrder = (prizeId, type) => result.filter((e) => e.prizeId === prizeId && e.type === type).length;
    generalRafflePrizes.forEach((p) => {
      const need = Math.max(1, p.quantity || 1);
      const have = countInOrder(p.id, 'general');
      for (let i = have; i < need; i++) result.push({ prizeId: p.id, type: 'general' });
    });
    bucketRafflePrizes.forEach((p) => {
      const need = Math.max(1, p.quantity || 1);
      const have = countInOrder(p.id, 'bucket');
      for (let i = have; i < need; i++) result.push({ prizeId: p.id, type: 'bucket' });
    });
    return result;
  }, [raffleData.order, generalRafflePrizes, bucketRafflePrizes]);

  const getRafflePrize = (entry) => {
    const list = entry.type === 'general' ? generalRafflePrizes : bucketRafflePrizes;
    return list.find((p) => p.id === entry.prizeId);
  };

  const getRaffleWinner = (index) => {
    const w = raffleData.winners[String(index)];
    return w ? { winnerFlyerId: w.winnerFlyerId, winnerName: w.winnerName } : { winnerFlyerId: null, winnerName: '' };
  };

  const setRaffleWinner = (index, { winnerFlyerId, winnerName }) => {
    const key = String(index);
    const next = { ...raffleData.winners, [key]: winnerFlyerId || winnerName ? { winnerFlyerId: winnerFlyerId || null, winnerName: winnerName || '' } : null };
    if (!next[key]) delete next[key];
    setRaffleData((prev) => ({ ...prev, winners: next }));
    saveRaffleData({ raffleWinners: next });
    setOpenWonByKey(null);
    setWonBySearch('');
    setRaffleHighlightIndex(index + 1 < raffleOrdered.length ? index + 1 : null);
  };

  const setRaffleSentToSponsor = (index, value) => {
    const key = String(index);
    const next = { ...(raffleData.sentToSponsor || {}), [key]: !!value };
    if (!next[key]) delete next[key];
    setRaffleData((prev) => ({ ...prev, sentToSponsor: next }));
    saveRaffleData({ raffleSentToSponsor: next });
  };

  const openPrizeTabSponsorEmails = () => {
    if (!selectedEvent) return;

    const sponsorIdsWithWin = new Set();

    awardData.categories.forEach((cat) => {
      const a = awardData.assignments[cat.id];
      if (!a || !Array.isArray(a.items)) return;
      const winnerLabel = getWinnerDisplayLabel(getCategoryWinner(cat.id));
      if (winnerLabel === '—') return;
      a.items.forEach((it) => {
        if (it.type !== 'prize' || !it.prizeId) return;
        const p = prizeTypePrizes.find((x) => x.id === it.prizeId);
        if (p && p.sponsorId) sponsorIdsWithWin.add(p.sponsorId);
      });
    });

    raffleOrdered.forEach((entry, idx) => {
      const prize = getRafflePrize(entry);
      if (!prize || !prize.sponsorId) return;
      const rw = getRaffleWinner(idx);
      if (getWinnerDisplayLabel(rw) === '—') return;
      sponsorIdsWithWin.add(prize.sponsorId);
    });

    if (sponsorIdsWithWin.size === 0) {
      window.alert('No award or raffle winners recorded yet. Use Award prizes or Raffles to set “Won by” first.');
      return;
    }

    const sponsorFollowUpGlobal = buildSponsorFollowUpGlobalMergeFields({
      flyers: allFlyers.length ? allFlyers : [...freshlings, ...mentors],
      teams,
      dzMappings,
      statsOverrides: memoryStatsOverrides,
    });

    const sponsorMergeByEmail = {};
    const canonicalByLower = new Map();

    sponsorIdsWithWin.forEach((sponsorId) => {
      const sponsor = sponsors.find((s) => s.id === sponsorId);
      if (!sponsor) return;
      const emails = collectSponsorContactEmails(sponsor);
      if (!emails.length) return;
      const sponsorName = (sponsor.name || '').trim() || 'Sponsor';
      const sponsorContactNames = formatHumanNameList(
        Array.isArray(sponsor.contacts)
          ? sponsor.contacts
              .map((c) => {
                const n = c && c.name ? String(c.name).trim() : '';
                return n ? firstNameOnly(n) : '';
              })
              .filter(Boolean)
          : []
      ) || sponsorName;
      const sponsorCompanyPossessive = possessive(sponsorName);
      const typeEntry = SPONSOR_TYPES.find((t) => t.value === (sponsor.type || 'none'));
      const sponsorType =
        typeEntry && typeEntry.value !== 'none' ? typeEntry.label.toLowerCase() : 'sponsor';
      const sponsorTypeArticle = /^[aeiou]/i.test(sponsorType) ? 'an' : 'a';
      const followUpPack = buildSponsorFollowUpDetailsHtml({
        sponsorId,
        awardData,
        raffleOrdered,
        getRafflePrize,
        getRaffleWinner,
        getCategoryWinner,
        getWinnerDisplayLabel,
        prizes,
        prizeTypePrizes,
        bucketRegistrations,
        freshlings,
        mentors,
      });
      const sponsorFollowUpDetailsHtml = followUpPack.html || '';
      const sponsorAttachmentUrls = Array.isArray(followUpPack.attachmentUrls) ? followUpPack.attachmentUrls : [];
      emails.forEach((em) => {
        const low = em.toLowerCase();
        canonicalByLower.set(low, em);
        sponsorMergeByEmail[low] = {
          sponsorName,
          sponsorContactNames,
          sponsorCompanyPossessive,
          sponsorType,
          sponsorTypeArticle,
          sponsorFollowUpDetailsHtml,
          sponsorAttachmentUrls,
          sponsorPrizeSummaryHtml: sponsorFollowUpDetailsHtml,
        };
      });
    });

    if (canonicalByLower.size === 0) {
      window.alert('No sponsor contact emails found for sponsors with recorded wins. Add emails on each sponsor record.');
      return;
    }

    const activeEvent = events.find((ev) => ev.id === selectedEvent) || null;
    const initialBody =
      '<p style="margin:0 0 12px 0;font-size:14px;line-height:1.5;">Hi {{SponsorContactNames}},</p>\n' +
      '<p style="margin:0 0 12px 0;font-size:14px;line-height:1.5;">Thank you for being part of <strong>{{eventTitle}}</strong>. I&rsquo;m incredibly proud of how this year&rsquo;s event turned out, and {{SponsorCompanyPossessive}} support as {{sponsorTypeArticle}} {{sponsorType}} sponsor truly helped make it such a success.</p>\n' +
      '<p style="margin:0 0 8px 0;font-size:14px;line-height:1.5;">You can see the 2026 Fresh Meet memory book at <a href="https://perrisfreshmeet.com">perrisfreshmeet.com</a>, with statistics and photos from the event.</p>\n' +
      '{{sponsorEventSnapshotHtml}}\n' +
      '<p style="margin:20px 0 0 0;padding-top:16px;border-top:1px solid #e2e8f0;line-height:0;font-size:0;">&nbsp;</p>\n' +
      '{{sponsorFollowUpDetailsHtml}}\n' +
      '<p style="margin:20px 0 0 0;padding-top:16px;border-top:1px solid #e2e8f0;line-height:0;font-size:0;">&nbsp;</p>\n' +
      '<p style="margin:0 0 12px 0;font-size:14px;line-height:1.5;">If you would like to connect with the winner or need anything further, I&rsquo;m happy to help.</p>\n' +
      '<p style="margin:0 0 12px 0;font-size:14px;line-height:1.5;">We would love to have you back next year:<br/><strong>Fresh Meet 2027</strong> &mdash; March 17&ndash;21<br/>More info: <a href="https://perrisfreshmeet.com">perrisfreshmeet.com</a></p>\n' +
      '<p style="margin:0 0 12px 0;font-size:14px;line-height:1.5;">Thank you again for your support.</p>\n' +
      '<p style="margin:0;font-size:14px;line-height:1.5;">Best,<br/>Christy Frikken<br/>Fury Coaching / Fresh Meet</p>';

    openEmailComposer({
      to: '',
      bcc: Array.from(canonicalByLower.values()).join(', '),
      template: 'custom',
      initialSubject: 'Thank you — {{eventTitle}} — sponsor follow-up',
      initialBody,
      context: {
        event: activeEvent,
        scope: 'sponsors',
        defaultSendMode: 'individual',
        sponsorMergeByEmail,
        sponsorFollowUpGlobal,
      },
    });
  };

  const moveRaffleSlot = (index, direction) => {
    const nextIdx = direction === 'up' ? index - 1 : index + 1;
    if (nextIdx < 0 || nextIdx >= raffleOrdered.length) return;
    const order = [...raffleOrdered];
    const winners = { ...raffleData.winners };
    const sentToSponsor = { ...(raffleData.sentToSponsor || {}) };
    const a = String(index);
    const b = String(nextIdx);
    [order[index], order[nextIdx]] = [order[nextIdx], order[index]];
    if (winners[a] || winners[b]) {
      [winners[a], winners[b]] = [winners[b], winners[a]];
      if (!winners[a]) delete winners[a];
      if (!winners[b]) delete winners[b];
    }
    if (sentToSponsor[a] || sentToSponsor[b]) {
      [sentToSponsor[a], sentToSponsor[b]] = [sentToSponsor[b], sentToSponsor[a]];
      if (!sentToSponsor[a]) delete sentToSponsor[a];
      if (!sentToSponsor[b]) delete sentToSponsor[b];
    }
    setRaffleData((prev) => ({ ...prev, order, winners, sentToSponsor }));
    saveRaffleData({ raffleOrder: order, raffleWinners: winners, raffleSentToSponsor: sentToSponsor });
  };

  const raffleOrderedRef = useRef(null);
  useEffect(() => {
    if (!selectedEvent || raffleData.order.length > 0 || raffleOrdered.length === 0) return;
    if (raffleOrderedRef.current === raffleOrdered.length) return;
    raffleOrderedRef.current = raffleOrdered.length;
    setRaffleData((prev) => ({ ...prev, order: raffleOrdered }));
    saveRaffleData({ raffleOrder: raffleOrdered });
  }, [selectedEvent, raffleData.order.length, raffleOrdered.length, raffleOrdered, saveRaffleData]);

  const usedPrizeCount = React.useMemo(() => {
    const count = {};
    prizeTypePrizes.forEach((p) => { count[p.id] = 0; });
    Object.values(awardData.assignments).forEach((a) => {
      (a.items || []).forEach((it) => {
        if (it.type === 'prize' && it.prizeId) count[it.prizeId] = (count[it.prizeId] || 0) + 1;
      });
    });
    return count;
  }, [awardData.assignments, prizeTypePrizes]);

  const usedOtherCount = React.useMemo(() => {
    const count = {};
    Object.values(awardData.assignments).forEach((a) => {
      (a.items || []).forEach((it) => {
        if (it.type === 'other' && it.label) count[it.label] = (count[it.label] || 0) + 1;
      });
    });
    return count;
  }, [awardData.assignments]);

  const isPrizeFullyUsed = (prizeId, excludeCatId, excludeIndex) => {
    const p = prizeTypePrizes.find((x) => x.id === prizeId);
    const qty = Math.max(1, p ? (p.quantity ?? 1) : 1);
    let count = usedPrizeCount[prizeId] || 0;
    if (excludeCatId != null && excludeIndex != null) {
      const items = getAssignmentItems(excludeCatId);
      const it = items[excludeIndex];
      if (it && it.type === 'prize' && it.prizeId === prizeId) count -= 1;
    }
    return count >= qty;
  };

  const toggleAwardAddSelection = (catId, key) => {
    setAwardAddSelection((prev) => {
      const arr = prev[catId] || [];
      const next = arr.includes(key) ? arr.filter((k) => k !== key) : [...arr, key];
      return { ...prev, [catId]: next };
    });
  };

  const addSelectedToCategory = (catId) => {
    const keys = awardAddSelection[catId] || [];
    if (keys.length === 0) return;
    const newItems = keys.map((k) => {
      if (k.startsWith('prize:')) return { type: 'prize', prizeId: k.slice(6) };
      return { type: 'other', label: k.slice(6) }; // 'other:'.length === 6
    });
    setAssignmentItems(catId, getAssignmentItems(catId).concat(newItems));
    setAwardAddSelection((prev) => ({ ...prev, [catId]: [] }));
  };

  const toggleAwardCategoryCollapsed = (catId) => {
    setExpandedAwardCategories((prev) => {
      const next = new Set(prev);
      if (next.has(catId)) next.delete(catId);
      else next.add(catId);
      return next;
    });
  };

  const setAssignmentItems = (catId, items) => {
    const next = { ...awardData, assignments: { ...awardData.assignments } };
    const current = next.assignments[catId] || {};
    next.assignments[catId] = { ...current, items };
    saveAwardData(next);
  };

  const removeAssignmentItem = (catId, index) => {
    const items = getAssignmentItems(catId).filter((_, i) => i !== index);
    setAssignmentItems(catId, items);
  };

  const updateAssignmentItem = (catId, index, patch) => {
    const items = getAssignmentItems(catId).map((it, i) => (i === index ? { ...it, ...patch } : it));
    setAssignmentItems(catId, items);
  };

  const toggleSponsorInclude = async (sponsorId, field) => {
    const sponsor = sponsors.find((s) => s.id === sponsorId);
    if (!sponsor) return;
    const current = sponsor[field] === undefined ? true : !!sponsor[field];
    const next = !current;
    try {
      await updateDoc(doc(firestore, 'sponsors', sponsorId), { [field]: next });
      setSponsors((prev) => prev.map((s) => (s.id === sponsorId ? { ...s, [field]: next } : s)));
    } catch (err) {
      setError('Update failed: ' + err.message);
    }
  };

  const getContactFullName = (s) => {
    const primary = (s.primaryContactName || '').trim();
    if (primary) return primary;
    const contacts = Array.isArray(s.contacts) ? s.contacts : [];
    if (contacts.length === 1 && (contacts[0].name || '').trim()) return contacts[0].name.trim();
    if (contacts.length > 0) return contacts.map((c) => (c.name || '').trim() || c.email).filter(Boolean).join('; ') || '—';
    return (s.contactEmail || '').trim() ? 'Contact' : '—';
  };

  const copyEmail = (email) => {
    if (!email) return;
    copyText(email);
  };

  const compareColCount = compareEventId ? 2 : 0;
  const sponsorTableColSpan =
    6 + (showLogoInclusionColumns ? 4 : 0) + 2 + 2 + compareColCount + 1;

  const addDirectoryOrgToEvent = async (org) => {
    if (!selectedEvent || !org?.id) return;
    const already = sponsors.some((s) => s.organizationId === org.id);
    if (already) {
      window.alert('This organization already has a row for this event.');
      return;
    }
    const denorm = denormalizedParticipationFromOrg(org);
    try {
      await addDoc(collection(firestore, 'sponsors'), {
        ...denorm,
        eventId: selectedEvent,
        directoryId: org.id,
        organizationId: org.id,
        logo: null,
        // Match new-row default from "Add sponsor" so the row appears in the table,
        // prize sponsor pickers, and email tools (type "none" is hidden there by design).
        type: 'exhibit',
        displayOrder: sponsors.length,
      });
      const q = query(collection(firestore, 'sponsors'), where('eventId', '==', selectedEvent));
      const snap = await getDocs(q);
      const raw = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const orgIds = [...new Set(raw.map((row) => row.organizationId).filter(Boolean))];
      const orgById = await fetchSponsorOrganizationsByIds(orgIds);
      setSponsors(await hydrateSponsorsWithLastNote(raw.map((row) => mergeParticipationWithOrg(row, orgById))));
      setActiveTab('sponsors');
    } catch (err) {
      setError('Could not add row: ' + (err.message || err));
    }
  };

  const exactDirectoryNameMatch = useMemo(() => {
    const n = (sponsorForm.name || '').trim().toLowerCase();
    if (!n) return null;
    return directoryEntries.find((d) => (d.name || '').trim().toLowerCase() === n) || null;
  }, [sponsorForm.name, directoryEntries]);

  const isSaveBlockedByDirectoryExactMatch = Boolean(exactDirectoryNameMatch && !(sponsorForm.directoryId || '').trim());

  const bucketSummary = useMemo(() => {
    const totalCount = bucketPrizesSorted.length;
    const filledCount = bucketPrizesSorted.filter((prize) => (bucketRegistrations[prize.id] || []).length > 0).length;
    const totalEntries = bucketPrizesSorted.reduce(
      (sum, prize) => sum + (bucketRegistrations[prize.id] || []).length,
      0
    );
    return { totalCount, filledCount, totalEntries };
  }, [bucketPrizesSorted, bucketRegistrations]);

  const exportBucketEntriesCsv = useCallback((prize, entries) => {
    const csvEscape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = entries.map((entry) => {
      const flyer = allFlyers.find((f) => f.id === entry.winnerFlyerId) || null;
      const label = getWinnerDisplayLabel(entry);
      return [
        label,
        flyer?.email || '',
        flyer?.type || '',
        flyer?.cancelled ? 'Y' : '',
      ];
    });
    const lines = [
      ['Prize', 'Entry Name', 'Email', 'Type', 'Cancelled'],
      ...rows.map((r) => [prize.name || prize.description || prize.id, ...r]),
    ];
    const csv = `${lines.map((line) => line.map(csvEscape).join(',')).join('\n')}\n`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safeTitle = (prize.name || prize.description || prize.id || 'bucket')
      .toString()
      .replace(/[^a-z0-9]+/gi, '-')
      .replace(/^-+|-+$/g, '')
      .toLowerCase();
    a.download = `bucket-entries-${safeTitle || 'prize'}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [allFlyers, getWinnerDisplayLabel]);

  const getLogoProvenanceBadge = useCallback((logoUrl) => {
    const url = String(logoUrl || '');
    if (!url) return '';
    const matched = events.find((ev) => ev.id && ev.id !== selectedEvent && url.includes(ev.id));
    if (!matched) return '';
    const yr = extractYearLabel(matched.title) || extractYearLabel(matched.id);
    return yr ? `↑ ${yr}` : '↑ prior';
  }, [events, selectedEvent]);

  const logoSourceYearLabel = useMemo(() => {
    if (sponsorLogoUploadedThisEvent) return '';
    if (!editingSponsorRow?._directoryUpdatedAt) return '';
    const dt = editingSponsorRow._directoryUpdatedAt;
    const date = dt && typeof dt.toDate === 'function' ? dt.toDate() : new Date(dt);
    if (!date || Number.isNaN(date.getTime())) return '';
    return String(date.getFullYear());
  }, [editingSponsorRow, sponsorLogoUploadedThisEvent]);

  const openPrizeHistoryForSponsor = async (s) => {
    if (!s.organizationId) {
      window.alert('This sponsor row is not linked to an organization yet. Edit and save once, or run the backfill script.');
      return;
    }
    setPrizeHistoryModal({ organizationId: s.organizationId, sponsorName: s.name || '' });
    setPrizeHistoryLoading(true);
    setPrizeHistoryRows([]);
    try {
      const rows = await fetchPrizesForOrganization(s.organizationId);
      const withEvent = rows.map((p) => {
        const ev = events.find((e) => e.id === p.eventId);
        return { ...p, _eventLabel: ev ? (ev.title || ev.id) : (p.eventId || '') };
      });
      withEvent.sort((a, b) => String(b.eventId || '').localeCompare(String(a.eventId || '')));
      setPrizeHistoryRows(withEvent);
    } catch (err) {
      setError('Prize history failed: ' + (err.message || err));
    } finally {
      setPrizeHistoryLoading(false);
    }
  };

  const copyHistoricPrizeToCurrentEvent = async (p) => {
    if (!selectedEvent || !prizeHistoryModal) return;
    const participation = sponsors.find((x) => x.organizationId === prizeHistoryModal.organizationId);
    if (!participation) {
      window.alert('Add this organization to the current event first (Directory tab).');
      return;
    }
    if (p.eventId === selectedEvent) {
      window.alert('This prize is already for the current event.');
      return;
    }
    try {
      await addDoc(collection(firestore, 'prizes'), {
        eventId: selectedEvent,
        sponsorId: participation.id,
        organizationId: prizeHistoryModal.organizationId,
        name: p.name || null,
        description: (p.description || '').trim() || null,
        giveawayType: p.giveawayType || 'general_raffle',
        status: null,
        quantity: p.quantity != null ? Number(p.quantity) : 1,
        imageUrl: (p.imageUrl || '').trim() || null,
        winnerPhotoUrls: [],
        winnerPhotoUrl: null,
        expirationDate: p.expirationDate || null,
        unlisted: !!p.unlisted,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      const pq = query(collection(firestore, 'prizes'), where('eventId', '==', selectedEvent));
      const pSnap = await getDocs(pq);
      setPrizes(pSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      setError('Copy prize failed: ' + (err.message || err));
    }
  };

  const directoryList = useMemo(() => {
    if (activeTab !== 'directory' || !selectedEvent || !directoryOrgsRaw.length) return [];
    return directoryOrgsRaw
      .map((o) => {
        const row = sponsors.find((s) => s.organizationId === o.id);
        const t = row ? (row.type === '' ? 'none' : (row.type || 'none')) : null;
        const inThisEvent = Boolean(row);
        const noneTypeParticipation = inThisEvent && t === 'none';
        return {
          ...o,
          inThisEvent,
          noneTypeParticipation,
        };
      })
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
  }, [activeTab, selectedEvent, directoryOrgsRaw, sponsors]);

  const sortedSponsors = useMemo(
    () =>
      [...sponsors]
        .filter((s) => showNoneType || (s.type || 'none') !== 'none')
        .sort((a, b) => {
          if (sortBy === 'name') {
            return (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase());
          }
          const orderA = SPONSOR_TYPE_SORT_ORDER[a.type] ?? SPONSOR_TYPE_SORT_ORDER.none;
          const orderB = SPONSOR_TYPE_SORT_ORDER[b.type] ?? SPONSOR_TYPE_SORT_ORDER.none;
          if (orderA !== orderB) return orderA - orderB;
          return (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase());
        }),
    [sponsors, showNoneType, sortBy]
  );

  const sortedPrizes = React.useMemo(() => {
    const getSponsorNameForSort = (sponsorId) => (sponsors.find((s) => s.id === sponsorId)?.name || '').toLowerCase();
    const list = [...prizes];
    const dir = prizeSortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      let cmp = 0;
      if (prizeSortKey === 'giveawayType') {
        const labelA = GIVEAWAY_TYPES.find((t) => t.value === a.giveawayType)?.label ?? (a.giveawayType || '');
        const labelB = GIVEAWAY_TYPES.find((t) => t.value === b.giveawayType)?.label ?? (b.giveawayType || '');
        cmp = labelA.localeCompare(labelB);
      } else if (prizeSortKey === 'sponsor') {
        cmp = getSponsorNameForSort(a.sponsorId).localeCompare(getSponsorNameForSort(b.sponsorId));
      }
      return cmp * dir;
    });
    return list;
  }, [prizes, prizeSortKey, prizeSortDir, sponsors]);

  const prizeStatusByValue = useMemo(() => {
    const map = {};
    PRIZE_STATUSES.forEach((s) => {
      map[s.value] = s;
    });
    return map;
  }, []);

  const filteredSortedPrizes = useMemo(() => {
    if (prizeStatusFilter === 'all') return sortedPrizes;
    if (prizeStatusFilter === 'assumed') {
      return sortedPrizes.filter((p) => normalizePrizeStatus(p.status) === 'assumed');
    }
    if (prizeStatusFilter === 'confirmedPlus') {
      return sortedPrizes.filter((p) => PRIZE_STATUS_INDEX[normalizePrizeStatus(p.status)] >= PRIZE_STATUS_INDEX.confirmed);
    }
    if (prizeStatusFilter === 'receivedPlus') {
      return sortedPrizes.filter((p) => PRIZE_STATUS_INDEX[normalizePrizeStatus(p.status)] >= PRIZE_STATUS_INDEX.received);
    }
    if (prizeStatusFilter === 'awarded') {
      return sortedPrizes.filter((p) => normalizePrizeStatus(p.status) === 'awarded');
    }
    return sortedPrizes;
  }, [prizeStatusFilter, sortedPrizes]);

  const cyclePrizeSort = (key) => {
    if (prizeSortKey === key) {
      setPrizeSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setPrizeSortKey(key);
      setPrizeSortDir('asc');
    }
  };

  const formatPrizeTimestamp = (ts) => {
    if (!ts) return null;
    try {
      const date = ts && typeof ts.toDate === 'function' ? ts.toDate() : (ts instanceof Date ? ts : new Date(ts));
      return date.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
    } catch (_) {
      return null;
    }
  };

  const getColumnsForExportCtx = useCallback((ctx) => {
    switch (ctx) {
      case 'sponsors':
        return [...SPONSORS_EXPORT_COLS_STATIC, ...(compareEventId ? SPONSORS_EXPORT_COLS_COMPARE : [])];
      case 'directory':
        return DIRECTORY_EXPORT_COLS;
      case 'prizes':
        return PRIZES_EXPORT_COLS;
      case 'awardPrizes':
        return AWARDS_EXPORT_COLS;
      case 'raffles':
        return RAFFLES_EXPORT_COLS;
      case 'bucketEntries':
        return BUCKET_EXPORT_COLS;
      case 'prizeHistory':
        return PRIZE_HISTORY_EXPORT_COLS;
      default:
        return [];
    }
  }, [compareEventId]);

  const getRowsForSponsorExport = (ctx) => {
      switch (ctx) {
        case 'sponsors':
          return sortedSponsors.map((s) => {
            const prevRow = s.organizationId ? compareMap.get(s.organizationId) : null;
            const t = s.type === '' ? 'none' : (s.type || 'none');
            const typeLabel = SPONSOR_TYPES.find((x) => x.value === t)?.label ?? t;
            const emails = collectSponsorContactEmails(s).join('; ');
            const contact = [getContactFullName(s), emails].filter(Boolean).join(' | ');
            let website = (s.website || '').trim();
            if (website && !website.startsWith('http')) website = `https://${website}`;
            const lastResp = formatSponsorLastResponseCell(s);
            const lastTouch = formatSponsorLastTouchCell(s);
            return {
              type: typeLabel,
              company: s.name || '',
              website,
              contact,
              logo: (s.logo || '').trim(),
              dash: s.showOnDashboard !== false ? 'Y' : '',
              sched: s.showOnSchedule !== false ? 'Y' : '',
              rules: s.showOnRulesPage !== false ? 'Y' : '',
              bonus: s.showOnBonusPuzzles ? 'Y' : '',
              pipeline: SPONSOR_PIPELINE_STAGES.find((x) => x.value === derivePipelineFromLegacy(s))?.label ?? '',
              billing: SPONSOR_BILLING_STAGES.find((x) => x.value === deriveBillingFromLegacy(s))?.label ?? '',
              lastTouchBy: lastTouch === '—' ? '' : lastTouch,
              nextFollowUp: formatFirestoreDateOnly(s.nextFollowUpAt),
              lastResponse: lastResp === '—' ? '' : lastResp,
              priorStatus: prevRow ? (prevRow.status || '—') : '',
              priorAnswered: prevRow ? (prevRow.answered ? 'Y' : '—') : '',
            };
          });
        case 'directory':
          return directoryList.map((o) => {
            const activeC = (Array.isArray(o.contacts) ? o.contacts : []).filter((c) => c && c.active !== false);
            let inThisEventCell = 'No';
            if (o.inThisEvent) {
              inThisEventCell = o.noneTypeParticipation ? 'Yes (type None — hidden on Sponsors until Show type: None)' : 'Yes';
            }
            return {
              company: o.name || '',
              activeContacts: activeC.length ? activeC.map((c) => (c.email || c.name || '—')).join(', ') : '',
              inThisEvent: inThisEventCell,
            };
          });
        case 'prizes':
          return sortedPrizes.map((p) => {
            const exp = p.expirationDate;
            const expStr =
              exp && (exp.toDate ? exp.toDate().toISOString().slice(0, 10) : typeof exp === 'string' ? exp.slice(0, 10) : '');
            return {
              giveaway: GIVEAWAY_TYPES.find((t) => t.value === p.giveawayType)?.label ?? p.giveawayType,
              quantity: p.quantity != null ? String(p.quantity) : '1',
              sponsor: getSponsorName(p.sponsorId),
              nameDesc: p.name || p.description || '',
              status: normalizePrizeStatus(p.status),
              expires: expStr || '',
              unlisted: p.unlisted ? 'Y' : '',
            };
          });
        case 'awardPrizes':
          return awardData.categories.map((cat) => {
            const items = (awardData.assignments[cat.id]?.items || []).map((it) => {
              if (it.type === 'prize' && it.prizeId) {
                const p = prizeTypePrizes.find((x) => x.id === it.prizeId);
                return p ? (p.name || p.description || p.id) : it.prizeId;
              }
              return it.label || '—';
            });
            const winner = awardData.assignments[cat.id];
            let wonBy = '';
            if (winner?.winnerFlyerId) {
              const f = freshlings.find((x) => x.id === winner.winnerFlyerId);
              const m = mentors.find((x) => x.id === winner.winnerFlyerId);
              wonBy = f
                ? (getDisplayName(f) || f.email || '')
                : m
                  ? (getDisplayName(m) || m.email || '')
                  : (winner.winnerName || '');
            } else if (winner?.winnerName) {
              wonBy = winner.winnerName;
            }
            return {
              category: categoryDisplayLabel(cat),
              items: items.length ? items.join(', ') : '—',
              wonBy: wonBy || '—',
            };
          });
        case 'raffles':
          return raffleOrdered
            .map((entry, idx) => {
              const prize = getRafflePrize(entry);
              if (!prize) return null;
              const w = getRaffleWinner(idx);
              const wonByLabel = getWinnerDisplayLabel(w);
              return {
                num: String(idx + 1),
                type: entry.type === 'general' ? 'General' : 'Bucket',
                sponsor: getSponsorName(prize.sponsorId),
                prize: prize.name || prize.description || entry.prizeId,
                wonBy: wonByLabel === '—' ? '' : wonByLabel,
                sentToSponsor: raffleData.sentToSponsor[String(idx)] ? 'Y' : '',
              };
            })
            .filter(Boolean);
        case 'bucketEntries': {
          const out = [];
          bucketPrizesSorted.forEach((prize) => {
            const entries = bucketRegistrations[prize.id] || [];
            const title = prize.name || prize.description || prize.id;
            const sponsor = getSponsorName(prize.sponsorId);
            if (entries.length === 0) {
              out.push({ prize: title, sponsor, entry: '—' });
            } else {
              entries.forEach((e) => {
                const label = getWinnerDisplayLabel(e);
                out.push({ prize: title, sponsor, entry: label === '—' ? '' : label });
              });
            }
          });
          return out;
        }
        case 'prizeHistory':
          return prizeHistoryRows.map((p) => ({
            event: p._eventLabel || p.eventId || '—',
            name: p.name || '—',
            description: (p.description || '').replace(/\s+/g, ' ').trim(),
            giveaway: p.giveawayType || '—',
          }));
        default:
          return [];
      }
  };

  useEffect(() => {
    if (!sponsorTableExport) return;
    const cols = getColumnsForExportCtx(sponsorTableExport.ctx);
    setSponsorTableExportCols(new Set(cols.filter((c) => c.default).map((c) => c.id)));
  }, [sponsorTableExport, getColumnsForExportCtx]);

  const openSponsorTableExport = (ctx, mode) => {
    const rows = getRowsForSponsorExport(ctx);
    if (!rows.length) {
      window.alert('Nothing to export for this view.');
      return;
    }
    setSponsorTableExport({ ctx, mode });
  };

  const resetSponsorTableExportCols = () => {
    if (!sponsorTableExport) return;
    const cols = getColumnsForExportCtx(sponsorTableExport.ctx);
    setSponsorTableExportCols(new Set(cols.filter((c) => c.default).map((c) => c.id)));
  };

  const runSponsorTableExport = (selectedColumnIds) => {
    if (!sponsorTableExport) return;
    const { ctx, mode } = sponsorTableExport;
    const allCols = getColumnsForExportCtx(ctx);
    const colSet =
      Array.isArray(selectedColumnIds) && selectedColumnIds.length > 0
        ? new Set(selectedColumnIds)
        : sponsorTableExportCols;
    const active = allCols.filter((c) => colSet.has(c.id));
    if (!active.length) return;
    const rows = getRowsForSponsorExport(ctx);
    const colMeta = active.map((c) => ({ id: c.id, header: c.label }));
    const rowRecords = rows.map((r) => {
      const o = {};
      active.forEach((c) => {
        o[c.id] = r[c.id] ?? '';
      });
      return o;
    });
    if (mode === 'copy') {
      copyText(buildTsvTable(colMeta, rowRecords));
    } else {
      const ev = events.find((e) => e.id === selectedEvent);
      const title = ev ? ev.title || ev.id : 'Sponsor management';
      const sub = SPONSOR_EXPORT_SUBTITLE[ctx] || 'Table';
      const headerCells = active.map((c) => `<th>${escapeHtmlCell(c.label)}</th>`).join('');
      const rowsHtml = rowRecords
        .map((rec) => {
          const cells = active.map((c) => `<td>${escapeHtmlCell(rec[c.id])}</td>`).join('');
          return `<tr>${cells}</tr>`;
        })
        .join('');
      const bodyHtml = `<main style="padding:8px 12px"><h1>${escapeHtmlCell(title)}</h1><p>${escapeHtmlCell(sub)}</p><p>${rowRecords.length} row(s)</p><table><thead><tr>${headerCells}</tr></thead><tbody>${rowsHtml}</tbody></table></main>`;
      openPrintWindow(`${title} — ${sub}`, bodyHtml, getOperationalTablePrintCss());
    }
    setSponsorTableExport(null);
  };

  const copyFilteredSponsorEmails = () => {
    if (!selectedEvent) return;
    const base = sponsors.filter((s) => {
      if ((s.type || 'none') === 'none') return false;
      return collectSponsorContactEmails(s).length > 0;
    });
    let filtered = base;
    if (emailSponsorType === 'interactive') {
      filtered = base.filter((s) => (s.type || 'none') === 'interactive');
    } else if (emailSponsorType === 'exhibit') {
      filtered = base.filter((s) => (s.type || 'none') === 'exhibit');
    } else if (emailSponsorType === 'interactive_exhibit') {
      filtered = base.filter((s) => {
        const t = (s.type || 'none');
        return t === 'interactive' || t === 'exhibit';
      });
    } else if (emailSponsorType === 'prize') {
      filtered = base.filter((s) => (s.type || 'none') === 'prize');
    }
    const emails = Array.from(
      new Set(filtered.flatMap((s) => collectSponsorContactEmails(s)).filter(Boolean))
    );
    if (emails.length === 0) {
      window.alert('No sponsor emails found for that filter.');
      return;
    }
    copyText(formatEmailList(emails, 'comma'));
  };

  return (
    <div className="sponsor-management">
      <h1>Sponsor Management</h1>
      {directorySeedSummary && <p className="sponsor-management-success">{directorySeedSummary}</p>}
      {sponsorToast ? <p className="sponsor-management-success">{sponsorToast}</p> : null}
      {error && <p className="sponsor-management-error">{error}</p>}

      <div className="sponsor-management-event-row">
        <label htmlFor="sponsor-event">Event:</label>
        <select
          id="sponsor-event"
          value={selectedEvent}
          onChange={(e) => setSelectedEvent(e.target.value)}
        >
          <option value="">Select event</option>
          {events.map((ev) => (
            <option key={ev.id} value={ev.id}>{ev.title || ev.id}</option>
          ))}
        </select>
      </div>

      {!selectedEvent && (
        <p className="sponsor-management-hint">Select an event to manage sponsors and prizes.</p>
      )}

      {selectedEvent && loading && <p>Loading…</p>}

      {selectedEvent && !loading && (
        <>
          <div className="sponsor-management-tabs">
            <button
              type="button"
              className={`sponsor-management-tab ${activeTab === 'sponsors' ? 'sponsor-management-tab-active' : ''}`}
              onClick={() => setActiveTab('sponsors')}
            >
              Sponsors
            </button>
            <button
              type="button"
              className={`sponsor-management-tab ${activeTab === 'directory' ? 'sponsor-management-tab-active' : ''}`}
              onClick={() => setActiveTab('directory')}
            >
              Directory
            </button>
            <button
              type="button"
              className={`sponsor-management-tab ${activeTab === 'prizes' ? 'sponsor-management-tab-active' : ''}`}
              onClick={() => setActiveTab('prizes')}
            >
              Prizes
            </button>
            <button
              type="button"
              className={`sponsor-management-tab ${activeTab === 'awardPrizes' ? 'sponsor-management-tab-active' : ''}`}
              onClick={() => setActiveTab('awardPrizes')}
            >
              Award prizes
            </button>
            <button
              type="button"
              className={`sponsor-management-tab ${activeTab === 'raffles' ? 'sponsor-management-tab-active' : ''}`}
              onClick={() => setActiveTab('raffles')}
            >
              Raffles
            </button>
            <button
              type="button"
              className={`sponsor-management-tab ${activeTab === 'bucketEntries' ? 'sponsor-management-tab-active' : ''}`}
              onClick={() => setActiveTab('bucketEntries')}
            >
              Bucket entries
            </button>
            <button
              type="button"
              className={`sponsor-management-tab ${activeTab === 'map' ? 'sponsor-management-tab-active' : ''}`}
              onClick={() => setActiveTab('map')}
            >
              Map
            </button>
          </div>

          {activeTab === 'sponsors' && (
          <section className="sponsor-management-section">
            <h2>Sponsors</h2>
            <div className="sponsor-management-section-actions">
              <label className="sponsor-management-filter-label" title="Show sponsors with type None in the table">
                <input
                  type="checkbox"
                  checked={showNoneType}
                  onChange={(e) => setShowNoneType(e.target.checked)}
                  aria-label="Show type None"
                />
                <span>Show type: None</span>
              </label>
              {!showNoneType && sponsors.some((s) => (s.type || 'none') === 'none') && (
                <span className="sponsor-management-hint-inline sponsor-management-muted" title="These rows still exist on the event; turn the checkbox on to see and delete them from the table, or use Directory → company → Remove from this event.">
                  ({sponsors.filter((s) => (s.type || 'none') === 'none').length} hidden &quot;None&quot; row(s))
                </span>
              )}
              <label className="sponsor-management-filter-label" title="Include sponsor logo on dashboard, schedule, rules page, and bonus puzzles">
                <input
                  type="checkbox"
                  checked={showLogoInclusionColumns}
                  onChange={(e) => setShowLogoInclusionColumns(e.target.checked)}
                  aria-label="Show logo inclusion columns"
                />
                <span>Logo inclusion (Dash/Sched/Rules/Bonus)</span>
              </label>
              <label className="sponsor-management-filter-label">
                <span>Sort:</span>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="sponsor-management-sort-select" aria-label="Sort by">
                  <option value="type">Type (Interactive → Exhibit → Prize)</option>
                  <option value="name">A–Z</option>
                </select>
              </label>
              <label className="sponsor-management-filter-label">
                <span>Compare event:</span>
                <select
                  value={compareEventId}
                  onChange={(e) => setCompareEventId(e.target.value)}
                  className="sponsor-management-sort-select"
                  aria-label="Compare to another event"
                >
                  <option value="">None</option>
                  {events.filter((ev) => ev.id !== selectedEvent).map((ev) => (
                    <option key={ev.id} value={ev.id}>{ev.title || ev.id}</option>
                  ))}
                </select>
              </label>
              <label className="sponsor-management-filter-label">
                <span>Email type:</span>
                <select
                  value={emailSponsorType}
                  onChange={(e) => setEmailSponsorType(e.target.value)}
                  className="sponsor-management-sort-select"
                  aria-label="Sponsor type for email"
                >
                  <option value="all">All</option>
                  <option value="interactive">Interactive only</option>
                  <option value="exhibit">Exhibit only</option>
                  <option value="interactive_exhibit">Interactive + Exhibit</option>
                  <option value="prize">Prize only</option>
                </select>
              </label>
              <button
                type="button"
                className="sponsor-management-btn sponsor-management-btn-secondary"
                onClick={openSponsorEmailComposer}
              >
                Email sponsors
              </button>
              <button type="button" className="sponsor-management-btn btn-fm-primary" onClick={openAddSponsor}>
                + ADD SPONSOR
              </button>
              <span className="sponsor-management-export-cluster">
                <button
                  type="button"
                  className="sponsor-management-btn sponsor-management-btn-secondary btn-fm-print-outline"
                  onClick={() => openSponsorTableExport('sponsors', 'print')}
                  disabled={sortedSponsors.length === 0}
                >
                  Print table
                </button>
                <AdminCopyMenu
                  ariaLabel="Copy sponsors table or emails"
                  disabled={sortedSponsors.length === 0}
                  items={[
                    {
                      label: 'Copy table…',
                      onSelect: () => openSponsorTableExport('sponsors', 'copy'),
                      showCopiedFeedback: false,
                    },
                    { label: 'Copy emails (comma-separated)', onSelect: () => copyFilteredSponsorEmails() },
                  ]}
                />
              </span>
            </div>
            <div className="sponsor-management-table-wrap">
              <table className="sponsor-management-table sponsor-management-table-compact">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Company</th>
                    <th>Contact</th>
                    <th title="Most recent note or outreach entry">LATEST NOTE</th>
                    <th title="Latest outreach outcome and summary">Last response</th>
                    <th>Logo</th>
                    {showLogoInclusionColumns && (
                      <>
                        <th className="sponsor-management-yn" title="Include sponsor logo on dashboard">Dash</th>
                        <th className="sponsor-management-yn" title="Include sponsor logo on schedule">Sched</th>
                        <th className="sponsor-management-yn" title="Include sponsor logo on rules page">Rules</th>
                        <th className="sponsor-management-yn" title="Include sponsor logo on bonus puzzles">Bonus</th>
                        <th className="sponsor-management-yn" title="Include in video challenge sponsor pool">Video</th>
                      </>
                    )}
                    <th title="Outreach flow">OUTREACH STATUS</th>
                    <th title="Flow: not set → needs invoice → invoice sent → invoice paid (saved on the sponsor row)">
                      Billing
                    </th>
                    <th title="Who last logged outreach or a note, and when">Last by</th>
                    <th title="Reminder date: set when you log outreach and fill in Next follow-up">Next F/U</th>
                    {compareEventId && (
                      <>
                        <th title="Status in comparison event">Prior status</th>
                        <th title="Answered in comparison event">Prior ans.</th>
                      </>
                    )}
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedSponsors.map((s) => {
                    const isNoteExpanded = expandedNoteId === s.id;
                    const prevRow = s.organizationId ? compareMap.get(s.organizationId) : null;
                    const activityPreview = formatActivityCellPreview(s);
                    const timelineItems = activityTimelineUi[s.id]?.items || [];
                    const timelineCount = timelineItems.length;
                    const timelineMaxPx =
                      timelineCount === 0
                        ? 96
                        : timelineCount <= 1
                          ? 128
                          : timelineCount <= 3
                            ? 200
                            : timelineCount <= 6
                              ? Math.min(340, 72 + timelineCount * 52)
                              : Math.min(480, 100 + timelineCount * 46);
                    const threadComposer = (suffix) => (
                      <div className="sponsor-management-thread-composer sponsor-management-thread-composer-top">
                        <label
                          className="sponsor-management-thread-composer-label"
                          htmlFor={`thread-note-${suffix}-${s.id}`}
                        >
                          Next note
                        </label>
                        <textarea
                          id={`thread-note-${suffix}-${s.id}`}
                          className="sponsor-management-thread-composer-input"
                          rows={2}
                          placeholder="Type a note…"
                          value={tableQuickNoteDraft[s.id] ?? ''}
                          onChange={(e) =>
                            setTableQuickNoteDraft((d) => ({ ...d, [s.id]: e.target.value }))
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                              e.preventDefault();
                              void submitTableQuickNote(s.id, e.currentTarget.value);
                            }
                          }}
                          disabled={tableQuickNoteSubmittingId === s.id}
                        />
                        <div className="sponsor-management-thread-composer-actions">
                          <button
                            type="button"
                            className="sponsor-management-btn sponsor-management-btn-sm btn-fm-primary"
                            disabled={
                              tableQuickNoteSubmittingId === s.id || !(tableQuickNoteDraft[s.id] || '').trim()
                            }
                            onClick={() => void submitTableQuickNote(s.id, tableQuickNoteDraft[s.id])}
                          >
                            {tableQuickNoteSubmittingId === s.id ? 'Saving…' : 'Add note'}
                          </button>
                          <span className="sponsor-management-thread-composer-hint">Ctrl+Enter</span>
                        </div>
                      </div>
                    );
                    return (
                      <React.Fragment key={s.id}>
                        <tr
                          onClick={(e) => {
                            const target = e.target;
                            if (target && typeof target.closest === 'function' && target.closest('button,input,select,textarea,a,label')) return;
                            setExpandedNoteId(isNoteExpanded ? null : s.id);
                          }}
                        >
                      <td className="sponsor-management-type-cell">
                        <button
                          type="button"
                          className="sponsor-management-note-toggle"
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedNoteId(isNoteExpanded ? null : s.id);
                          }}
                          title={isNoteExpanded ? 'Collapse inline conversation' : 'Expand inline conversation'}
                          aria-expanded={isNoteExpanded}
                          aria-label={`${isNoteExpanded ? 'Collapse' : 'Expand'} conversation for ${s.name || 'sponsor'}`}
                        >
                          {isNoteExpanded ? '▼' : '▶'}
                        </button>
                        <select
                          value={s.type === 'none' || s.type === '' ? 'none' : (s.type || 'none')}
                          onChange={(e) => updateSponsorType(s.id, e.target.value)}
                          className="sponsor-management-type-select"
                          aria-label={`Type for ${s.name}`}
                        >
                          {SPONSOR_TYPES.map((t) => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="sponsor-management-company-cell">
                        {s.name || '—'}
                        {!s.directoryId ? (
                          <span
                            className="sponsor-management-directory-warning-icon"
                            title="Not linked to Directory — edit to link"
                            aria-label="Not linked to Directory — edit to link"
                          >
                            ⚠
                          </span>
                        ) : null}
                      </td>
                      <td className="sponsor-management-contact-cell">
                        {(getContactFullName(s) !== '—' || (s.contactEmail || '').trim()) ? (
                          <div ref={contactPopoverId === s.id ? contactPopoverRef : undefined}>
                            <button
                              type="button"
                              className="sponsor-management-contact-trigger"
                              onClick={() => {
                                setActivityPopoverSponsorId(null);
                                setContactPopoverId(contactPopoverId === s.id ? null : s.id);
                              }}
                              aria-expanded={contactPopoverId === s.id}
                            >
                              {getContactFullName(s)}
                            </button>
                            {contactPopoverId === s.id && (
                              <div className="sponsor-management-contact-popover">
                                {Array.isArray(s.contacts) && s.contacts.length > 0 ? (
                                  s.contacts.map((c, i) => (
                                    <div key={i} className="sponsor-management-contact-popover-block">
                                      <div className="sponsor-management-contact-popover-name">
                                        {c.name || '—'}
                                        {Number(s.mainContactIndex) === i && <span className="sponsor-management-main-badge">Main</span>}
                                        {Number(s.invoiceContactIndex) === i && <span className="sponsor-management-invoice-badge">Invoice</span>}
                                      </div>
                                      <div className="sponsor-management-contact-popover-email">
                                        {c.email || '—'}
                                        {c.email && (
                                          <button type="button" className="sponsor-management-copy-btn" onClick={() => copyEmail(c.email)} title="Copy email" aria-label="Copy email">
                                            📋
                                          </button>
                                        )}
                                      </div>
                                      <div className="sponsor-management-contact-popover-phone">{c.phone ? <a href={`tel:${c.phone.replace(/\D/g, '')}`}>{c.phone}</a> : '—'}</div>
                                      {c.note && <div className="sponsor-management-contact-popover-note">{c.note}</div>}
                                    </div>
                                  ))
                                ) : (
                                  <div className="sponsor-management-contact-popover-block">
                                    <div className="sponsor-management-contact-popover-name">{s.primaryContactName || '—'}</div>
                                    <div className="sponsor-management-contact-popover-email">
                                      {s.contactEmail || '—'}
                                      {s.contactEmail && (
                                        <button type="button" className="sponsor-management-copy-btn" onClick={() => copyEmail(s.contactEmail)} title="Copy email" aria-label="Copy email">
                                          📋
                                        </button>
                                      )}
                                    </div>
                                    <div className="sponsor-management-contact-popover-phone">—</div>
                                  </div>
                                )}
                                {s.invoiceContact && (
                                  <div className="sponsor-management-contact-popover-extra">Invoice note: {s.invoiceContact}</div>
                                )}
                                <button type="button" className="sponsor-management-contact-popover-close" onClick={() => setContactPopoverId(null)}>Close</button>
                              </div>
                            )}
                          </div>
                        ) : '—'}
                      </td>
                      <td className="sponsor-management-activity-cell">
                        <span className={`sponsor-management-activity-preview-snippet${activityPreview.empty ? ' sponsor-management-activity-preview-trigger-muted' : ''}`}>
                          {activityPreview.snippet}
                        </span>
                      </td>
                      <td className="sponsor-management-last-response-cell" title={formatSponsorLastResponseCell(s)}>
                        {formatSponsorLastResponseCell(s)}
                      </td>
                      <td className="sponsor-management-logo-cell">
                        {s.logo && (s.logo || '').trim() ? (
                          <div className="sponsor-management-logo-with-provenance">
                            <a href={s.logo} target="_blank" rel="noopener noreferrer">
                              <img
                                src={s.logo}
                                alt={s.name || ''}
                                className="sponsor-management-logo-img"
                              />
                            </a>
                            {getLogoProvenanceBadge(s.logo) ? (
                              <span className="sponsor-management-logo-provenance-badge">
                                {getLogoProvenanceBadge(s.logo)}
                              </span>
                            ) : null}
                          </div>
                        ) : '—'}
                      </td>
                      {showLogoInclusionColumns && (
                        <>
                          <td className="sponsor-management-yn" title="Include sponsor logo on dashboard">
                            <label className="sponsor-management-yn-label">
                              <input
                                type="checkbox"
                                checked={s.showOnDashboard !== false}
                                onChange={() => toggleSponsorInclude(s.id, 'showOnDashboard')}
                                aria-label="Include logo on dashboard"
                              />
                            </label>
                          </td>
                          <td className="sponsor-management-yn" title="Include sponsor logo on schedule">
                            <label className="sponsor-management-yn-label">
                              <input
                                type="checkbox"
                                checked={s.showOnSchedule !== false}
                                onChange={() => toggleSponsorInclude(s.id, 'showOnSchedule')}
                                aria-label="Include logo on schedule"
                              />
                            </label>
                          </td>
                          <td className="sponsor-management-yn" title="Include sponsor logo on rules page">
                            <label className="sponsor-management-yn-label">
                              <input
                                type="checkbox"
                                checked={s.showOnRulesPage !== false}
                                onChange={() => toggleSponsorInclude(s.id, 'showOnRulesPage')}
                                aria-label="Include logo on rules page"
                              />
                            </label>
                          </td>
                          <td className="sponsor-management-yn" title="Include sponsor logo on bonus puzzles">
                            <label className="sponsor-management-yn-label">
                              <input
                                type="checkbox"
                                checked={!!s.showOnBonusPuzzles}
                                onChange={() => toggleSponsorInclude(s.id, 'showOnBonusPuzzles')}
                                aria-label="Include logo on bonus puzzles"
                              />
                            </label>
                          </td>
                          <td className="sponsor-management-yn" title="Include in video challenge sponsor pool">
                            <label className="sponsor-management-yn-label">
                              <input
                                type="checkbox"
                                checked={!!s.includeInVideoChallenge}
                                onChange={() => toggleSponsorInclude(s.id, 'includeInVideoChallenge')}
                                aria-label="Include in video challenge"
                              />
                            </label>
                          </td>
                        </>
                      )}
                      <td
                        className="sponsor-management-pipeline-cell"
                        title={
                          s.sponsorPipelineUpdatedAt
                            ? `Updated ${s.sponsorPipelineUpdatedAt}`
                            : 'Outreach / response flow'
                        }
                      >
                        <select
                          value={derivePipelineFromLegacy(s)}
                          onChange={(e) => updateSponsorPipelineStage(s.id, e.target.value, { skipNudgeCheck: false })}
                          className="sponsor-management-pipeline-select"
                          aria-label={`Pipeline for ${s.name || 'sponsor'}`}
                          style={{
                            backgroundColor: (PIPELINE_BADGE_STYLES[derivePipelineFromLegacy(s)] || PIPELINE_BADGE_STYLES['']).bg,
                            color: (PIPELINE_BADGE_STYLES[derivePipelineFromLegacy(s)] || PIPELINE_BADGE_STYLES['']).fg,
                            border: (PIPELINE_BADGE_STYLES[derivePipelineFromLegacy(s)] || PIPELINE_BADGE_STYLES['']).border,
                          }}
                        >
                          {SPONSOR_PIPELINE_STAGES.map((opt) => (
                            <option key={opt.value || 'pipeline-empty'} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        {pipelineNudgeBySponsor[s.id] ? (
                          <div className="sponsor-management-pipeline-nudge">
                            <span>No outreach logged</span>
                            <div className="sponsor-management-pipeline-nudge-row">
                              <input
                                type="text"
                                value={pipelineNudgeNoteBySponsor[s.id] || ''}
                                onChange={(e) => setPipelineNudgeNoteBySponsor((prev) => ({ ...prev, [s.id]: e.target.value }))}
                                placeholder="Quick note"
                                className="sponsor-management-pipeline-nudge-input"
                              />
                              <button
                                type="button"
                                className="sponsor-management-btn sponsor-management-btn-xs btn-fm-primary"
                                onClick={() => handleSavePipelineNudgeNote(s.id)}
                                disabled={!auth.currentUser || !(pipelineNudgeNoteBySponsor[s.id] || '').trim()}
                              >
                                Save
                              </button>
                            </div>
                          </div>
                        ) : null}
                      </td>
                      <td
                        className="sponsor-management-billing-cell"
                        title={
                          s.sponsorBillingUpdatedAt
                            ? `Updated ${s.sponsorBillingUpdatedAt}`
                            : 'Invoice and payment'
                        }
                      >
                        <select
                          value={deriveBillingFromLegacy(s)}
                          onChange={(e) => updateSponsorBillingStage(s.id, e.target.value)}
                          className="sponsor-management-pipeline-select sponsor-management-billing-select"
                          aria-label={`Billing for ${s.name || 'sponsor'}`}
                          title="Not set → needs invoice → invoice sent → invoice paid"
                          style={{
                            backgroundColor: (BILLING_BADGE_STYLES[deriveBillingFromLegacy(s)] || BILLING_BADGE_STYLES['']).bg,
                            color: (BILLING_BADGE_STYLES[deriveBillingFromLegacy(s)] || BILLING_BADGE_STYLES['']).fg,
                            border: (BILLING_BADGE_STYLES[deriveBillingFromLegacy(s)] || BILLING_BADGE_STYLES['']).border,
                          }}
                        >
                          {SPONSOR_BILLING_STAGES.map((opt) => (
                            <option key={opt.value || 'billing-empty'} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td
                        className="sponsor-management-last-touch-cell"
                        title={formatSponsorLastTouchCellTitle(s)}
                      >
                        {formatSponsorLastTouchCell(s)}
                      </td>
                      <td className="sponsor-management-fu-cell">{formatFirestoreDateOnly(s.nextFollowUpAt)}</td>
                      {compareEventId && (
                        <>
                          <td className="sponsor-management-compare-cell">{prevRow ? (prevRow.status || '—') : '—'}</td>
                          <td className="sponsor-management-compare-cell">{prevRow ? (prevRow.answered ? 'Y' : '—') : '—'}</td>
                        </>
                      )}
                      <td className="sponsor-management-actions-cell">
                        <button
                          type="button"
                          className="sponsor-management-btn sponsor-management-btn-secondary"
                          onClick={() => openPrizeHistoryForSponsor(s)}
                          title="Prizes won / pledged for this sponsor"
                        >
                          History
                        </button>
                        <button
                          type="button"
                          className="sponsor-management-btn sponsor-management-btn-secondary"
                          onClick={() => openEditSponsor(s)}
                          title="Edit sponsor"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="sponsor-management-icon-btn sponsor-management-icon-btn-danger"
                          onClick={() => deleteSponsor(s.id)}
                          aria-label={`Delete ${s.name || 'sponsor'}`}
                          title="Delete"
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                    <tr className={`sponsor-management-note-row ${isNoteExpanded ? 'sponsor-management-note-row-open' : ''}`}>
                      <td colSpan={sponsorTableColSpan} className="sponsor-management-note-cell">
                        <div className="sponsor-management-inline-thread">
                          <h4 className="sponsor-management-inline-thread-title">Conversation</h4>
                          <details className="sponsor-management-thread-composer-details">
                            <summary className="sponsor-management-thread-composer-summary">
                              Add quick note
                            </summary>
                            {threadComposer('inline')}
                          </details>
                          <div
                            className="sponsor-management-activity-timeline sponsor-management-activity-timeline-primary sponsor-management-inline-thread-timeline sponsor-management-activity-timeline-dynamic"
                            style={{ maxHeight: timelineMaxPx }}
                          >
                            {activityTimelineUi[s.id]?.error && (
                              <p className="sponsor-management-error">{activityTimelineUi[s.id].error}</p>
                            )}
                            {renderSponsorMergedTimelineList(activityTimelineUi[s.id]?.items)}
                          </div>
                          <div className="sponsor-management-inline-thread-status-bar sponsor-management-activity-status-bar">
                            <div className="sponsor-management-activity-status-block">
                              <strong>Next follow-up reminder</strong>
                              <div>
                                {s.nextFollowUpAt ? formatFirestoreDateOnly(s.nextFollowUpAt) : '—'}
                                <span className="sponsor-management-activity-status-sub">
                                  {' '}
                                  (set when logging outreach)
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="sponsor-management-inline-thread-actions">
                            <button
                              type="button"
                              className="sponsor-management-btn sponsor-management-btn-sm btn-fm-primary"
                              onClick={() => {
                                setExpandedNoteId(null);
                                openEditSponsor(s);
                              }}
                            >
                              Open edit
                            </button>
                            <button
                              type="button"
                              className="sponsor-management-btn sponsor-management-btn-sm"
                              onClick={() => setExpandedNoteId(null)}
                            >
                              Collapse
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              {sortedSponsors.length === 0 && <p className="sponsor-management-empty">No sponsors yet. Add one to get started.</p>}
            </div>
          </section>
          )}

          {activeTab === 'directory' && (
          <section className="sponsor-management-section">
            <h2>Sponsor directory</h2>
            <p className="sponsor-management-hint-inline">
              Master list of organizations and contacts. <strong>Click a row</strong> to open full directory details (logo, website, all contacts and history, notes, IDs).
              Use <strong>Add to this event</strong> to create a participation row for the selected event (for example after a decline in a prior year).
              Use <strong>Import directory from past event</strong> once to copy last year’s sponsor companies into this list and link those event’s rows to directory records.
              {' '}
              <strong>In this event</strong> includes sponsor rows whose type is <strong>None</strong>; those rows stay off the Sponsors table until you check <strong>Show type: None</strong> there — use <strong>Show in Sponsors</strong> below to jump there with that filter on.
            </p>
            <div className="sponsor-management-section-actions sponsor-management-directory-actions">
              <button type="button" className="sponsor-management-btn btn-fm-primary" onClick={openDirectorySeedModal}>
                Import directory from past event…
              </button>
              <span className="sponsor-management-export-cluster">
                <button
                  type="button"
                  className="sponsor-management-btn sponsor-management-btn-secondary btn-fm-print-outline"
                  onClick={() => openSponsorTableExport('directory', 'print')}
                  disabled={directoryLoading || directoryList.length === 0}
                >
                  Print table
                </button>
                <AdminCopyMenu
                  ariaLabel="Copy directory table"
                  disabled={directoryLoading || directoryList.length === 0}
                  items={[
                    {
                      label: 'Copy table…',
                      onSelect: () => openSponsorTableExport('directory', 'copy'),
                      showCopiedFeedback: false,
                    },
                  ]}
                />
              </span>
            </div>
            {directorySeedError && <p className="sponsor-management-error">{directorySeedError}</p>}
            {directoryLoading && <p>Loading directory…</p>}
            {!directoryLoading && (
              <div className="sponsor-management-table-wrap">
                <table className="sponsor-management-table">
                  <thead>
                    <tr>
                      <th>Company</th>
                      <th>Active contacts</th>
                      <th>In this event</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {directoryList.map((o) => {
                      const activeC = (Array.isArray(o.contacts) ? o.contacts : []).filter((c) => c && c.active !== false);
                      return (
                        <tr
                          key={o.id}
                          className="sponsor-management-directory-row"
                          onClick={() => openDirectoryOrgDetails(o.id)}
                          title="Click row for full directory details"
                        >
                          <td>
                            <span className="sponsor-management-directory-row-name">{o.name || '—'}</span>
                            <span className="sponsor-management-directory-row-hint">View</span>
                          </td>
                          <td>{activeC.length ? activeC.map((c) => (c.email || c.name || '—')).join(', ') : '—'}</td>
                          <td>
                            {!o.inThisEvent ? (
                              'No'
                            ) : o.noneTypeParticipation ? (
                              <span
                                title='A sponsor row exists with type "None". It is hidden on the Sponsors tab until "Show type: None" is checked (same as the hint next to that checkbox).'
                              >
                                Yes (None)
                              </span>
                            ) : (
                              'Yes'
                            )}
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            {!o.inThisEvent ? (
                              <button
                                type="button"
                                className="sponsor-management-btn btn-fm-primary sponsor-management-btn-sm"
                                onClick={() => addDirectoryOrgToEvent(o)}
                              >
                                Add to this event
                              </button>
                            ) : o.noneTypeParticipation ? (
                              <button
                                type="button"
                                className="sponsor-management-btn sponsor-management-btn-secondary sponsor-management-btn-sm"
                                title="Turns on Show type: None and opens the Sponsors tab"
                                onClick={() => {
                                  setShowNoneType(true);
                                  setActiveTab('sponsors');
                                }}
                              >
                                Show in Sponsors
                              </button>
                            ) : (
                              <span className="sponsor-management-muted">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {directoryList.length === 0 && !directoryLoading && (
                  <p className="sponsor-management-empty">No organizations yet. Use <strong>Import directory from past event</strong>, add sponsors on the Sponsors tab, or run the backfill script.</p>
                )}
              </div>
            )}
          </section>
          )}

          {activeTab === 'prizes' && (
          <section className="sponsor-management-section">
            <h2>Prizes</h2>
            <p className="sponsor-management-hint-inline sponsor-management-prize-email-hint">
              Email each sponsor who has at least one award or raffle winner recorded. The message lists those wins and thanks them for any other pledged prizes not marked as awarded yet.
            </p>
            <button
              type="button"
              className="sponsor-management-btn sponsor-management-btn-secondary sponsor-management-prize-email-btn"
              onClick={openPrizeTabSponsorEmails}
            >
              Email sponsors (winners and thank-yous)
            </button>
            <button type="button" className="sponsor-management-btn btn-fm-primary" onClick={openAddPrize} disabled={sponsors.length === 0}>
              Add prize
            </button>
            {sponsors.length === 0 && <span className="sponsor-management-hint-inline">Add at least one sponsor first.</span>}
            <span className="sponsor-management-export-cluster">
              <button
                type="button"
                className="sponsor-management-btn sponsor-management-btn-secondary btn-fm-print-outline"
                onClick={() => openSponsorTableExport('prizes', 'print')}
                disabled={sortedPrizes.length === 0}
              >
                Print table
              </button>
              <AdminCopyMenu
                ariaLabel="Copy prizes table"
                disabled={sortedPrizes.length === 0}
                items={[
                  {
                    label: 'Copy table…',
                    onSelect: () => openSponsorTableExport('prizes', 'copy'),
                    showCopiedFeedback: false,
                  },
                ]}
              />
            </span>
            <div className="sponsor-management-prize-status-filters" role="tablist" aria-label="Filter prizes by status">
              {[
                { id: 'all', label: 'All' },
                { id: 'assumed', label: 'Assumed' },
                { id: 'confirmedPlus', label: 'Confirmed+' },
                { id: 'receivedPlus', label: 'Received+' },
                { id: 'awarded', label: 'Awarded' },
              ].map((filter) => (
                <button
                  key={filter.id}
                  type="button"
                  className={`sponsor-management-prize-filter-chip${prizeStatusFilter === filter.id ? ' sponsor-management-prize-filter-chip-active' : ''}`}
                  onClick={() => setPrizeStatusFilter(filter.id)}
                  aria-pressed={prizeStatusFilter === filter.id}
                >
                  {filter.label}
                </button>
              ))}
            </div>
            <div className="sponsor-management-table-wrap">
              <table className="sponsor-management-table">
                <thead>
                  <tr>
                    <th>
                      <button type="button" className="sponsor-management-th-sort" onClick={() => cyclePrizeSort('giveawayType')} title="Sort by giveaway type">
                        Giveaway {prizeSortKey === 'giveawayType' && (prizeSortDir === 'asc' ? ' ↑' : ' ↓')}
                      </button>
                    </th>
                    <th>#</th>
                    <th>
                      <button type="button" className="sponsor-management-th-sort" onClick={() => cyclePrizeSort('sponsor')} title="Sort by sponsor name">
                        Sponsor {prizeSortKey === 'sponsor' && (prizeSortDir === 'asc' ? ' ↑' : ' ↓')}
                      </button>
                    </th>
                    <th>Name / Description</th>
                    <th>Status</th>
                    <th>Expires</th>
                    <th title="Unlisted: not shown on What can I win">Unlisted</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSortedPrizes.map((p) => {
                    const exp = p.expirationDate;
                    const expStr = exp && (exp.toDate ? exp.toDate().toISOString().slice(0, 10) : (typeof exp === 'string' ? exp.slice(0, 10) : ''));
                    const addedStr = formatPrizeTimestamp(p.createdAt);
                    const modifiedStr = formatPrizeTimestamp(p.updatedAt);
                    const statusValue = normalizePrizeStatus(p.status);
                    const statusMeta = prizeStatusByValue[statusValue] || PRIZE_STATUSES[0];
                    return (
                      <tr key={p.id}>
                        <td className="sponsor-management-giveaway-type-cell">{(GIVEAWAY_TYPES.find((t) => t.value === p.giveawayType)?.label || p.giveawayType || '').toUpperCase()}</td>
                        <td>{p.quantity != null ? p.quantity : 1}</td>
                        <td>{getSponsorName(p.sponsorId)}</td>
                        <td>{p.name || p.description || '—'}</td>
                        <td>
                          <div className="sponsor-management-prize-status-select">
                            <button
                              type="button"
                              className="sponsor-management-prize-status-chip"
                              style={{ backgroundColor: statusMeta.bg, color: statusMeta.fg, border: statusMeta.border || 'none' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (openPrizeStatusMenuId === p.id) {
                                  closePrizeStatusMenu();
                                } else {
                                  prizeStatusAnchorRef.current = e.currentTarget;
                                  const r = e.currentTarget.getBoundingClientRect();
                                  const minW = Math.max(r.width, 150);
                                  const pad = 8;
                                  let left = r.left;
                                  left = Math.min(left, window.innerWidth - minW - pad);
                                  left = Math.max(pad, left);
                                  setPrizeStatusMenuLayout({
                                    top: r.bottom + 4,
                                    left,
                                    minWidth: minW,
                                  });
                                  setOpenPrizeStatusMenuId(p.id);
                                }
                              }}
                              aria-haspopup="menu"
                              aria-expanded={openPrizeStatusMenuId === p.id}
                            >
                              {statusMeta.label}
                            </button>
                          </div>
                          {PRIZE_STATUS_INDEX[statusValue] >= PRIZE_STATUS_INDEX.confirmed ? (
                            <span className="sponsor-management-public-badge">→ PUBLIC</span>
                          ) : null}
                          {(addedStr || modifiedStr) && (
                            <div className="sponsor-management-prize-timestamps">
                              {addedStr && <span>Added: {addedStr}</span>}
                              {modifiedStr && <span>Modified: {modifiedStr}</span>}
                            </div>
                          )}
                        </td>
                        <td>{expStr || '—'}</td>
                        <td className="sponsor-management-yn" title="Unlisted: not shown on What can I win">
                          <label className="sponsor-management-yn-label">
                            <input
                              type="checkbox"
                              checked={!!p.unlisted}
                              onChange={(e) => updatePrizeUnlisted(p.id, e.target.checked)}
                              aria-label="Unlisted"
                            />
                          </label>
                        </td>
                        <td>
                          <button type="button" className="sponsor-management-btn sponsor-management-btn-sm" onClick={() => openEditPrize(p)}>Edit</button>
                          <button type="button" className="sponsor-management-btn sponsor-management-btn-sm sponsor-management-btn-danger" onClick={() => deletePrize(p.id)}>Delete</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {prizes.length === 0 && sponsors.length > 0 && <p className="sponsor-management-empty">No prizes yet.</p>}
            </div>
          </section>
          )}

          {activeTab === 'awardPrizes' && (
          <section className="sponsor-management-section">
            <h2>Award prizes</h2>
            <p className="sponsor-management-hint-inline">
              Edit categories below. Assign one or more prizes (or &quot;other&quot; items) per category. Record who won each item in &quot;Won by&quot;.
            </p>
            <div className="sponsor-management-section-actions" style={{ marginBottom: '0.75rem' }}>
              <button
                type="button"
                className="sponsor-management-btn sponsor-management-btn-secondary btn-fm-print-outline"
                onClick={() => openSponsorTableExport('awardPrizes', 'print')}
                disabled={awardData.categories.length === 0}
              >
                Print table
              </button>
              <AdminCopyMenu
                ariaLabel="Copy award prizes table"
                disabled={awardData.categories.length === 0}
                items={[
                  {
                    label: 'Copy table…',
                    onSelect: () => openSponsorTableExport('awardPrizes', 'copy'),
                    showCopiedFeedback: false,
                  },
                ]}
              />
            </div>

            <div className="sponsor-management-award-block">
              <h3 className="sponsor-management-award-subtitle sponsor-management-award-subtitle-collapsible">
                <button
                  type="button"
                  className="sponsor-management-award-collapse-section-btn"
                  onClick={() => setAwardCategoriesBlockCollapsed((c) => !c)}
                  aria-expanded={!awardCategoriesBlockCollapsed}
                >
                  {awardCategoriesBlockCollapsed ? '▶' : '▼'}
                </button>
                Award categories
              </h3>
              {!awardCategoriesBlockCollapsed && (
              <>
              <div className="sponsor-management-award-category-list">
                <List
                  height={360}
                  itemCount={awardData.categories.length}
                  itemSize={72}
                  width="100%"
                  itemData={{
                    categories: awardData.categories,
                    onDebouncedChange: setCategoryLabelDraft,
                    onBlurCommit: commitCategoryLabel,
                    onDelete: removeAwardCategory,
                  }}
                  itemKey={(index, data) => data.categories[index]?.id || index}
                >
                  {({ index, style, data }) => (
                    <AwardCategoryRow
                      style={style}
                      category={data.categories[index]}
                      onDebouncedChange={data.onDebouncedChange}
                      onBlurCommit={data.onBlurCommit}
                      onDelete={data.onDelete}
                      warning={awardCategoryWarnings[data.categories[index]?.id]}
                    />
                  )}
                </List>
              </div>
              <button type="button" className="sponsor-management-btn sponsor-management-btn-sm btn-fm-primary" onClick={addAwardCategory}>+ Add category</button>
              </>
              )}
            </div>

            <div className="sponsor-management-award-block">
              <h3 className="sponsor-management-award-subtitle">Other items (e.g. Medal, Mug)</h3>
              <div className="sponsor-management-other-items-row">
                {awardData.otherItems.map((label, i) => (
                  <span key={i} className="sponsor-management-other-item-chip">
                    {label}
                    {(usedOtherCount[label] || 0) > 0 && (
                      <span className="sponsor-management-other-item-count"> ({usedOtherCount[label]})</span>
                    )}
                    <button type="button" className="sponsor-management-other-item-remove" onClick={() => removeAwardOtherItem(i)} aria-label="Remove">×</button>
                  </span>
                ))}
                <span className="sponsor-management-other-item-add">
                  <input
                    type="text"
                    value={newOtherItemLabel}
                    onChange={(e) => setNewOtherItemLabel(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAwardOtherItem(newOtherItemLabel); setNewOtherItemLabel(''); } }}
                    placeholder="Add item…"
                    className="sponsor-management-award-custom"
                  />
                  <button type="button" className="sponsor-management-btn sponsor-management-btn-sm btn-fm-primary" onClick={() => { addAwardOtherItem(newOtherItemLabel); setNewOtherItemLabel(''); }}>Add</button>
                </span>
              </div>
            </div>

            <div className="sponsor-management-table-wrap">
              <table className="sponsor-management-table sponsor-management-award-table">
                <thead>
                  <tr>
                    <th>Award category</th>
                    <th>Items won</th>
                    <th>Won by</th>
                  </tr>
                </thead>
                <tbody>
                  {awardData.categories.map((cat) => {
                    const items = getAssignmentItems(cat.id);
                    const selectedKeys = awardAddSelection[cat.id] || [];
                    const isCollapsed = !expandedAwardCategories.has(cat.id);
                    return (
                      <tr key={cat.id}>
                        <td className="sponsor-management-award-cat-cell">
                          <button
                            type="button"
                            className="sponsor-management-award-collapse-btn"
                            onClick={() => toggleAwardCategoryCollapsed(cat.id)}
                            aria-expanded={!isCollapsed}
                            title={isCollapsed ? 'Expand' : 'Collapse'}
                          >
                            {isCollapsed ? '▶' : '▼'}
                          </button>
                          <span className="sponsor-management-award-cat-label">{categoryDisplayLabel(cat)}</span>
                        </td>
                        <td>
                          {isCollapsed ? (
                            <span className="sponsor-management-award-collapsed-summary">
                              {items.length === 0
                                ? '—'
                                : items
                                    .map((it) => {
                                      if (it.type === 'prize' && it.prizeId) {
                                        const p = prizeTypePrizes.find((x) => x.id === it.prizeId);
                                        return p ? (p.name || p.description || p.id) : it.prizeId;
                                      }
                                      return it.label || '—';
                                    })
                                    .join(', ')}
                            </span>
                          ) : (
                          <div className="sponsor-management-award-items">
                            <div className="sponsor-management-award-multi-add" ref={openAddDropdownCatId === cat.id ? awardAddDropdownRef : null}>
                              <button
                                type="button"
                                className="sponsor-management-btn sponsor-management-btn-sm btn-fm-primary sponsor-management-award-add-trigger"
                                onClick={() => setOpenAddDropdownCatId((prev) => (prev === cat.id ? null : cat.id))}
                                aria-expanded={openAddDropdownCatId === cat.id}
                              >
                                {selectedKeys.length === 0
                                  ? 'Add items ▼'
                                  : `Add items (${selectedKeys.length} selected) ▼`}
                              </button>
                              {openAddDropdownCatId === cat.id && (
                                <div className="sponsor-management-award-add-dropdown">
                                  {selectedKeys.length > 0 && (
                                    <div className="sponsor-management-award-picked-list">
                                      <span className="sponsor-management-award-multi-label">Picked:</span>
                                      {selectedKeys.map((key) => {
                                        const label = key.startsWith('prize:')
                                          ? (prizeTypePrizes.find((x) => x.id === key.slice(6))?.name || prizeTypePrizes.find((x) => x.id === key.slice(6))?.description || key.slice(6))
                                          : key.slice(6);
                                        return (
                                          <span key={key} className="sponsor-management-award-picked-chip">
                                            {label}
                                            <button type="button" className="sponsor-management-other-item-remove" onClick={() => toggleAwardAddSelection(cat.id, key)} aria-label="Remove">×</button>
                                          </span>
                                        );
                                      })}
                                    </div>
                                  )}
                                  <div className="sponsor-management-award-add-options">
                                    {prizeTypePrizes.map((p) => {
                                      const key = `prize:${p.id}`;
                                      const disabled = isPrizeFullyUsed(p.id, null, null);
                                      return (
                                        <label key={p.id} className={disabled ? 'sponsor-management-award-check-disabled' : ''} title={disabled ? `All ${p.quantity ?? 1} used` : ''}>
                                          <input
                                            type="checkbox"
                                            checked={selectedKeys.includes(key)}
                                            onChange={() => !disabled && toggleAwardAddSelection(cat.id, key)}
                                            disabled={disabled}
                                          />
                                          <span>{p.name || p.description || p.id}</span>
                                          {disabled && <span className="sponsor-management-award-used-badge"> (used)</span>}
                                        </label>
                                      );
                                    })}
                                    {awardData.otherItems.map((l) => {
                                      const key = `other:${l}`;
                                      const n = usedOtherCount[l] || 0;
                                      return (
                                        <label key={l}>
                                          <input
                                            type="checkbox"
                                            checked={selectedKeys.includes(key)}
                                            onChange={() => toggleAwardAddSelection(cat.id, key)}
                                          />
                                          <span>{l}{n > 0 ? ` (${n})` : ''}</span>
                                        </label>
                                      );
                                    })}
                                  </div>
                                  <button type="button" className="sponsor-management-btn sponsor-management-btn-sm btn-fm-primary" onClick={() => { addSelectedToCategory(cat.id); setOpenAddDropdownCatId(null); }} disabled={selectedKeys.length === 0}>Add selected ({selectedKeys.length})</button>
                                </div>
                              )}
                            </div>
                            {items.map((it, idx) => (
                              <div key={idx} className="sponsor-management-award-item-row">
                                {it.type === 'custom' || (it.type === 'other' && !awardData.otherItems.includes(it.label)) ? (
                                  <span className="sponsor-management-award-readonly-item">Other: {it.label || '—'}</span>
                                ) : (
                                  <select
                                    value={
                                      it.type === 'prize'
                                        ? `prize:${it.prizeId || ''}`
                                        : `other:${it.label || ''}`
                                    }
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      if (v.startsWith('prize:')) updateAssignmentItem(cat.id, idx, { type: 'prize', prizeId: v.slice(6) });
                                      else if (v.startsWith('other:')) updateAssignmentItem(cat.id, idx, { type: 'other', label: v.slice(6) });
                                    }}
                                    className="sponsor-management-award-select"
                                  >
                                    <option value="">— None —</option>
                                    {prizeTypePrizes.map((p) => {
                                      const fullyUsed = isPrizeFullyUsed(p.id, cat.id, idx);
                                      return (
                                        <option key={p.id} value={`prize:${p.id}`} disabled={fullyUsed}>
                                          {p.name || p.description || p.id}{fullyUsed ? ` (all ${p.quantity ?? 1} used)` : ''}
                                        </option>
                                      );
                                    })}
                                    {awardData.otherItems.map((l) => {
                                      const n = usedOtherCount[l] || 0;
                                      return (
                                        <option key={l} value={`other:${l}`}>{l}{n > 0 ? ` (${n})` : ''}</option>
                                      );
                                    })}
                                  </select>
                                )}
                                <button type="button" className="sponsor-management-btn sponsor-management-btn-sm sponsor-management-btn-danger" onClick={() => removeAssignmentItem(cat.id, idx)}>×</button>
                              </div>
                            ))}
                          </div>
                          )}
                        </td>
                        <td className="sponsor-management-award-wonby-cell">
                          <div className="sponsor-management-wonby-wrap" ref={openWonByKey === cat.id ? wonByDropdownRef : null}>
                            <button
                              type="button"
                              className="sponsor-management-wonby-trigger"
                              onClick={() => {
                                setOpenWonByKey((prev) => (prev === cat.id ? null : cat.id));
                                setWonBySearch('');
                              }}
                              aria-expanded={openWonByKey === cat.id}
                            >
                              {getWinnerDisplayLabel(getCategoryWinner(cat.id)) === '—' ? 'Won by…' : getWinnerDisplayLabel(getCategoryWinner(cat.id))}
                            </button>
                            {openWonByKey === cat.id && (
                              <div className="sponsor-management-wonby-dropdown">
                                <input
                                  type="text"
                                  value={wonBySearch}
                                  onChange={(e) => setWonBySearch(e.target.value)}
                                  placeholder="Search freshlings or mentors…"
                                  className="sponsor-management-wonby-search"
                                  autoFocus
                                />
                                <div className="sponsor-management-wonby-list">
                                  <button
                                    type="button"
                                    className="sponsor-management-wonby-option"
                                    onClick={() => {
                                      setCategoryWinner(cat.id, { winnerFlyerId: null, winnerName: '' });
                                      setOpenWonByKey(null);
                                      setWonBySearch('');
                                    }}
                                  >
                                    — None —
                                  </button>
                                  {freshlings.length > 0 && (
                                    <>
                                      <div className="sponsor-management-wonby-group-label">Freshlings</div>
                                      {freshlings
                                        .filter((f) => {
                                          const name = (getDisplayName(f) || '').toLowerCase();
                                          const email = ((f.email || '').toString()).toLowerCase();
                                          const q = wonBySearch.trim().toLowerCase();
                                          return !q || name.includes(q) || email.includes(q);
                                        })
                                        .map((f) => (
                                          <button
                                            key={f.id}
                                            type="button"
                                            className="sponsor-management-wonby-option"
                                            onClick={() => {
                                              setCategoryWinner(cat.id, { winnerFlyerId: f.id, winnerName: getDisplayName(f) || f.email || '' });
                                              setOpenWonByKey(null);
                                              setWonBySearch('');
                                            }}
                                          >
                                            {getDisplayName(f) || f.email || f.id}
                                          </button>
                                        ))}
                                    </>
                                  )}
                                  {mentors.length > 0 && (
                                    <>
                                      <div className="sponsor-management-wonby-group-label">Mentors</div>
                                      {mentors
                                        .filter((m) => {
                                          const name = (getDisplayName(m) || '').toLowerCase();
                                          const email = ((m.email || '').toString()).toLowerCase();
                                          const q = wonBySearch.trim().toLowerCase();
                                          return !q || name.includes(q) || email.includes(q);
                                        })
                                        .map((m) => (
                                          <button
                                            key={m.id}
                                            type="button"
                                            className="sponsor-management-wonby-option"
                                            onClick={() => {
                                              setCategoryWinner(cat.id, { winnerFlyerId: m.id, winnerName: getDisplayName(m) || m.email || '' });
                                              setOpenWonByKey(null);
                                              setWonBySearch('');
                                            }}
                                          >
                                            {getDisplayName(m) || m.email || m.id}
                                          </button>
                                        ))}
                                    </>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {awardData.categories.length === 0 && (
              <p className="sponsor-management-empty">Add award categories above, then assign prizes and record winners.</p>
            )}
          </section>
          )}

          {activeTab === 'raffles' && (
          <section className="sponsor-management-section sponsor-management-raffles-section">
            <h2>Raffles</h2>
            <p className="sponsor-management-hint-inline">
              Set the drawing order (up/down). When someone wins, select them in &quot;Won by&quot;. Order is saved automatically.
            </p>

            {raffleOrdered.length === 0 ? (
              <p className="sponsor-management-empty">No general or bucket raffle prizes. Add prizes in the Prizes tab.</p>
            ) : (
              <>
              <div className="sponsor-management-bucket-summary-row">
                <span>
                  {bucketSummary.filledCount} of {bucketSummary.totalCount} buckets have entries · {bucketSummary.totalEntries} total entries
                </span>
                <div
                  className="sponsor-management-bucket-progress"
                  {...(bucketSummary.totalCount > 0
                    ? {
                        role: 'progressbar',
                        'aria-valuemin': 0,
                        'aria-valuemax': bucketSummary.totalCount,
                        'aria-valuenow': bucketSummary.filledCount,
                        'aria-label': `${bucketSummary.filledCount} of ${bucketSummary.totalCount} bucket raffle slots have at least one entry`,
                      }
                    : { 'aria-hidden': true })}
                >
                  <div
                    className="sponsor-management-bucket-progress-fill"
                    style={{
                      width:
                        bucketSummary.totalCount > 0
                          ? `${Math.min(100, Math.round((100 * bucketSummary.filledCount) / bucketSummary.totalCount))}%`
                          : '0%',
                    }}
                  />
                </div>
              </div>
              <div className="sponsor-management-section-actions" style={{ marginBottom: '0.5rem' }}>
                <button
                  type="button"
                  className="sponsor-management-btn sponsor-management-btn-secondary btn-fm-print-outline"
                  onClick={() => openSponsorTableExport('raffles', 'print')}
                >
                  Print table
                </button>
                <AdminCopyMenu
                  ariaLabel="Copy raffle draw table"
                  items={[
                    {
                      label: 'Copy table…',
                      onSelect: () => openSponsorTableExport('raffles', 'copy'),
                      showCopiedFeedback: false,
                    },
                  ]}
                />
              </div>
              <ul className="sponsor-management-raffle-list">
                {raffleOrdered.map((entry, idx) => {
                  const prize = getRafflePrize(entry);
                  if (!prize) return null;
                  const key = `raffle-${idx}`;
                  const isHighlight = raffleHighlightIndex === idx;
                  return (
                    <li key={key} className={`sponsor-management-raffle-row${isHighlight ? ' sponsor-management-raffle-row-next' : ''}`}>
                      <span className="sponsor-management-raffle-num" aria-label={`Draw position ${idx + 1}`}>{idx + 1}</span>
                      <span className="sponsor-management-raffle-type">{entry.type === 'general' ? 'General' : 'Bucket'}</span>
                      <span className="sponsor-management-raffle-sponsor">{getSponsorName(prize.sponsorId)}</span>
                      <div className="sponsor-management-raffle-prize-group">
                        <span className="sponsor-management-raffle-name">{prize.name || prize.description || entry.prizeId}</span>
                        <div className="sponsor-management-raffle-actions">
                          <button type="button" className="sponsor-management-btn sponsor-management-btn-sm sponsor-management-raffle-move" onClick={() => moveRaffleSlot(idx, 'up')} disabled={idx === 0} aria-label="Move up">↑</button>
                          <button type="button" className="sponsor-management-btn sponsor-management-btn-sm sponsor-management-raffle-move" onClick={() => moveRaffleSlot(idx, 'down')} disabled={idx === raffleOrdered.length - 1} aria-label="Move down">↓</button>
                        </div>
                        <div className="sponsor-management-wonby-wrap sponsor-management-raffle-wonby" ref={openWonByKey === key ? wonByDropdownRef : null}>
                          <button
                            type="button"
                            className="sponsor-management-wonby-trigger"
                            onClick={() => { setOpenWonByKey((prev) => (prev === key ? null : key)); setWonBySearch(''); setRaffleHighlightIndex(null); }}
                            aria-expanded={openWonByKey === key}
                          >
                            {getWinnerDisplayLabel(getRaffleWinner(idx)) === '—' ? 'Won by…' : getWinnerDisplayLabel(getRaffleWinner(idx))}
                          </button>
                          {openWonByKey === key && (
                            <div className="sponsor-management-wonby-dropdown">
                              <input type="text" value={wonBySearch} onChange={(e) => setWonBySearch(e.target.value)} placeholder="Search freshlings or mentors…" className="sponsor-management-wonby-search" autoFocus />
                              <div className="sponsor-management-wonby-list">
                                <button type="button" className="sponsor-management-wonby-option" onClick={() => setRaffleWinner(idx, { winnerFlyerId: null, winnerName: '' })}>— None —</button>
                                {freshlings.length > 0 && (
                                  <>
                                    <div className="sponsor-management-wonby-group-label">Freshlings</div>
                                    {freshlings.filter((f) => { const name = (getDisplayName(f) || '').toLowerCase(); const email = ((f.email || '').toString()).toLowerCase(); const q = wonBySearch.trim().toLowerCase(); return !q || name.includes(q) || email.includes(q); }).map((f) => (
                                      <button key={f.id} type="button" className="sponsor-management-wonby-option" onClick={() => setRaffleWinner(idx, { winnerFlyerId: f.id, winnerName: getDisplayName(f) || f.email || '' })}>
                                        {getDisplayName(f) || f.email || f.id}
                                      </button>
                                    ))}
                                  </>
                                )}
                                {mentors.length > 0 && (
                                  <>
                                    <div className="sponsor-management-wonby-group-label">Mentors</div>
                                    {mentors.filter((m) => { const name = (getDisplayName(m) || '').toLowerCase(); const email = ((m.email || '').toString()).toLowerCase(); const q = wonBySearch.trim().toLowerCase(); return !q || name.includes(q) || email.includes(q); }).map((m) => (
                                      <button key={m.id} type="button" className="sponsor-management-wonby-option" onClick={() => setRaffleWinner(idx, { winnerFlyerId: m.id, winnerName: getDisplayName(m) || m.email || '' })}>
                                        {getDisplayName(m) || m.email || m.id}
                                      </button>
                                    ))}
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        <span className="sponsor-management-raffle-sent">
                          <label className="sponsor-management-raffle-sent-label">
                            <input
                              type="checkbox"
                              checked={!!(raffleData.sentToSponsor && raffleData.sentToSponsor[String(idx)])}
                              onChange={(e) => setRaffleSentToSponsor(idx, e.target.checked)}
                              aria-label="Sent to sponsor"
                            />
                            <span>Sent to Sponsor</span>
                          </label>
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
              </>
            )}
          </section>
          )}

          {activeTab === 'bucketEntries' && (
          <section className="sponsor-management-section sponsor-management-bucket-entries-section">
            <h2>Bucket raffle entries</h2>
            <p className="sponsor-management-hint">
              For each bucket prize, add registered freshlings from the list (search by name or email) or use scan mode with a keyboard wedge scanner.
              Printed raffle tickets encode the freshling id on the back (no prefix); older tickets with FMFLY: still work.
              The same freshling may appear multiple times (one row per ticket). Entries save automatically for this event.
            </p>
            {bucketPrizesSorted.length > 0 && (
              <div className="sponsor-management-bucket-scan-options">
                <label className="sponsor-management-bucket-scan-skip-label">
                  <span>Scan: ignore first</span>
                  <input
                    type="number"
                    min={0}
                    max={500}
                    step={1}
                    value={bucketScanSkipLeading}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const n = raw === '' ? 0 : Math.floor(Number(raw));
                      const v = !Number.isFinite(n) || n < 0 ? 0 : Math.min(500, n);
                      setBucketScanSkipLeading(v);
                      persistBucketScanSkipLeading(v);
                    }}
                    className="sponsor-management-bucket-scan-skip-input"
                    aria-label="Ignore first N characters from each scan"
                  />
                  <span>characters (stored in this browser; for scanner prefixes)</span>
                </label>
                <p className="sponsor-management-bucket-scan-shortcut-hint">
                  Shortcut to scan field:{' '}
                  <kbd className="sponsor-management-kbd">Ctrl</kbd>
                  {' + '}
                  <kbd className="sponsor-management-kbd">Alt</kbd>
                  {' + '}
                  <kbd className="sponsor-management-kbd">V</kbd>
                  {' — first bucket prize, scan mode on (use the '}
                  <kbd className="sponsor-management-kbd">Ctrl</kbd>
                  {' key on Mac too, not Command).'}
                </p>
              </div>
            )}
            {bucketPrizesSorted.length === 0 ? (
              <p className="sponsor-management-empty">No bucket raffle prizes. Add prizes with giveaway type &quot;Bucket Raffle&quot; in the Prizes tab.</p>
            ) : (
              <>
              <div className="sponsor-management-section-actions" style={{ marginBottom: '0.5rem' }}>
                <button
                  type="button"
                  className="sponsor-management-btn sponsor-management-btn-secondary btn-fm-print-outline"
                  onClick={() => openSponsorTableExport('bucketEntries', 'print')}
                >
                  Print table
                </button>
                <AdminCopyMenu
                  ariaLabel="Copy bucket entries table"
                  items={[
                    {
                      label: 'Copy table…',
                      onSelect: () => openSponsorTableExport('bucketEntries', 'copy'),
                      showCopiedFeedback: false,
                    },
                  ]}
                />
              </div>
              <ul className="sponsor-management-bucket-prize-list">
                {bucketPrizesSorted.map((prize) => {
                  const entries = bucketRegistrations[prize.id] || [];
                  const sortedEntries = entries
                    .map((entry, idx) => ({ entry, originalIdx: idx, label: (getWinnerDisplayLabel(entry) || '').toString() }))
                    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
                  const entriesCountLabel = `${entries.length} ${entries.length === 1 ? 'entry' : 'entries'}`;
                  const title = prize.name || prize.description || prize.id;
                  const isCollapsed = collapsedBucketPrizeIds.has(prize.id);
                  const q = bucketEntrySearch.trim().toLowerCase();
                  const pickerOpen = openBucketAddPrizeId === prize.id;
                  const pickerOptions = freshlings
                    .filter((f) => {
                      const name = (getDisplayName(f) || '').toLowerCase();
                      const email = ((f.email || '').toString()).toLowerCase();
                      return !q || name.includes(q) || email.includes(q);
                    })
                    .map((f) => ({
                      freshling: f,
                      dupCount: entries.filter((e) => e.winnerFlyerId === f.id).length,
                    }));
                  const scanModeOn = !!bucketScanModeByPrize[prize.id];
                  const activeHighlightIdx = Math.max(0, Math.min(bucketEntryHighlightIdx, Math.max(0, pickerOptions.length - 1)));
                  const setHighlightIdxSafe = (nextIdx) => {
                    if (pickerOptions.length === 0) {
                      setBucketEntryHighlightIdx(0);
                      return;
                    }
                    const clamped = Math.max(0, Math.min(nextIdx, pickerOptions.length - 1));
                    setBucketEntryHighlightIdx(clamped);
                  };
                  const onBucketSearchKeyDown = (e) => {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setHighlightIdxSafe(activeHighlightIdx + 1);
                      return;
                    }
                    if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      setHighlightIdxSafe(activeHighlightIdx - 1);
                      return;
                    }
                    if (e.key === 'Enter' || e.key === 'Tab') {
                      if (pickBucketHighlightedFreshling(prize.id, pickerOptions)) {
                        e.preventDefault();
                      }
                    }
                  };
                  const scrollToHighlighted = (node) => {
                    if (node) node.scrollIntoView({ block: 'nearest' });
                  };
                  return (
                    <li key={prize.id} className="sponsor-management-bucket-prize-card">
                      <div className="sponsor-management-bucket-prize-head">
                        <button
                          type="button"
                          className="sponsor-management-bucket-collapse-toggle"
                          onClick={() => toggleBucketPrizeCollapsed(prize.id)}
                          aria-expanded={!isCollapsed}
                          aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${title}`}
                        >
                          <span className={`sponsor-management-bucket-collapse-chevron${isCollapsed ? '' : ' sponsor-management-bucket-collapse-chevron-open'}`}>▸</span>
                          <span className="sponsor-management-bucket-prize-title">
                            {title} · <span className="sponsor-management-bucket-prize-title-count">{entriesCountLabel}</span>
                          </span>
                          {prize.quantity > 1 && (
                            <span className="sponsor-management-bucket-prize-qty"> ×{prize.quantity}</span>
                          )}
                          <div className="sponsor-management-bucket-prize-sponsor">{getSponsorName(prize.sponsorId)}</div>
                        </button>
                        <span className="sponsor-management-bucket-prize-count" title="Registered count">
                          {entriesCountLabel}
                        </span>
                      </div>
                      {!isCollapsed && (
                      <>
                      <div className="sponsor-management-bucket-add-row">
                        <div
                          className="sponsor-management-wonby-wrap sponsor-management-bucket-add-picker"
                          ref={pickerOpen ? bucketAddDropdownRef : null}
                        >
                          <button
                            type="button"
                            className="sponsor-management-wonby-trigger sponsor-management-bucket-add-trigger"
                            onClick={() => {
                              setOpenBucketAddPrizeId((prev) => (prev === prize.id ? null : prize.id));
                              setBucketEntrySearch('');
                              setBucketEntryHighlightIdx(0);
                            }}
                            aria-expanded={pickerOpen}
                            aria-haspopup="listbox"
                          >
                            Add freshling…
                          </button>
                          {pickerOpen && (
                            <div className="sponsor-management-wonby-dropdown sponsor-management-bucket-add-dropdown">
                              <input
                                type="text"
                                value={bucketEntrySearch}
                                onChange={(e) => {
                                  setBucketEntrySearch(e.target.value);
                                  setBucketEntryHighlightIdx(0);
                                }}
                                onKeyDown={onBucketSearchKeyDown}
                                placeholder="Search freshlings…"
                                className="sponsor-management-wonby-search"
                                autoFocus
                                aria-label={`Search freshlings for ${title}`}
                              />
                              <div className="sponsor-management-wonby-list" role="listbox">
                                {freshlings.length === 0 && (
                                  <div className="sponsor-management-bucket-add-empty">No freshlings for this event.</div>
                                )}
                                {freshlings.length > 0 && pickerOptions.length === 0 && (
                                  <div className="sponsor-management-bucket-add-empty">No matches. Try another search.</div>
                                )}
                                {pickerOptions.map((option, idx) => {
                                  const f = option.freshling;
                                  const label = getDisplayName(f) || f.email || f.id;
                                  const selectedClass = idx === activeHighlightIdx ? ' sponsor-management-wonby-option-active' : '';
                                  const dupNote =
                                    option.dupCount > 0 ? ` (${option.dupCount} in bucket)` : '';
                                  return (
                                    <button
                                      key={f.id}
                                      type="button"
                                      className={`sponsor-management-wonby-option${selectedClass}`}
                                      onMouseEnter={() => setBucketEntryHighlightIdx(idx)}
                                      onFocus={() => setBucketEntryHighlightIdx(idx)}
                                      onClick={() => addBucketFreshling(prize.id, f)}
                                      ref={idx === activeHighlightIdx ? scrollToHighlighted : null}
                                    >
                                      {label}
                                      {dupNote}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                        {entries.length > 0 && (
                          <button
                            type="button"
                            className="sponsor-management-btn sponsor-management-btn-sm"
                            onClick={() => clearBucketRegistrants(prize.id)}
                          >
                            Clear all
                          </button>
                        )}
                        <button
                          type="button"
                          className="sponsor-management-btn sponsor-management-btn-sm sponsor-management-btn-secondary"
                          onClick={() => exportBucketEntriesCsv(prize, entries)}
                          disabled={entries.length === 0}
                        >
                          Export CSV
                        </button>
                      </div>
                      <div className="sponsor-management-bucket-scan-row">
                        <button
                          type="button"
                          className={`sponsor-management-btn sponsor-management-btn-sm sponsor-management-btn-secondary sponsor-management-bucket-scan-toggle${scanModeOn ? ' sponsor-management-bucket-scan-toggle-on' : ''}`}
                          aria-pressed={scanModeOn}
                          onClick={() => {
                            setBucketScanModeByPrize((prev) => {
                              const next = { ...prev };
                              const turningOn = !next[prize.id];
                              if (next[prize.id]) delete next[prize.id];
                              else next[prize.id] = true;
                              if (turningOn) {
                                setTimeout(() => bucketScanInputRefs.current[prize.id]?.focus(), 0);
                              }
                              return next;
                            });
                            setBucketScanErrorByPrize((prev) => ({ ...prev, [prize.id]: '' }));
                          }}
                        >
                          {scanModeOn ? 'Scan mode on' : 'Scan mode'}
                        </button>
                        {scanModeOn && (
                          <div className="sponsor-management-bucket-scan-field">
                            <input
                              ref={(el) => {
                                if (el) bucketScanInputRefs.current[prize.id] = el;
                                else delete bucketScanInputRefs.current[prize.id];
                              }}
                              type="text"
                              className="sponsor-management-bucket-scan-input"
                              placeholder="Scan, paste, or type ticket code — Enter to add"
                              value={bucketScanDraftByPrize[prize.id] || ''}
                              onChange={(e) =>
                                setBucketScanDraftByPrize((p) => ({ ...p, [prize.id]: e.target.value }))
                              }
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  const raw = e.currentTarget.value;
                                  setBucketScanDraftByPrize((p) => ({ ...p, [prize.id]: '' }));
                                  addBucketEntryFromScan(prize.id, raw);
                                }
                              }}
                              aria-label={`Scan raffle ticket barcode for ${title}`}
                            />
                            {bucketScanErrorByPrize[prize.id] ? (
                              <span className="sponsor-management-bucket-scan-error" role="alert">
                                {bucketScanErrorByPrize[prize.id]}
                              </span>
                            ) : null}
                          </div>
                        )}
                      </div>
                      {entries.length > 0 && (
                        <ul className="sponsor-management-bucket-names">
                          {sortedEntries.map(({ entry, originalIdx, label }) => {
                            const flyer = allFlyers.find((f) => f.id === entry.winnerFlyerId) || null;
                            const isCancelled = !!flyer?.cancelled;
                            return (
                              <li key={`${prize.id}-${originalIdx}-${entry.winnerFlyerId || entry.winnerName || originalIdx}`} className="sponsor-management-bucket-name-row">
                                <span className="sponsor-management-bucket-name-text">
                                  {label}
                                  {isCancelled ? <span className="sponsor-management-bucket-cancelled-badge">[Cancelled]</span> : null}
                                </span>
                                <button
                                  type="button"
                                  className="sponsor-management-bucket-name-remove"
                                  onClick={() => removeBucketRegistrant(prize.id, originalIdx)}
                                  aria-label={`Remove ${label}`}
                                >
                                  ×
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                      </>
                      )}
                    </li>
                  );
                })}
              </ul>
              </>
            )}
          </section>
          )}

          {activeTab === 'map' && (
            <SponsorMapTab
              sponsors={sponsors}
              slotAssignments={mapSlotAssignments}
              onSlotAssignmentsChange={saveMapSlotAssignments}
              saving={mapSaving}
            />
          )}
        </>
      )}

      {(directoryDetailsLoading || directoryDetailsOrg) && (
        <div
          className="fm-modal-overlay"
          role="presentation"
          onClick={() => {
            if (!directoryDetailsLoading) closeDirectoryDetailsModal();
          }}
        >
          <div
            className="fm-modal fm-modal--lg sponsor-management-modal sponsor-management-directory-details-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sponsor-directory-details-title"
            onClick={(e) => e.stopPropagation()}
          >
            {directoryDetailsLoading && !directoryDetailsOrg && (
              <>
                <div className="fm-modal-header">
                  <h2 className="fm-modal-title" id="sponsor-directory-details-title">
                    Directory details
                  </h2>
                  <button type="button" className="fm-modal-close" onClick={closeDirectoryDetailsModal} aria-label="Close">
                    ✕
                  </button>
                </div>
                <div className="fm-modal-body">
                  <p className="sponsor-management-hint-inline">Loading organization…</p>
                </div>
                <div className="fm-modal-footer">
                  <button type="button" className="fm-modal-btn fm-modal-btn-cancel" onClick={closeDirectoryDetailsModal}>
                    Cancel
                  </button>
                </div>
              </>
            )}
            {directoryDetailsOrg && (
              <>
                <div className="fm-modal-header">
                  <h2 className="fm-modal-title" id="sponsor-directory-details-title">
                    {directoryDetailsOrg.name || 'Organization'}
                  </h2>
                  <button type="button" className="fm-modal-close" onClick={closeDirectoryDetailsModal} aria-label="Close">
                    ✕
                  </button>
                </div>
                <div className="fm-modal-body">
                <div className="sponsor-management-directory-details-top">
                  {directoryDetailsOrg.logo && String(directoryDetailsOrg.logo).trim() ? (
                    <a
                      href={String(directoryDetailsOrg.logo).trim()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="sponsor-management-directory-details-logo-wrap"
                    >
                      <img
                        src={String(directoryDetailsOrg.logo).trim()}
                        alt=""
                        className="sponsor-management-directory-details-logo"
                      />
                    </a>
                  ) : (
                    <div className="sponsor-management-directory-details-logo-missing">No logo URL</div>
                  )}
                  <dl className="sponsor-management-details-list sponsor-management-directory-details-dl">
                    <dt>Website</dt>
                    <dd>
                      {(directoryDetailsOrg.website || '').trim() ? (
                        <a
                          href={(directoryDetailsOrg.website || '').trim().startsWith('http')
                            ? (directoryDetailsOrg.website || '').trim()
                            : `https://${(directoryDetailsOrg.website || '').trim()}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="sponsor-management-details-link"
                        >
                          {(directoryDetailsOrg.website || '').trim()}
                        </a>
                      ) : (
                        '—'
                      )}
                    </dd>
                  </dl>
                </div>
                {directoryDetailsOrg.notes != null && String(directoryDetailsOrg.notes).trim() !== '' && (
                  <div className="sponsor-management-directory-details-notes">
                    <h4 className="sponsor-management-directory-details-h4">Directory notes (legacy)</h4>
                    <p className="sponsor-management-hint-inline">Single text field on the org record. Prefer the note history below for new entries.</p>
                    <p className="sponsor-management-directory-details-notes-body">{String(directoryDetailsOrg.notes).trim()}</p>
                  </div>
                )}
                <h4 className="sponsor-management-directory-details-h4">Note history</h4>
                <p className="sponsor-management-hint-inline">Each entry records who added it, when it was last edited, and who edited it.</p>
                {directoryOrgNotesLoading && <p className="sponsor-management-empty">Loading notes…</p>}
                {!directoryOrgNotesLoading && (
                  <ul className="sponsor-management-note-log">
                    {directoryOrgNotes.map((n) => (
                      <li key={n.id} className="sponsor-management-note-log-item">
                        {editingDirectoryOrgNoteId === n.id ? (
                          <div className="sponsor-management-note-log-edit">
                            <textarea
                              value={editingDirectoryOrgNoteText}
                              onChange={(e) => setEditingDirectoryOrgNoteText(e.target.value)}
                              rows={3}
                              className="sponsor-management-textarea"
                              disabled={directoryOrgNoteSubmitting}
                            />
                            <div className="sponsor-management-note-log-actions">
                              <button
                                type="button"
                                className="sponsor-management-btn sponsor-management-btn-sm btn-fm-primary"
                                onClick={handleSaveDirectoryOrgNoteEdit}
                                disabled={directoryOrgNoteSubmitting || !editingDirectoryOrgNoteText.trim()}
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                className="sponsor-management-btn sponsor-management-btn-sm sponsor-management-btn-secondary"
                                onClick={() => {
                                  setEditingDirectoryOrgNoteId(null);
                                  setEditingDirectoryOrgNoteText('');
                                }}
                                disabled={directoryOrgNoteSubmitting}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <p className="sponsor-management-note-log-body">{n.body}</p>
                            <p className="sponsor-management-note-log-meta">
                              {n.authorDisplayName || 'Unknown'} · {formatFirestoreDateTime(n.createdAt)}
                              {noteWasEdited(n) ? (
                                <>
                                  {' '}
                                  · Last edited {formatFirestoreDateTime(n.updatedAt)} by {n.lastEditorDisplayName || 'Unknown'}
                                </>
                              ) : null}
                            </p>
                            <button
                              type="button"
                              className="sponsor-management-btn sponsor-management-btn-xs sponsor-management-btn-secondary"
                              onClick={() => {
                                setEditingDirectoryOrgNoteId(n.id);
                                setEditingDirectoryOrgNoteText(n.body || '');
                              }}
                              disabled={!auth.currentUser || directoryOrgNoteSubmitting}
                            >
                              Edit
                            </button>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                <div className="sponsor-management-form-group sponsor-management-note-log-add">
                  <label htmlFor="new-directory-org-note">Add note</label>
                  <textarea
                    id="new-directory-org-note"
                    value={newDirectoryOrgNoteText}
                    onChange={(e) => setNewDirectoryOrgNoteText(e.target.value)}
                    rows={2}
                    className="sponsor-management-textarea"
                    placeholder="Visible to admins only"
                    disabled={directoryOrgNoteSubmitting || !auth.currentUser}
                  />
                  <button
                    type="button"
                    className="sponsor-management-btn sponsor-management-btn-sm btn-fm-primary"
                    onClick={handleAddDirectoryOrgNote}
                    disabled={directoryOrgNoteSubmitting || !newDirectoryOrgNoteText.trim() || !auth.currentUser}
                  >
                    Add note
                  </button>
                  {!auth.currentUser && (
                    <p className="sponsor-management-hint-inline">Sign in to add or edit notes.</p>
                  )}
                </div>
                <h4 className="sponsor-management-directory-details-h4">Contacts (active and history)</h4>
                <p className="sponsor-management-hint-inline">
                  Main and Invoice mark the primary and billing contacts. Past contacts are kept for history.
                </p>
                <div className="sponsor-management-table-wrap sponsor-management-directory-details-table-wrap">
                  <table className="sponsor-management-table sponsor-management-table-compact">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Note</th>
                        <th>Status</th>
                        <th>Retired</th>
                        <th>Roles</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const dirContacts = Array.isArray(directoryDetailsOrg.contacts) ? directoryDetailsOrg.contacts : [];
                        const n = dirContacts.length;
                        const mainIdx = n ? Math.min(Number(directoryDetailsOrg.mainContactIndex) || 0, n - 1) : 0;
                        const invIdx = n ? Math.min(Number(directoryDetailsOrg.invoiceContactIndex) || 0, n - 1) : 0;
                        return dirContacts.map((c, i) => {
                          const isActive = c && c.active !== false;
                          const roles = [
                            i === mainIdx ? 'Main' : null,
                            i === invIdx ? 'Invoice' : null,
                          ].filter(Boolean).join(', ') || '—';
                          return (
                            <tr key={i} className={isActive ? '' : 'sponsor-management-directory-contact-past'}>
                              <td>{i + 1}</td>
                              <td>{(c && c.name) || '—'}</td>
                              <td>
                                {(c && c.email) ? (
                                  <>
                                    {c.email}
                                    <button
                                      type="button"
                                      className="sponsor-management-copy-btn"
                                      onClick={(e) => { e.stopPropagation(); copyEmail(c.email); }}
                                      title="Copy email"
                                      aria-label="Copy email"
                                    >
                                      📋
                                    </button>
                                  </>
                                ) : '—'}
                              </td>
                              <td>{(c && c.phone) || '—'}</td>
                              <td>{(c && c.note) || '—'}</td>
                              <td>{isActive ? 'Active' : 'Past'}</td>
                              <td>{(c && c.retiredAt) || '—'}</td>
                              <td>{roles}</td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                  {(!directoryDetailsOrg.contacts || !directoryDetailsOrg.contacts.length) && (
                    <p className="sponsor-management-empty">No contacts on file.</p>
                  )}
                </div>
                </div>
                <div className="fm-modal-footer sponsor-management-directory-details-actions">
                  <button
                    type="button"
                    className="fm-modal-btn fm-modal-btn-cancel"
                    onClick={closeDirectoryDetailsModal}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className="fm-modal-btn fm-modal-btn-cancel"
                    onClick={() => {
                      const org = directoryDetailsOrg;
                      closeDirectoryDetailsModal();
                      openPrizeHistoryForSponsor({
                        organizationId: org.id,
                        name: org.name,
                      });
                    }}
                  >
                    Prize history
                  </button>
                  {(() => {
                    const row = sponsors.find((s) => s.organizationId === directoryDetailsOrg.id);
                    return row ? (
                      <>
                        <button
                          type="button"
                          className="fm-modal-btn fm-modal-btn-primary"
                          onClick={() => {
                            closeDirectoryDetailsModal();
                            setActiveTab('sponsors');
                            openEditSponsor(row);
                          }}
                        >
                          Edit for this event
                        </button>
                        <button
                          type="button"
                          className="sponsor-management-btn sponsor-management-btn-danger"
                          onClick={async () => {
                            if (
                              !window.confirm(
                                'Remove this company from the current event? This deletes the sponsor row for this event only (not the directory record). Prizes linked to it will stay but show "Unknown sponsor".'
                              )
                            ) {
                              return;
                            }
                            const ok = await deleteSponsor(row.id, { skipConfirm: true });
                            if (ok) closeDirectoryDetailsModal();
                          }}
                        >
                          Remove from this event
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="fm-modal-btn fm-modal-btn-primary"
                        onClick={() => {
                          closeDirectoryDetailsModal();
                          addDirectoryOrgToEvent(directoryDetailsOrg);
                        }}
                      >
                        Add to this event
                      </button>
                    );
                  })()}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {prizeHistoryModal && (
        <div
          className="fm-modal-overlay"
          role="presentation"
          onClick={() => !prizeHistoryLoading && setPrizeHistoryModal(null)}
        >
          <div
            className="fm-modal fm-modal--lg sponsor-management-modal sponsor-management-modal-wide"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sponsor-prize-history-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="fm-modal-header">
              <h2 className="fm-modal-title" id="sponsor-prize-history-title">
                Prize history — {prizeHistoryModal.sponsorName || 'Sponsor'}
              </h2>
              <button
                type="button"
                className="fm-modal-close"
                onClick={() => !prizeHistoryLoading && setPrizeHistoryModal(null)}
                aria-label="Close"
                disabled={prizeHistoryLoading}
              >
                ✕
              </button>
            </div>
            <div className="fm-modal-body">
            {prizeHistoryLoading && <p>Loading…</p>}
            {!prizeHistoryLoading && (
              <>
              <div className="sponsor-management-section-actions" style={{ marginBottom: '0.5rem' }}>
                <button
                  type="button"
                  className="sponsor-management-btn sponsor-management-btn-secondary btn-fm-print-outline"
                  onClick={() => openSponsorTableExport('prizeHistory', 'print')}
                  disabled={prizeHistoryRows.length === 0}
                >
                  Print table
                </button>
                <AdminCopyMenu
                  ariaLabel="Copy prize history table"
                  disabled={prizeHistoryRows.length === 0}
                  items={[
                    {
                      label: 'Copy table…',
                      onSelect: () => openSponsorTableExport('prizeHistory', 'copy'),
                      showCopiedFeedback: false,
                    },
                  ]}
                />
              </div>
              <div className="sponsor-management-table-wrap">
                <table className="sponsor-management-table sponsor-management-table-compact">
                  <thead>
                    <tr>
                      <th>Event</th>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Giveaway</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {prizeHistoryRows.map((p) => (
                      <tr key={p.id}>
                        <td>{p._eventLabel || p.eventId || '—'}</td>
                        <td>{p.name || '—'}</td>
                        <td>{(p.description || '').slice(0, 80)}{(p.description || '').length > 80 ? '…' : ''}</td>
                        <td>{p.giveawayType || '—'}</td>
                        <td>
                          {p.eventId !== selectedEvent ? (
                            <button
                              type="button"
                              className="sponsor-management-btn sponsor-management-btn-sm"
                              onClick={() => copyHistoricPrizeToCurrentEvent(p)}
                            >
                              Copy to this event
                            </button>
                          ) : (
                            <span className="sponsor-management-muted">Current</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {prizeHistoryRows.length === 0 && (
                  <p className="sponsor-management-empty">No prizes found for this organization (or run backfill to set organizationId on older prizes).</p>
                )}
              </div>
              </>
            )}
            </div>
            <div className="fm-modal-footer">
              <button
                type="button"
                className="fm-modal-btn fm-modal-btn-cancel"
                onClick={() => setPrizeHistoryModal(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {detailsSponsor && (
        <div className="fm-modal-overlay" role="presentation" onClick={() => setDetailsSponsor(null)}>
          <div
            className="fm-modal fm-modal--md sponsor-management-modal sponsor-management-details-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sponsor-details-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="fm-modal-header">
              <h2 className="fm-modal-title" id="sponsor-details-title">
                {detailsSponsor.name || 'Sponsor details'}
              </h2>
              <button type="button" className="fm-modal-close" onClick={() => setDetailsSponsor(null)} aria-label="Close">
                ✕
              </button>
            </div>
            <div className="fm-modal-body">
            <dl className="sponsor-management-details-list">
              {[
                ['Company', detailsSponsor.name],
                ['Primary contact', detailsSponsor.primaryContactName],
                ['Main contact', (() => {
                  const contacts = Array.isArray(detailsSponsor.contacts) ? detailsSponsor.contacts : [];
                  const idx = detailsSponsor.mainContactIndex != null ? Number(detailsSponsor.mainContactIndex) : 0;
                  const c = contacts[idx];
                  return c ? `${(c.name || '').trim() || '—'} ${c.email ? `<${c.email}>` : ''}`.trim() || '—' : '—';
                })()],
                ['Invoice contact', detailsSponsor.invoiceContact],
                ['Contacts', Array.isArray(detailsSponsor.contacts) && detailsSponsor.contacts.length ? detailsSponsor.contacts.map((c) => {
                const part = `${(c.name || '').trim()} ${c.email ? `<${c.email}>` : ''}`.trim();
                return (c.note && c.note.trim()) ? `${part} — ${c.note.trim()}` : part;
              }).join('; ') : ''],
                ['Email', detailsSponsor.contactEmail],
                ['Website', detailsSponsor.website],
                ['Logo', detailsSponsor.logo ? '✓ (set)' : ''],
                ['Last Updated', detailsSponsor.lastUpdated],
                [
                  'Pipeline',
                  SPONSOR_PIPELINE_STAGES.find((x) => x.value === derivePipelineFromLegacy(detailsSponsor))
                    ?.label ?? '—',
                ],
                [
                  'Billing',
                  SPONSOR_BILLING_STAGES.find((x) => x.value === deriveBillingFromLegacy(detailsSponsor))
                    ?.label ?? '—',
                ],
                ['New on list 2026', detailsSponsor.newOnList],
                ['Status', detailsSponsor.status],
                ['Type', detailsSponsor.type],
                ['Tent', detailsSponsor.tent],
                ['T-Shirt', detailsSponsor.tShirt],
                ['Art File', detailsSponsor.artFile],
                ['Swag', detailsSponsor.swag],
                ['Strip', detailsSponsor.strip],
                ['Thanks', detailsSponsor.thanks],
                ['Travel Details', detailsSponsor.travelDetails],
                ['Prizes / donation', detailsSponsor.donationSummary],
                ['Notes', detailsSponsor.notes],
              ].map(([label, value]) => (
                <React.Fragment key={label}>
                  <dt>{label}</dt>
                  <dd>
                    {value == null || value === '' ? (
                      '—'
                    ) : typeof value === 'string' && value.startsWith('http') ? (
                      <a
                        href={value}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="sponsor-management-details-link"
                      >
                        {value}
                      </a>
                    ) : (
                      String(value)
                    )}
                  </dd>
                </React.Fragment>
              ))}
            </dl>
            </div>
            <div className="fm-modal-footer">
              <button type="button" className="fm-modal-btn fm-modal-btn-cancel" onClick={() => setDetailsSponsor(null)}>
                Close
              </button>
              <button
                type="button"
                className="fm-modal-btn fm-modal-btn-primary"
                onClick={() => {
                  setDetailsSponsor(null);
                  openEditSponsor(detailsSponsor);
                }}
              >
                Edit
              </button>
            </div>
          </div>
        </div>
      )}

      {sponsorModalOpen && (
        <div className="fm-modal-overlay" role="presentation" onClick={closeSponsorModal}>
          <div
            className="fm-modal fm-modal--md sponsor-management-modal sponsor-management-modal-sponsor"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sponsor-edit-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="fm-modal-header">
              <h2 className="fm-modal-title" id="sponsor-edit-modal-title">
                {editingSponsorId ? 'Edit sponsor' : 'Add sponsor'}
              </h2>
              <button type="button" className="fm-modal-close" onClick={closeSponsorModal} aria-label="Close">
                ✕
              </button>
            </div>
            <form noValidate onSubmit={saveSponsor} className="sponsor-management-sponsor-form">
              <div className="fm-modal-body">
              <div className="sponsor-management-form-row">
                <div className="sponsor-management-form-group">
                  <label>Type</label>
                  <select
                    value={sponsorForm.type}
                    onChange={(e) => setSponsorForm((f) => ({ ...f, type: e.target.value }))}
                  >
                    {SPONSOR_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="sponsor-management-form-group">
                  <label>Name *</label>
                  <input
                    name="sponsorName"
                    type="text"
                    value={sponsorForm.name}
                    onChange={(e) => setSponsorForm((f) => ({ ...f, name: e.target.value, directoryId: '' }))}
                    required
                    placeholder="Company or sponsor name"
                  />
                  <ModalFieldRequiredError invalidNames={sponsorModalInvalidNames} fieldName="sponsorName" />
                  {directoryEntriesLoading ? (
                    <p className="sponsor-management-hint-inline">Checking Directory…</p>
                  ) : null}
                  {directoryNameMatches.length > 0 ? (
                    <div className="sponsor-management-directory-match-row">
                      <span className="sponsor-management-directory-match-label">Did you mean…?</span>
                      {directoryNameMatches.map((entry) => (
                        <button
                          key={entry.id}
                          type="button"
                          className="sponsor-management-directory-match-chip"
                          onClick={() => applyDirectoryEntryToSponsorForm(entry)}
                        >
                          {entry.name || entry.id}
                        </button>
                      ))}
                    </div>
                  ) : null}
                  {isSaveBlockedByDirectoryExactMatch ? (
                    <p className="sponsor-management-error sponsor-management-inline-error">
                      Exact Directory match found. Select the existing entry before saving.
                    </p>
                  ) : null}
                </div>
              </div>
              <div className="sponsor-management-form-group">
                <label>Website</label>
                <input
                  type="url"
                  value={sponsorForm.website}
                  onChange={(e) => setSponsorForm((f) => ({ ...f, website: e.target.value }))}
                  placeholder="https://..."
                />
              </div>
              <div className="sponsor-management-form-group sponsor-management-contacts-group">
                <label>Contacts</label>
                {(sponsorForm.contacts || []).map((c, i) => (
                  <div key={i} className="sponsor-management-contact-card">
                    <div className="sponsor-management-contact-card-row">
                      <span className="sponsor-management-contact-radios">
                        <label className="sponsor-management-contact-edit-invoice">
                          <input
                            type="radio"
                            name="mainContact"
                            checked={sponsorForm.mainContactIndex === i}
                            onChange={() => setSponsorForm((f) => ({ ...f, mainContactIndex: i }))}
                          />
                          <span>Main</span>
                        </label>
                        <label className="sponsor-management-contact-edit-invoice">
                          <input
                            type="radio"
                            name="invoiceContact"
                            checked={sponsorForm.invoiceContactIndex === i}
                            onChange={() => setSponsorForm((f) => ({ ...f, invoiceContactIndex: i }))}
                          />
                          <span>Invoice</span>
                        </label>
                        <label className="sponsor-management-contact-edit-invoice">
                          <input
                            type="checkbox"
                            checked={c.active !== false}
                            onChange={(e) => updateFormContact(i, 'active', e.target.checked)}
                          />
                          <span>Active</span>
                        </label>
                      </span>
                      <button type="button" className="sponsor-management-btn sponsor-management-btn-xs sponsor-management-btn-danger sponsor-management-contact-remove" onClick={() => removeFormContact(i)}>Remove</button>
                    </div>
                    <div className="sponsor-management-contact-card-fields">
                      <input
                        type="text"
                        value={c.name}
                        onChange={(e) => updateFormContact(i, 'name', e.target.value)}
                        placeholder="Name"
                        className="sponsor-management-contact-field"
                      />
                      <input
                        type="email"
                        value={c.email}
                        onChange={(e) => updateFormContact(i, 'email', e.target.value)}
                        placeholder="Email"
                        className="sponsor-management-contact-field"
                      />
                      <input
                        type="tel"
                        value={c.phone}
                        onChange={(e) => updateFormContact(i, 'phone', e.target.value)}
                        placeholder="Phone"
                        className="sponsor-management-contact-field"
                      />
                      <input
                        type="text"
                        value={c.note ?? ''}
                        onChange={(e) => updateFormContact(i, 'note', e.target.value)}
                        placeholder="Note about this contact"
                        className="sponsor-management-contact-field sponsor-management-contact-note"
                      />
                    </div>
                  </div>
                ))}
                <button type="button" className="sponsor-management-btn sponsor-management-btn-sm btn-fm-primary" onClick={addFormContact}>Add contact</button>
              </div>
              <div className="sponsor-management-form-group">
                <label>Directory notes (organization-wide, all years)</label>
                <textarea
                  value={sponsorForm.organizationNotes}
                  onChange={(e) => setSponsorForm((f) => ({ ...f, organizationNotes: e.target.value }))}
                  rows={2}
                  className="sponsor-management-textarea"
                  placeholder="Internal notes stored on the sponsor directory record…"
                />
              </div>
              {editingSponsorId && (
                <div className="sponsor-management-form-group sponsor-management-event-note-log">
                  <label>Outreach and notes (this event)</label>
                  <p className="sponsor-management-hint-inline">
                    History below is chronological. Each entry shows who did it and when. Outreach is append-only; notes can be edited.
                  </p>
                  <div className="sponsor-management-followup-reminder-row">
                    <strong>Follow-up reminder on file:</strong>{' '}
                    {editingSponsorRow?.nextFollowUpAt ? (
                      <>
                        {formatFirestoreDateOnly(editingSponsorRow.nextFollowUpAt)}
                        <button
                          type="button"
                          className="sponsor-management-btn sponsor-management-btn-xs sponsor-management-btn-secondary"
                          onClick={handleClearSponsorNextFollowUp}
                          disabled={sponsorModalMetaBusy}
                        >
                          Clear date
                        </button>
                      </>
                    ) : (
                      <span className="sponsor-management-hint-inline">None — set <strong>Next follow-up</strong> when you log outreach.</span>
                    )}
                  </div>
                  <div className="sponsor-management-note-log-add">
                    <strong className="sponsor-management-outreach-add-title">Add internal note</strong>
                    <textarea
                      value={newSponsorRowNoteText}
                      onChange={(e) => setNewSponsorRowNoteText(e.target.value)}
                      rows={2}
                      className="sponsor-management-textarea"
                      placeholder="Internal note for this event only"
                      disabled={sponsorRowNoteSubmitting || !auth.currentUser}
                    />
                    <button
                      type="button"
                      className="sponsor-management-btn sponsor-management-btn-sm btn-fm-primary"
                      onClick={handleAddSponsorRowNote}
                      disabled={sponsorRowNoteSubmitting || !newSponsorRowNoteText.trim() || !auth.currentUser}
                    >
                      Add note
                    </button>
                    {!auth.currentUser && (
                      <p className="sponsor-management-hint-inline">Sign in to add notes.</p>
                    )}
                  </div>
                  {!sponsorEventTimelineLoading && (
                    <ul className="sponsor-management-note-log sponsor-management-event-timeline sponsor-management-conversation-thread sponsor-management-conversation-thread-modal">
                      {sponsorEventTimeline.map((item) => {
                        if (item.kind === 'outreach') {
                          const row = item.entry;
                          const isPipelineChange = row.type === 'pipeline_change';
                          return (
                            <li key={item.key} className={`sponsor-management-note-log-item ${isPipelineChange ? 'sponsor-management-timeline-pipeline-change' : 'sponsor-management-timeline-outreach'}`}>
                              <span className={`sponsor-management-timeline-badge ${isPipelineChange ? 'sponsor-management-timeline-badge-pipeline-change' : 'sponsor-management-timeline-badge-outreach'}`}>
                                {isPipelineChange ? 'Pipeline' : 'Outreach'}
                              </span>
                              {isPipelineChange ? (
                                <p className="sponsor-management-note-log-body">
                                  Pipeline changed: <strong>{sponsorPipelineStageDisplayLabel(row.from)}</strong> → <strong>{sponsorPipelineStageDisplayLabel(row.to)}</strong>
                                  {row.body ? ` · ${row.body}` : ''}
                                </p>
                              ) : (
                                <p className="sponsor-management-note-log-body">{row.body}</p>
                              )}
                              <p className="sponsor-management-note-log-meta">
                                {row.authorDisplayName || 'Unknown'} · {formatFirestoreDateTime(row.createdAt)}
                                {!isPipelineChange ? (
                                  <>
                                    {' · '}
                                    {outreachChannelLabel(row.channel)}
                                    {row.outcome ? ` · ${outreachOutcomeLabel(row.outcome)}` : ''}
                                    {row.nextFollowUpAt ? ` · Follow up ${formatFirestoreDateOnly(row.nextFollowUpAt)}` : ''}
                                  </>
                                ) : null}
                              </p>
                            </li>
                          );
                        }
                        const n = item.entry;
                        return (
                          <li key={item.key} className="sponsor-management-note-log-item sponsor-management-timeline-note">
                            <span className="sponsor-management-timeline-badge sponsor-management-timeline-badge-note">Note</span>
                            {editingSponsorRowNoteId === n.id ? (
                              <div className="sponsor-management-note-log-edit">
                                <textarea
                                  value={editingSponsorRowNoteText}
                                  onChange={(e) => setEditingSponsorRowNoteText(e.target.value)}
                                  rows={3}
                                  className="sponsor-management-textarea"
                                  disabled={sponsorRowNoteSubmitting}
                                />
                                <div className="sponsor-management-note-log-actions">
                                  <button
                                    type="button"
                                    className="sponsor-management-btn sponsor-management-btn-sm btn-fm-primary"
                                    onClick={handleSaveSponsorRowNoteEdit}
                                    disabled={sponsorRowNoteSubmitting || !editingSponsorRowNoteText.trim()}
                                  >
                                    Save
                                  </button>
                                  <button
                                    type="button"
                                    className="sponsor-management-btn sponsor-management-btn-sm sponsor-management-btn-secondary"
                                    onClick={() => {
                                      setEditingSponsorRowNoteId(null);
                                      setEditingSponsorRowNoteText('');
                                    }}
                                    disabled={sponsorRowNoteSubmitting}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <p className="sponsor-management-note-log-body">{n.body}</p>
                                <p className="sponsor-management-note-log-meta">
                                  {n.authorDisplayName || 'Unknown'} · {formatFirestoreDateTime(n.createdAt)}
                                  {noteWasEdited(n) ? (
                                    <>
                                      {' '}
                                      · Last edited {formatFirestoreDateTime(n.updatedAt)} by {n.lastEditorDisplayName || 'Unknown'}
                                    </>
                                  ) : null}
                                </p>
                                <button
                                  type="button"
                                  className="sponsor-management-btn sponsor-management-btn-xs sponsor-management-btn-secondary"
                                  onClick={() => {
                                    setEditingSponsorRowNoteId(n.id);
                                    setEditingSponsorRowNoteText(n.body || '');
                                  }}
                                  disabled={!auth.currentUser || sponsorRowNoteSubmitting}
                                >
                                  Edit
                                </button>
                              </>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  <div className="sponsor-management-outreach-add sponsor-management-note-log-add">
                    <strong className="sponsor-management-outreach-add-title">Log outreach</strong>
                    <div className="sponsor-management-outreach-add-row">
                      <label className="sponsor-management-outreach-add-field">
                        <span className="sponsor-management-outreach-field-label">Method</span>
                        <select
                          className="sponsor-management-sort-select sponsor-management-outreach-add-select"
                          value={outreachChannel}
                          onChange={(e) => setOutreachChannel(e.target.value)}
                          disabled={outreachSubmitting}
                        >
                          {OUTREACH_CHANNELS.map((c) => (
                            <option key={c.value} value={c.value}>{c.label}</option>
                          ))}
                        </select>
                      </label>
                      <label className="sponsor-management-outreach-add-field">
                        <span className="sponsor-management-outreach-field-label">Outcome</span>
                        <select
                          className="sponsor-management-sort-select sponsor-management-outreach-add-select"
                          value={outreachOutcome}
                          onChange={(e) => setOutreachOutcome(e.target.value)}
                          disabled={outreachSubmitting}
                        >
                          {OUTREACH_OUTCOMES.map((o) => (
                            <option key={o.value || 'none'} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </label>
                      <label className="sponsor-management-outreach-fu-label" title="Optional. Saves a reminder date on this sponsor row (Next F/U column and popover).">
                        Next follow-up date
                        <input
                          type="date"
                          value={outreachNextFollowUpDate}
                          onChange={(e) => setOutreachNextFollowUpDate(e.target.value)}
                          disabled={outreachSubmitting}
                        />
                      </label>
                    </div>
                    <textarea
                      value={outreachBody}
                      onChange={(e) => setOutreachBody(e.target.value)}
                      rows={2}
                      className="sponsor-management-textarea"
                      placeholder="What they said, what you sent, next step…"
                      disabled={outreachSubmitting || !auth.currentUser}
                    />
                    <button
                      type="button"
                      className="sponsor-management-btn sponsor-management-btn-sm btn-fm-primary"
                      onClick={handleAddSponsorOutreach}
                      disabled={outreachSubmitting || !outreachBody.trim() || !auth.currentUser}
                    >
                      Add outreach
                    </button>
                    {!auth.currentUser && (
                      <p className="sponsor-management-hint-inline">Sign in to add outreach.</p>
                    )}
                  </div>
                </div>
              )}
              <div className="sponsor-management-form-group">
                <label>
                  {sponsorLogoUploadedThisEvent
                    ? 'Logo — uploaded this event'
                    : `Logo — from directory${logoSourceYearLabel ? ` (last updated ${logoSourceYearLabel})` : ''}`}
                </label>
                <div className="sponsor-management-logo-mode-row">
                  <button
                    type="button"
                    className={`sponsor-management-btn sponsor-management-btn-xs ${sponsorLogoMode === 'directory' ? 'btn-fm-primary' : 'sponsor-management-btn-secondary btn-fm-print-outline'}`}
                    onClick={() => {
                      setSponsorLogoMode('directory');
                      setSponsorLogoUploadedThisEvent(false);
                      const directoryEntry = directoryEntries.find((d) => d.id === (sponsorForm.directoryId || '').trim());
                      if (directoryEntry) {
                        setSponsorForm((f) => ({ ...f, logo: directoryEntry.logo || '' }));
                      }
                    }}
                  >
                    Use directory logo
                  </button>
                  <button
                    type="button"
                    className={`sponsor-management-btn sponsor-management-btn-xs ${sponsorLogoMode === 'event_upload' ? 'btn-fm-primary' : 'sponsor-management-btn-secondary btn-fm-print-outline'}`}
                    onClick={() => setSponsorLogoMode('event_upload')}
                  >
                    Upload new for this event
                  </button>
                </div>
                <input
                  type="url"
                  value={sponsorForm.logo}
                  onChange={(e) => setSponsorForm((f) => ({ ...f, logo: e.target.value }))}
                  placeholder="https://... or leave blank"
                  disabled={sponsorLogoMode !== 'event_upload'}
                />
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoFileChange}
                  className="sponsor-management-file-input"
                  style={{ marginTop: 8 }}
                  disabled={sponsorLogoMode !== 'event_upload'}
                />
                {sponsorForm.logo && (
                  <img src={sponsorForm.logo} alt="" className="sponsor-management-logo-preview" style={{ marginTop: 8 }} />
                )}
              </div>
              </div>
              <div className="fm-modal-footer sponsor-management-modal-actions">
                {editingSponsorId && (
                  <button
                    type="button"
                    className="sponsor-management-btn sponsor-management-btn-danger"
                    style={{ marginRight: 'auto' }}
                    onClick={async () => {
                      const ok = await deleteSponsor(editingSponsorId);
                      if (ok) closeSponsorModal();
                    }}
                  >
                    Delete sponsor row…
                  </button>
                )}
                <button type="button" className="fm-modal-btn fm-modal-btn-cancel" onClick={closeSponsorModal}>
                  Cancel
                </button>
                <button type="submit" className="fm-modal-btn fm-modal-btn-primary" disabled={isSaveBlockedByDirectoryExactMatch}>
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {directorySeedModalOpen && (
        <div
          className="fm-modal-overlay"
          role="presentation"
          onClick={() => !directorySeedBusy && setDirectorySeedModalOpen(false)}
        >
          <div
            className="fm-modal fm-modal--md sponsor-management-modal sponsor-management-modal-import"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sponsor-directory-seed-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="fm-modal-header">
              <h2 className="fm-modal-title" id="sponsor-directory-seed-title">
                Import directory from past event
              </h2>
              <button
                type="button"
                className="fm-modal-close"
                onClick={() => !directorySeedBusy && setDirectorySeedModalOpen(false)}
                aria-label="Close"
                disabled={directorySeedBusy}
              >
                ✕
              </button>
            </div>
            <div className="fm-modal-body">
            <p className="sponsor-management-import-hint">
              Creates or updates directory organization records from every sponsor row on the event you pick, sets organizationId on those rows, refreshes denormalized contact fields, and tags that event’s prizes with organizationId where possible.
              Safe to run more than once; existing directory entries are matched by normalized company name.
            </p>
            <div className="sponsor-management-form-group">
              <label htmlFor="directory-seed-event">Source event</label>
              <select
                id="directory-seed-event"
                className="sponsor-management-sort-select"
                style={{ width: '100%', maxWidth: '100%' }}
                value={directorySeedEventId}
                onChange={(e) => setDirectorySeedEventId(e.target.value)}
                disabled={directorySeedBusy}
              >
                <option value="">Select event…</option>
                {events.map((ev) => (
                  <option key={ev.id} value={ev.id}>{ev.title || ev.id}</option>
                ))}
              </select>
            </div>
            {directorySeedError && <p className="sponsor-management-error">{directorySeedError}</p>}
            </div>
            <div className="fm-modal-footer">
              <button
                type="button"
                className="fm-modal-btn fm-modal-btn-cancel"
                onClick={() => !directorySeedBusy && setDirectorySeedModalOpen(false)}
                disabled={directorySeedBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="fm-modal-btn fm-modal-btn-primary"
                onClick={runDirectorySeedFromPastEvent}
                disabled={directorySeedBusy || !directorySeedEventId}
              >
                {directorySeedBusy ? 'Working…' : 'Run import'}
              </button>
            </div>
          </div>
        </div>
      )}

      {prizeModalOpen && (
        <div className="fm-modal-overlay" role="presentation" onClick={() => setPrizeModalOpen(false)}>
          <div
            className="fm-modal fm-modal--md sponsor-management-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sponsor-prize-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="fm-modal-header">
              <h2 className="fm-modal-title" id="sponsor-prize-modal-title">
                {editingPrizeId ? 'Edit prize' : 'Add prize'}
              </h2>
              <button type="button" className="fm-modal-close" onClick={() => setPrizeModalOpen(false)} aria-label="Close">
                ✕
              </button>
            </div>
            <form ref={prizeFormRef} onSubmit={savePrize}>
              <div className="fm-modal-body">
              <div className="sponsor-management-form-group">
                <label>Sponsor</label>
                <select
                  ref={prizeFormFirstInputRef}
                  value={prizeForm.sponsorId}
                  onChange={(e) => setPrizeForm((f) => ({ ...f, sponsorId: e.target.value }))}
                >
                  <option value="">— None —</option>
                  {sponsors
                    .filter((s) => (s.type || 'none') !== 'none')
                    .map((s) => (
                      <option key={s.id} value={s.id}>{s.name || s.id}</option>
                    ))}
                </select>
              </div>
              <div className="sponsor-management-form-group">
                <label>Giveaway type</label>
                <select
                  value={prizeForm.giveawayType}
                  onChange={(e) => setPrizeForm((f) => ({ ...f, giveawayType: e.target.value }))}
                >
                  {GIVEAWAY_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div className="sponsor-management-form-row">
                <div className="sponsor-management-form-group">
                  <label>Number (quantity)</label>
                  <input
                    type="number"
                    min={1}
                    value={prizeForm.quantity === '' ? '' : prizeForm.quantity}
                    onChange={(e) => {
                      const v = e.target.value;
                      setPrizeForm((f) => ({ ...f, quantity: v === '' ? '' : Math.max(1, parseInt(v, 10) || 1) }));
                    }}
                  />
                </div>
                <div className="sponsor-management-form-group">
                  <label>Status (internal)</label>
                  <select
                    value={prizeForm.status}
                    onChange={(e) => setPrizeForm((f) => ({ ...f, status: e.target.value }))}
                  >
                    {PRIZE_STATUSES.map((row) => (
                      <option key={row.value} value={row.value}>{row.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="sponsor-management-form-group">
                <label>Name (short)</label>
                <input
                  type="text"
                  value={prizeForm.name}
                  onChange={(e) => setPrizeForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Juno II"
                />
              </div>
              <div className="sponsor-management-form-group">
                <label>Description</label>
                <textarea
                  value={prizeForm.description}
                  onChange={(e) => setPrizeForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Full description of the prize"
                  rows={3}
                />
              </div>
              <div className="sponsor-management-form-row">
                <div className="sponsor-management-form-group">
                  <label>Image (optional)</label>
                  <input
                    ref={prizeImageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePrizeImageFileChange}
                    disabled={prizeImageUploading}
                    className="sponsor-management-file-input"
                  />
                  {prizeImageUploading && <span className="sponsor-management-upload-hint">Uploading…</span>}
                  {prizeForm.imageUrl && (
                    <div className="sponsor-management-prize-image-row">
                      <img src={prizeForm.imageUrl} alt="" className="sponsor-management-prize-image-preview" />
                      <button
                        type="button"
                        className="sponsor-management-btn sponsor-management-btn-sm"
                        onClick={() => setPrizeForm((f) => ({ ...f, imageUrl: '' }))}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
                <div className="sponsor-management-form-group">
                  <label>Expiration date (optional)</label>
                  <input
                    type="date"
                    value={prizeForm.expirationDate}
                    onChange={(e) => setPrizeForm((f) => ({ ...f, expirationDate: e.target.value }))}
                  />
                </div>
              </div>
              <div className="sponsor-management-form-group">
                <label>Winner photo (optional)</label>
                <p className="sponsor-management-hint-inline" style={{ marginTop: 0, marginBottom: 8 }}>
                  Upload after the event; stored in the same prizes folder as prize images.
                </p>
                <input
                  ref={prizeWinnerPhotoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePrizeWinnerPhotoFileChange}
                  disabled={prizeWinnerPhotoUploading}
                  className="sponsor-management-file-input"
                />
                {prizeWinnerPhotoUploading && <span className="sponsor-management-upload-hint">Uploading…</span>}
                {(prizeForm.winnerPhotoUrls || []).map((url, idx) => (
                  <div key={idx} className="sponsor-management-prize-image-row">
                    <img src={url} alt="" className="sponsor-management-prize-image-preview" />
                    <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>#{idx + 1}</span>
                    <button
                      type="button"
                      className="sponsor-management-btn sponsor-management-btn-sm"
                      onClick={() => setPrizeForm((f) => ({ ...f, winnerPhotoUrls: f.winnerPhotoUrls.filter((_, i) => i !== idx) }))}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
              <div className="sponsor-management-form-group">
                <label className="sponsor-management-checkbox-label">
                  <input
                    type="checkbox"
                    checked={!!prizeForm.unlisted}
                    onChange={(e) => setPrizeForm((f) => ({ ...f, unlisted: e.target.checked }))}
                  />
                  Unlisted (award/other only — not shown on What can I win)
                </label>
              </div>
              </div>
              <div className="fm-modal-footer">
                <button type="button" className="fm-modal-btn fm-modal-btn-cancel" onClick={() => setPrizeModalOpen(false)}>
                  Cancel
                </button>
                {!editingPrizeId && (
                  <button
                    type="button"
                    className="fm-modal-btn fm-modal-btn-cancel"
                    onClick={() => {
                      saveAndAddAnotherRef.current = true;
                      prizeFormRef.current?.requestSubmit();
                    }}
                  >
                    Save and add another
                  </button>
                )}
                <button type="submit" className="fm-modal-btn fm-modal-btn-primary">
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {sponsorTableExport && (
        <AdminColumnExportModal
          mode={sponsorTableExport.mode}
          columns={getColumnsForExportCtx(sponsorTableExport.ctx)}
          selectedIds={sponsorTableExportCols}
          onChangeSelected={setSponsorTableExportCols}
          onCancel={() => setSponsorTableExport(null)}
          onConfirm={runSponsorTableExport}
          onResetDefaults={resetSponsorTableExportCols}
        />
      )}

      <EmailComposerModal
        isOpen={emailComposerProps.isOpen}
        context={emailComposerProps.context}
        initialRecipients={emailComposerProps.initialRecipients}
        initialTemplate={emailComposerProps.initialTemplate}
        initialSubject={emailComposerProps.initialSubject}
        initialBody={emailComposerProps.initialBody}
        onClose={closeEmailComposer}
      />

      {openPrizeStatusMenuId != null &&
        prizeStatusMenuLayout != null &&
        (() => {
          const menuPrize = prizes.find((x) => x.id === openPrizeStatusMenuId);
          if (!menuPrize) return null;
          const menuStatusValue = normalizePrizeStatus(menuPrize.status);
          return createPortal(
            <div
              ref={prizeStatusDropdownRef}
              className="sponsor-management-prize-status-menu sponsor-management-prize-status-menu--fixed"
              role="menu"
              style={{
                top: prizeStatusMenuLayout.top,
                left: prizeStatusMenuLayout.left,
                minWidth: prizeStatusMenuLayout.minWidth,
              }}
            >
              {PRIZE_STATUSES.map((statusRow) => (
                <button
                  key={statusRow.value}
                  type="button"
                  role="menuitemradio"
                  aria-checked={statusRow.value === menuStatusValue}
                  className={`sponsor-management-prize-status-option${statusRow.value === menuStatusValue ? ' sponsor-management-prize-status-option-active' : ''}`}
                  onClick={() => {
                    closePrizeStatusMenu();
                    updatePrizeStatus(menuPrize.id, statusRow.value);
                  }}
                >
                  <span
                    className="sponsor-management-prize-status-chip sponsor-management-prize-status-chip-option"
                    style={{ backgroundColor: statusRow.bg, color: statusRow.fg, border: statusRow.border || 'none' }}
                  >
                    {statusRow.label}
                  </span>
                </button>
              ))}
            </div>,
            document.body
          );
        })()}
    </div>
  );
};

export default SponsorManagement;
